/**
 * Seb Gower Art — interaction engine
 * Magnetic · tilt · parallax · scrub · cursor · reveals
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(pointer: fine)').matches;
  var touch = window.matchMedia('(pointer: coarse)').matches;
  var narrow = window.matchMedia('(max-width: 899px)').matches;
  // Phones/tablets: drop desktop cursor + heavy motion affordances
  if (touch || ('ontouchstart' in window && !fine)) {
    document.body.classList.add('is-touch');
  }
  if (narrow) {
    document.body.classList.add('is-narrow');
  }
  try {
    window.matchMedia('(max-width: 899px)').addEventListener('change', function (e) {
      document.body.classList.toggle('is-narrow', e.matches);
      narrow = e.matches;
    });
  } catch (e) {}

  /*
    iPhone: kill sideways page drift. If the gesture is mostly horizontal
    and the finger isn’t on an intentional horizontal rail, cancel it so
    scrolling stays smooth and vertical.
  */
  (function lockVerticalPageScroll() {
    if (!(touch || narrow || 'ontouchstart' in window)) return;
    var sx = 0;
    var sy = 0;
    var X_ALLOW =
      'input, textarea, select, [contenteditable="true"], ' +
      '.skin-bar__options, .arc__filters, .gx-wings, [data-allow-x-scroll], ' +
      '.lb__thumbs, .buy__thumbs, .sg-adventure__wall, .about-polaroids__wall, ' +
      '[data-scroll-x], .walk__bgs, .walk__paint-colors, .walk__themes-panel, .walk__themes-list, ' +
      '.walk__rooms-grid, .walk__sound-sheet, .walk__tones';

    on(
      document,
      'touchstart',
      function (e) {
        if (!e.touches || !e.touches[0]) return;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      },
      { passive: true }
    );

    on(
      document,
      'touchmove',
      function (e) {
        if (!e.touches || !e.touches[0] || e.touches.length > 1) return;
        var dx = e.touches[0].clientX - sx;
        var dy = e.touches[0].clientY - sy;
        /* Mostly horizontal, with a real sideways intent */
        if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < 10) return;
        var t = e.target;
        if (t && t.closest && t.closest(X_ALLOW)) return;
        /* Keep the page locked to the screen width */
        if (window.scrollX !== 0) {
          try {
            window.scrollTo(0, window.scrollY || window.pageYOffset || 0);
          } catch (errL) {}
        }
        if (e.cancelable) e.preventDefault();
      },
      { passive: false }
    );

    /* If anything shoved the page sideways, snap back */
    on(
      window,
      'scroll',
      function () {
        if ((window.scrollX || window.pageXOffset || 0) !== 0) {
          try {
            window.scrollTo(0, window.scrollY || window.pageYOffset || 0);
          } catch (errS) {}
        }
      },
      { passive: true }
    );
  })();

  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }
  function on(el, ev, fn, opts) {
    if (el) el.addEventListener(ev, fn, opts || false);
  }

  /* ── Site skins · Dawn / Dusk / Mind / Wild ───────────────
     light→Dawn · dark→Dusk · color→Mind · wild→Wild
     Mind = philosophical deep (was Drip). Wild = velvet/boudoir heat.
     Loud retired → maps to Dawn. Persisted (localStorage sg_skin). */
  (function initSiteSkin() {
    var STORAGE = 'sg_skin';
    var COLLAPSE_KEY = 'sg_skin_bar_collapsed';
    var SKINS = {
      light: { mode: 'light', meta: '#ffffff', label: 'Dawn' },
      dark: { mode: 'dark', meta: '#0b0d12', label: 'Dusk' },
      color: { mode: 'dark', meta: '#0a0c12', label: 'Mind' },
      wild: { mode: 'dark', meta: '#1a0a10', label: 'Wild' },
    };
    var LEGACY = {
      colour: 'color',
      dawn: 'light',
      dusk: 'dark',
      drip: 'color',
      mind: 'color',
      loud: 'light',
      'greensand-beach': 'light',
      'tui-gorge': 'dark',
      'antelope-canyon': 'color',
      aotearoa: 'dark',
      'deep-south': 'dark',
      yellowstone: 'dark',
    };
    var DEFAULT = 'light';

    /* Retire Loud: anyone still on loud lands on Dawn */
    try {
      if (localStorage.getItem(STORAGE) === 'loud') {
        localStorage.setItem(STORAGE, 'light');
      }
    } catch (eMig) {}

    function normalize(s) {
      s = String(s || DEFAULT).toLowerCase().replace(/_/g, '-').trim();
      if (LEGACY[s]) s = LEGACY[s];
      if (!SKINS[s]) s = DEFAULT;
      return s;
    }

    function modeOf(skin) {
      return (SKINS[skin] && SKINS[skin].mode) || 'light';
    }

    function current() {
      return normalize(
        document.documentElement.getAttribute('data-skin') ||
          (function () {
            try {
              return localStorage.getItem(STORAGE);
            } catch (e) {
              return DEFAULT;
            }
          })()
      );
    }

    function isCollapsed() {
      try {
        return localStorage.getItem(COLLAPSE_KEY) === '1';
      } catch (e) {
        return document.documentElement.classList.contains('skin-bar-collapsed');
      }
    }

    function paintBar(skin) {
      document.querySelectorAll('[data-skin-bar]').forEach(function (bar) {
        bar.querySelectorAll('[data-skin-set]').forEach(function (btn) {
          var id = normalize(btn.getAttribute('data-skin-set'));
          var on = id === skin;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      });
    }

    function paintCollapse(collapsed) {
      document.documentElement.classList.toggle('skin-bar-collapsed', collapsed);
      document.querySelectorAll('[data-skin-bar]').forEach(function (bar) {
        bar.classList.toggle('is-collapsed', collapsed);
        var toggle = bar.querySelector('[data-skin-bar-toggle]');
        var panel = bar.querySelector('[data-skin-bar-panel]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
          toggle.setAttribute(
            'title',
            collapsed ? 'Show theme options' : 'Hide theme options'
          );
          toggle.setAttribute(
            'aria-label',
            collapsed ? 'Show theme options' : 'Hide theme options'
          );
        }
        if (panel) {
          if (collapsed) panel.setAttribute('hidden', '');
          else panel.removeAttribute('hidden');
        }
      });
    }

    function setCollapsed(collapsed, persist) {
      collapsed = !!collapsed;
      if (persist !== false) {
        try {
          localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
        } catch (e) {}
      }
      paintCollapse(collapsed);
    }

    function apply(skin, persist) {
      skin = normalize(skin);
      var mode = modeOf(skin);
      var label = (SKINS[skin] && SKINS[skin].label) || skin;
      document.documentElement.setAttribute('data-skin', skin);
      document.documentElement.setAttribute('data-skin-mode', mode);
      document.documentElement.setAttribute('data-skin-label', label);
      if (persist !== false) {
        try {
          localStorage.setItem(STORAGE, skin);
        } catch (e) {}
      }
      var meta =
        document.querySelector('meta[data-theme-color]') ||
        document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', (SKINS[skin] && SKINS[skin].meta) || SKINS[DEFAULT].meta);
      paintBar(skin);
      try {
        document.dispatchEvent(
          new CustomEvent('sg:skinchange', {
            detail: { skin: skin, mode: mode, label: label },
          })
        );
      } catch (e2) {}
    }

    apply(current(), false);
    paintCollapse(isCollapsed());

    document.querySelectorAll('[data-skin-set]').forEach(function (btn) {
      on(btn, 'click', function () {
        apply(btn.getAttribute('data-skin-set'), true);
      });
    });

    document.querySelectorAll('[data-skin-bar-toggle]').forEach(function (btn) {
      on(btn, 'click', function () {
        setCollapsed(!isCollapsed(), true);
      });
    });

    window.SgSkin = {
      apply: apply,
      current: current,
      setCollapsed: setCollapsed,
      isCollapsed: isCollapsed,
      skins: SKINS,
    };
  })();

  /* ── Alluring gallery soundscape  /* ── Alluring gallery soundscape (Web Audio) ─────────────
     Unlocked by "Add sound". Warm pads + glass for categories / art. */
  var Sound = (function () {
    var ctx = null;
    var master = null;
    var enabled = false;
    var lastAt = 0;

    function ensure() {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      if (!ctx) {
        ctx = new AC();
        master = ctx.createGain();
        master.gain.value = 0.42;
        master.connect(ctx.destination);
      }
      if (ctx.state === 'suspended') {
        try {
          ctx.resume();
        } catch (e) {}
      }
      return ctx;
    }

    function now() {
      return ensure() ? ctx.currentTime : 0;
    }

    /** Soft tone into master with gentle filter envelope */
    function voice(opts) {
      var c = ensure();
      if (!c || !enabled) return;
      opts = opts || {};
      var t0 = opts.t0 != null ? opts.t0 : c.currentTime;
      var freq = opts.freq || 220;
      var dur = opts.dur || 0.8;
      var type = opts.type || 'sine';
      var peak = opts.gain != null ? opts.gain : 0.12;
      var f0 = opts.filter || 1800;
      var f1 = opts.filterEnd != null ? opts.filterEnd : f0 * 0.45;
      var detune = opts.detune || 0;

      var osc = c.createOscillator();
      var g = c.createGain();
      var f = c.createBiquadFilter();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, t0);
      if (opts.glide) {
        osc.frequency.exponentialRampToValueAtTime(Math.max(40, opts.glide), t0 + dur * 0.85);
      }
      if (detune) osc.detune.setValueAtTime(detune, t0);
      f.type = 'lowpass';
      f.Q.value = opts.q != null ? opts.q : 0.7;
      f.frequency.setValueAtTime(f0, t0);
      f.frequency.exponentialRampToValueAtTime(Math.max(80, f1), t0 + dur);

      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(peak, t0 + (opts.attack != null ? opts.attack : 0.04));
      g.gain.exponentialRampToValueAtTime(peak * 0.55, t0 + dur * 0.35);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

      osc.connect(f);
      f.connect(g);
      g.connect(master);
      osc.start(t0);
      osc.stop(t0 + dur + 0.05);
    }

    /** Breath of noise — velvet hush under tones */
    function hush(dur, peak, t0) {
      var c = ensure();
      if (!c || !enabled) return;
      t0 = t0 != null ? t0 : c.currentTime;
      dur = dur || 1.2;
      peak = peak != null ? peak : 0.03;
      var len = Math.floor(c.sampleRate * dur);
      var buf = c.createBuffer(1, len, c.sampleRate);
      var data = buf.getChannelData(0);
      for (var i = 0; i < len; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / len);
      }
      var src = c.createBufferSource();
      src.buffer = buf;
      var f = c.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 420;
      f.Q.value = 0.6;
      var g = c.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(peak, t0 + 0.12);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      src.connect(f);
      f.connect(g);
      g.connect(master);
      src.start(t0);
      src.stop(t0 + dur + 0.02);
    }

    /** Simple feedback delay for airy space */
    function space(inputNode, t0, mix) {
      var c = ensure();
      if (!c || !inputNode) return;
      var delay = c.createDelay(1.2);
      delay.delayTime.value = 0.18;
      var fb = c.createGain();
      fb.gain.value = 0.28;
      var wet = c.createGain();
      wet.gain.value = mix != null ? mix : 0.22;
      inputNode.connect(delay);
      delay.connect(fb);
      fb.connect(delay);
      delay.connect(wet);
      wet.connect(master);
    }

    function throttle(ms) {
      var t = performance.now();
      if (t - lastAt < (ms || 90)) return false;
      lastAt = t;
      return true;
    }

    function enable() {
      enabled = true;
      ensure();
      // Invitation chime — warm open fifth
      var t0 = now() + 0.02;
      hush(1.4, 0.028, t0);
      voice({ freq: 196.0, dur: 1.4, type: 'sine', gain: 0.1, filter: 900, attack: 0.08, t0: t0 });
      voice({ freq: 293.66, dur: 1.35, type: 'triangle', gain: 0.06, filter: 1400, detune: 4, t0: t0 + 0.05 });
      voice({ freq: 392.0, dur: 1.1, type: 'sine', gain: 0.045, filter: 2200, t0: t0 + 0.12 });
      voice({ freq: 587.33, dur: 0.9, type: 'sine', gain: 0.03, filter: 3200, t0: t0 + 0.22 });
    }

    function disable() {
      enabled = false;
      if (ctx && ctx.state === 'running') {
        // soft fade — don't hard-cut master mid-tone
        try {
          var t0 = ctx.currentTime;
          master.gain.cancelScheduledValues(t0);
          master.gain.setValueAtTime(master.gain.value, t0);
          master.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
          setTimeout(function () {
            if (!enabled && master) master.gain.setValueAtTime(0.42, ctx.currentTime);
          }, 280);
        } catch (e) {}
      }
    }

    /** Category / wing filter — soft ascending glass */
    function category(tag) {
      if (!enabled || !throttle(70)) return;
      var c = ensure();
      if (!c) return;
      var t0 = c.currentTime + 0.01;
      // Different colour per wing, still alluring
      var map = {
        all: [261.63, 329.63, 392.0, 523.25],
        places: [220.0, 277.18, 329.63, 440.0],
        process: [246.94, 311.13, 369.99, 493.88],
        walls: [196.0, 246.94, 293.66, 392.0],
      };
      var notes = map[tag] || map.all;
      hush(0.9, 0.018, t0);
      notes.forEach(function (f, i) {
        voice({
          freq: f,
          dur: 0.55 + i * 0.08,
          type: i % 2 ? 'triangle' : 'sine',
          gain: 0.07 - i * 0.01,
          filter: 2400 - i * 200,
          attack: 0.02 + i * 0.015,
          t0: t0 + i * 0.055,
          detune: i * 2,
        });
      });
      // airy sparkle
      voice({
        freq: notes[notes.length - 1] * 2,
        dur: 0.7,
        type: 'sine',
        gain: 0.022,
        filter: 4800,
        t0: t0 + 0.2,
      });
    }

    /** Artwork open — deep, sensual pad + gold shimmer */
    function artwork() {
      if (!enabled || !throttle(110)) return;
      var c = ensure();
      if (!c) return;
      var t0 = c.currentTime + 0.01;
      hush(1.6, 0.04, t0);
      // low body
      voice({
        freq: 98.0,
        dur: 1.7,
        type: 'sine',
        gain: 0.14,
        filter: 480,
        filterEnd: 220,
        attack: 0.12,
        t0: t0,
      });
      voice({
        freq: 146.83,
        dur: 1.55,
        type: 'triangle',
        gain: 0.08,
        filter: 700,
        detune: -6,
        attack: 0.1,
        t0: t0 + 0.04,
      });
      // warm third / fifth
      voice({
        freq: 196.0,
        dur: 1.35,
        type: 'sine',
        gain: 0.07,
        filter: 1200,
        t0: t0 + 0.08,
      });
      voice({
        freq: 246.94,
        dur: 1.25,
        type: 'sine',
        gain: 0.05,
        filter: 1800,
        t0: t0 + 0.14,
      });
      // gold shimmer cascade
      [392.0, 493.88, 587.33, 784.0].forEach(function (f, i) {
        voice({
          freq: f,
          dur: 0.85,
          type: 'sine',
          gain: 0.035 - i * 0.005,
          filter: 3600,
          attack: 0.01,
          t0: t0 + 0.18 + i * 0.07,
          detune: i * 3,
        });
      });
      // slight rising breath
      voice({
        freq: 110,
        glide: 164.81,
        dur: 1.4,
        type: 'sine',
        gain: 0.04,
        filter: 600,
        attack: 0.15,
        t0: t0 + 0.1,
      });
    }

    return {
      enable: enable,
      disable: disable,
      category: category,
      artwork: artwork,
      isEnabled: function () {
        return enabled;
      },
    };
  })();

  /* ── Member progress storage (guest + logged-in scope) ─── */
  var customerId = document.body.getAttribute('data-customer-id') || '';
  function storageKey(base) {
    return customerId ? base + '_c' + customerId : base;
  }
  function readJSON(base, fallback) {
    try {
      var raw = localStorage.getItem(storageKey(base));
      if (!raw && customerId) {
        // migrate guest → member once
        var guest = localStorage.getItem(base);
        if (guest) {
          localStorage.setItem(storageKey(base), guest);
          raw = guest;
        }
      }
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }
  function writeJSON(base, value) {
    try {
      localStorage.setItem(storageKey(base), JSON.stringify(value));
    } catch (e) {}
  }
  function sumArchiveXP() {
    var a = readJSON('sg_archive_v3', null) || readJSON('sg_archive_v2', {});
    return (a && a.xp) || 0;
  }
  function sumCollectorXP() {
    try {
      return parseInt(localStorage.getItem(storageKey('sg_collector_xp')) || localStorage.getItem('sg_collector_xp') || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }
  function sumContact() {
    var c = readJSON('sg_contact_v2', null) || readJSON('sg_contact_v1', {});
    return c || { connected: {}, followed: {}, xp: 0 };
  }
  function sumStory() {
    var v2 = readJSON('sg_story_v2', null);
    if (v2) return v2;
    return readJSON('sg_story_v1', { score: 0, unlocked: 0, answered: {}, xp: 0 });
  }
  function totalMemberXP(orderBonus) {
    var contact = sumContact();
    var story = sumStory();
    return (
      sumArchiveXP() +
      sumCollectorXP() +
      (contact.xp || 0) +
      (story.score || 0) * 25 +
      (orderBonus || 0)
    );
  }
  function memberRank(xp) {
    if (xp >= 2000) return 'Studio Kin';
    if (xp >= 1200) return 'Patron';
    if (xp >= 700) return 'Connoisseur';
    if (xp >= 350) return 'Collector';
    if (xp >= 120) return 'Seeker';
    return 'Guest of colour';
  }


  /* ── Custom cursor retired — visitors keep their own pointer ── */
  var cursor, cursorDot;
  if (false && fine && !reduce && !touch && !document.body.classList.contains('is-touch')) {
    cursor = document.createElement('div');
    cursor.className = 'sg-cursor';
    cursor.innerHTML =
      '<span class="sg-cursor__glow"></span><span class="sg-cursor__ring"></span><span class="sg-cursor__dot"></span><span class="sg-cursor__label"></span>';
    document.body.appendChild(cursor);
    document.body.classList.add('has-cursor');
    cursorDot = cursor;

    /* Full-viewport stream — single path; look follows site skin */
    var trail = document.createElement('canvas');
    trail.className = 'sg-paint-trail';
    trail.setAttribute('aria-hidden', 'true');
    document.body.appendChild(trail);
    var tctx = trail.getContext('2d', { alpha: true });
    var trailDpr = 1;
    var hue = 310;
    var pts = [];
    var lastPt = null;
    var trailDirty = false;
    var trailRaf = 0;
    var trailLive = false;
    var MAX_PTS = 160;
    var cursorSkin = 'light'; /* light | dark | color | wild */

    function readCursorSkin() {
      var skin = '';
      var mode = '';
      try {
        skin = (document.documentElement.getAttribute('data-skin') || '').toLowerCase();
        mode = (document.documentElement.getAttribute('data-skin-mode') || '').toLowerCase();
      } catch (eSkin) {}
      if (skin === 'wild') return 'wild';
      if (skin === 'color' || skin === 'drip') return 'color';
      if (skin === 'dark' || skin === 'dusk' || mode === 'dark') return 'dark';
      if (skin === 'light' || skin === 'dawn' || mode === 'light') return 'light';
      return 'light';
    }
    function syncCursorSkin() {
      cursorSkin = readCursorSkin();
      try {
        cursor.setAttribute('data-cursor-skin', cursorSkin);
        trail.setAttribute('data-cursor-skin', cursorSkin);
        document.documentElement.setAttribute('data-cursor-skin', cursorSkin);
      } catch (eSync) {}
    }
    syncCursorSkin();
    /* Re-sync when theme chips change */
    try {
      var skinObs = new MutationObserver(function () {
        var prev = cursorSkin;
        syncCursorSkin();
        if (prev !== cursorSkin) {
          pts = [];
          trailDirty = true;
          kickTrail();
        }
      });
      skinObs.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-skin', 'data-skin-mode']
      });
    } catch (eObs) {}

    function sizeTrail() {
      trailDpr = Math.min(window.devicePixelRatio || 1, 1.75);
      var w = window.innerWidth || 1;
      var h = window.innerHeight || 1;
      trail.width = Math.max(1, Math.floor(w * trailDpr));
      trail.height = Math.max(1, Math.floor(h * trailDpr));
      trail.style.width = w + 'px';
      trail.style.height = h + 'px';
      tctx.setTransform(trailDpr, 0, 0, trailDpr, 0, 0);
      trailDirty = true;
      kickTrail();
    }
    sizeTrail();
    on(window, 'resize', sizeTrail, { passive: true });

    /* One sample per path point — soft glow is the same blob, not a second line */
    function pushPoint(x, y, dist) {
      var sk = cursorSkin;
      var r;
      var a;
      if (sk === 'light') {
        /* Dawn — gentle soft light */
        r = 4.2 + Math.min(dist * 0.32, 14);
        a = 0.38;
      } else if (sk === 'dark') {
        /* Dusk — soft graphite / silver ink */
        r = 3.6 + Math.min(dist * 0.3, 13);
        a = 0.48;
      } else if (sk === 'wild') {
        /* Wild — soft rose heat trail */
        r = 5 + Math.min(dist * 0.42, 18);
        a = 0.78;
        hue = (320 + (hue + dist * 0.9) % 40) % 360;
      } else {
        /* Mind — full spectrum paint */
        r = 3.4 + Math.min(dist * 0.28, 12);
        a = 0.62;
        hue = (hue + dist * 0.45 + 0.25) % 360;
      }
      pts.push({ x: x, y: y, r: r, h: hue, a: a, sk: sk });
      while (pts.length > MAX_PTS) pts.shift();
    }

    function drawBlob(p) {
      var a = p.a;
      if (a < 0.02) return;
      var r = p.r;
      if (r < 0.4) return;
      var g = tctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
      var sk = p.sk || cursorSkin;

      if (sk === 'light') {
        /* Dawn — warm ivory soft light */
        g.addColorStop(0, 'rgba(255, 252, 246, ' + (a * 0.72).toFixed(3) + ')');
        g.addColorStop(0.32, 'rgba(255, 248, 238, ' + (a * 0.38).toFixed(3) + ')');
        g.addColorStop(0.7, 'rgba(245, 236, 220, ' + (a * 0.12).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(255, 250, 240, 0)');
      } else if (sk === 'dark') {
        /* Dusk — cool silver smoke */
        g.addColorStop(0, 'rgba(230, 236, 248, ' + (a * 0.55).toFixed(3) + ')');
        g.addColorStop(0.3, 'rgba(170, 185, 210, ' + (a * 0.32).toFixed(3) + ')');
        g.addColorStop(0.65, 'rgba(90, 100, 120, ' + (a * 0.16).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(40, 48, 64, 0)');
      } else if (sk === 'wild') {
        /* Wild — velvet rose / lipstick heat */
        g.addColorStop(0, 'rgba(255, 200, 220, ' + (a * 0.85).toFixed(3) + ')');
        g.addColorStop(0.3, 'rgba(255, 80, 140, ' + (a * 0.55).toFixed(3) + ')');
        g.addColorStop(0.65, 'rgba(160, 20, 70, ' + (a * 0.28).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(40, 5, 15, 0)');
      } else {
        /* Mind — saturated spectrum */
        g.addColorStop(0, 'hsla(' + p.h + ' 100% 68% / ' + (a * 0.88).toFixed(3) + ')');
        g.addColorStop(0.35, 'hsla(' + ((p.h + 14) % 360) + ' 98% 58% / ' + (a * 0.48).toFixed(3) + ')');
        g.addColorStop(0.75, 'hsla(' + ((p.h + 28) % 360) + ' 92% 52% / ' + (a * 0.14).toFixed(3) + ')');
        g.addColorStop(1, 'hsla(' + p.h + ' 90% 50% / 0)');
      }
      tctx.beginPath();
      tctx.fillStyle = g;
      tctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      tctx.fill();
    }

    function tickTrail() {
      if (pts.length || trailDirty) {
        tctx.save();
        tctx.setTransform(1, 0, 0, 1, 0, 0);
        tctx.clearRect(0, 0, trail.width, trail.height);
        tctx.restore();

        /* Mind: additive. Wild/Dusk: soft screen. Dawn: normal. */
        if (cursorSkin === 'color') tctx.globalCompositeOperation = 'lighter';
        else if (cursorSkin === 'wild' || cursorSkin === 'dark') tctx.globalCompositeOperation = 'screen';
        else tctx.globalCompositeOperation = 'source-over';

        var next = [];
        for (var j = 0; j < pts.length; j++) {
          var p = pts[j];
          /* Points stay put — only fade. No velocity = never runs ahead of the cursor. */
          drawBlob(p);
          if (p.sk === 'light') {
            p.a *= 0.9;
            p.r *= 0.982;
          } else if (p.sk === 'dark') {
            p.a *= 0.91;
            p.r *= 0.984;
          } else if (p.sk === 'wild') {
            p.a *= 0.9;
            p.r *= 0.98;
          } else {
            p.a *= 0.92;
            p.r *= 0.985;
          }
          if (p.a > 0.02 && p.r > 0.35) next.push(p);
        }
        pts = next;
        trailDirty = pts.length > 0;
        tctx.globalCompositeOperation = 'source-over';
      }

      if (pts.length || trailDirty) {
        trailRaf = requestAnimationFrame(tickTrail);
      } else {
        trailRaf = 0;
        trailLive = false;
      }
    }
    function kickTrail() {
      if (!trailLive) {
        trailLive = true;
        trailRaf = requestAnimationFrame(tickTrail);
      }
    }

    var cx = 0,
      cy = 0,
      tx = 0,
      ty = 0;
    var cursorRaf = 0;
    var cursorLive = false;

    function tickCursor() {
      /* Pure lerp — no spring overshoot past the pointer */
      cx = lerp(cx, tx, 0.32);
      cy = lerp(cy, ty, 0.32);
      cursor.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
      if (cursorSkin === 'color') {
        cursor.style.setProperty('--cursor-hue', String(Math.round(hue)));
      }
      var dx = Math.abs(tx - cx);
      var dy = Math.abs(ty - cy);
      if (dx > 0.12 || dy > 0.12) {
        cursorRaf = requestAnimationFrame(tickCursor);
      } else {
        cursorRaf = 0;
        cursorLive = false;
      }
    }
    function kickCursor() {
      if (!cursorLive) {
        cursorLive = true;
        cursorRaf = requestAnimationFrame(tickCursor);
      }
    }

    on(
      window,
      'pointermove',
      function (e) {
        tx = e.clientX;
        ty = e.clientY;
        /* Single stream only — interpolate along the real pointer path */
        if (lastPt) {
          var ddx = e.clientX - lastPt.x;
          var ddy = e.clientY - lastPt.y;
          var d = Math.hypot(ddx, ddy);
          if (d > 0.4) {
            var steps = Math.max(1, Math.min(12, Math.floor(d / 4)));
            for (var i = 1; i <= steps; i++) {
              var t = i / steps;
              pushPoint(lastPt.x + ddx * t, lastPt.y + ddy * t, d / steps);
            }
            trailDirty = true;
            kickTrail();
          }
        }
        lastPt = { x: e.clientX, y: e.clientY };
        cursor.classList.add('is-on');
        kickCursor();
      },
      { passive: true }
    );
    on(document, 'mouseleave', function () {
      cursor.classList.remove('is-on');
      lastPt = null;
    });
    on(document, 'mouseenter', function () {
      /* Reset so we never join a stale segment across the edge */
      lastPt = null;
    });

    // Delegate hot targets — avoid N listeners on every gallery card
    on(document, 'pointerover', function (e) {
      var el = e.target && e.target.closest
        ? e.target.closest('a, button, [data-tilt], [data-arc-item], .cfg__option, input, textarea, select')
        : null;
      if (!el) return;
      cursor.classList.add('is-hot');
      var label = el.getAttribute('data-cursor') || '';
      var lab = cursor.querySelector('.sg-cursor__label');
      if (lab) lab.textContent = label;
      cursor.classList.toggle('has-label', !!label);
    });
    on(document, 'pointerout', function (e) {
      var el = e.target && e.target.closest
        ? e.target.closest('a, button, [data-tilt], [data-arc-item], .cfg__option, input, textarea, select')
        : null;
      if (!el) return;
      var to = e.relatedTarget;
      if (to && el.contains(to)) return;
      cursor.classList.remove('is-hot', 'has-label');
    });
  }


  /* ── Homepage premiere opening ─────────────────────────── */
  (function () {
    var root = document.querySelector('[data-premiere]');
    if (!root) return;
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-premiere-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-premiere-dot]'));
    var cap = root.querySelector('[data-premiere-caption]');
    var capLink = root.querySelector('[data-premiere-caption-link]');
    var i = 0;
    var timer;

    function slideMeta(slide) {
      if (!slide) return { label: '', slug: '', href: '' };
      var slug = slide.getAttribute('data-work-slug') || '';
      var label = slide.getAttribute('data-work-label') || '';
      var link = slide.querySelector('[data-premiere-work-link]');
      var href = link ? link.getAttribute('href') || '' : '';
      if (!href && slug) {
        href = (capLink && capLink.getAttribute('href')) || '';
        if (href.indexOf('work=') >= 0) {
          href = href.replace(/work=[^&#]*/, 'work=' + encodeURIComponent(slug));
        }
      }
      return { label: label, slug: slug, href: href };
    }

    function show(n) {
      if (!slides.length) return;
      i = ((n % slides.length) + slides.length) % slides.length;
      slides.forEach(function (s, idx) {
        s.classList.toggle('is-active', idx === i);
      });
      dots.forEach(function (d, idx) {
        d.classList.toggle('is-active', idx === i);
      });
      var meta = slideMeta(slides[i]);
      if (cap) cap.textContent = meta.label || '';
      if (capLink && meta.href) {
        capLink.setAttribute('href', meta.href);
        capLink.setAttribute('aria-label', 'Open ' + (meta.label || 'work') + ' in the Walkthrough');
      }
    }
    function next() {
      show(i + 1);
    }
    function start() {
      clearInterval(timer);
      if (reduce) return;
      timer = setInterval(next, narrow ? 6400 : 5200);
    }

    /*
      Opening night choreography:
      1) gentle hold (curtains closed, soft breath)
      2) lower half smoky mist + stage lights warm in
      3) curtains pull open
      4) mist settles, lights soften
    */
    /*
      Opening curtains on home load:
      1) hold closed with soft breath
      2) pull open to reveal the work
    */
    function openPremiere() {
      if (reduce) {
        root.classList.add('is-hold', 'is-open', 'is-settled');
        document.body.classList.add('premiere-open');
        return;
      }
      root.classList.add('is-hold');
      var holdMs = narrow ? 550 : 900;
      var openMs = narrow ? 1400 : 2000;
      setTimeout(function () {
        root.classList.add('is-open');
        document.body.classList.add('premiere-open');
      }, holdMs);
      setTimeout(function () {
        root.classList.add('is-settled');
      }, openMs);
    }

    requestAnimationFrame(function () {
      setTimeout(openPremiere, reduce ? 0 : 80);
    });

    // Don't spin the carousel until curtains have opened
    dots.forEach(function (d, idx) {
      on(d, 'click', function () {
        show(idx);
        start();
      });
    });
    show(0);
    /* Start carousel after curtains have opened */
    setTimeout(start, reduce ? 0 : narrow ? 2800 : 3600);

    // Subtle pointer parallax on satellites
    if (fine && !reduce) {
      var sats = root.querySelectorAll('[data-premiere-sat]');
      on(window, 'pointermove', function (e) {
        var x = (e.clientX / window.innerWidth - 0.5) * 2;
        var y = (e.clientY / window.innerHeight - 0.5) * 2;
        sats.forEach(function (s, idx) {
          var f = (idx + 1) * 4;
          s.style.translate = x * f + 'px ' + y * f + 'px';
        });
      }, { passive: true });
    }
  })();

  /* ── Header + mobile nav (body-level portal) ───────────── */
  var header = document.querySelector('[data-header]');
  var menuBtn = document.querySelector('[data-menu-toggle]');
  var mnav = document.querySelector('[data-mnav]');
  var drawer = document.querySelector('[data-menu-drawer]');
  var menuScrim = document.querySelector('[data-menu-scrim]');
  var headerDock = document.querySelector('[data-header-dock]');
  var menuCloseTimer = null;
  var menuScrollY = 0;

  /* Critical: mount menu on <body> so fixed layers aren't trapped by
     sticky header / backdrop-filter / Shopify section stacking. */
  if (mnav && mnav.parentNode !== document.body) {
    document.body.appendChild(mnav);
  } else if (!mnav && drawer && drawer.parentNode !== document.body) {
    document.body.appendChild(drawer);
    if (menuScrim && menuScrim.parentNode !== document.body) {
      document.body.appendChild(menuScrim);
    }
  }

  function lockBodyScroll(lock) {
    if (lock) {
      menuScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + menuScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, menuScrollY || 0);
    }
  }

  function setMenuOpen(open) {
    if (!mnav && !drawer) return;
    clearTimeout(menuCloseTimer);
    var shell = mnav || drawer;

    if (open) {
      if (mnav) mnav.removeAttribute('hidden');
      if (drawer) drawer.removeAttribute('hidden');
      if (menuScrim) {
        menuScrim.removeAttribute('hidden');
        menuScrim.setAttribute('aria-hidden', 'false');
      }
      lockBodyScroll(true);
      /* Double rAF so CSS transitions run from the closed state */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          document.body.classList.add('nav-open');
          shell.classList.add('is-open');
        });
      });
      /* Focus first link for a11y */
      setTimeout(function () {
        var first = drawer && drawer.querySelector('a, button');
        if (first && first.focus) first.focus();
      }, 80);
    } else {
      document.body.classList.remove('nav-open');
      if (shell) shell.classList.remove('is-open');
      menuCloseTimer = setTimeout(function () {
        if (mnav) mnav.setAttribute('hidden', '');
        if (drawer) drawer.setAttribute('hidden', '');
        if (menuScrim) {
          menuScrim.setAttribute('hidden', '');
          menuScrim.setAttribute('aria-hidden', 'true');
        }
        lockBodyScroll(false);
      }, 380);
    }

    if (menuBtn) {
      menuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      menuBtn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    }
  }

  if (menuBtn) {
    on(menuBtn, 'click', function (e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setMenuOpen(!document.body.classList.contains('nav-open'));
    });
  }

  document.querySelectorAll('[data-menu-close]').forEach(function (btn) {
    on(btn, 'click', function (e) {
      if (e) e.preventDefault();
      setMenuOpen(false);
    });
  });

  document.querySelectorAll('[data-menu-link]').forEach(function (link) {
    on(link, 'click', function () {
      setMenuOpen(false);
    });
  });

  on(document, 'keydown', function (e) {
    if (e.key === 'Escape' && document.body.classList.contains('nav-open')) {
      setMenuOpen(false);
    }
  });

  function onScrollHeader() {
    if (!header) return;
    header.classList.toggle('is-solid', (window.scrollY || 0) > 40);
  }
  /* Scroll work coalesced below via rAF (see scroll hub) */

  /* Sync full chrome height (nav + docked HUD) for sticky filters etc. */
  function syncChromeHeight() {
    if (!header) return;
    var h = Math.ceil(header.getBoundingClientRect().height) || 0;
    if (h > 0) {
      document.documentElement.style.setProperty('--chrome-h', h + 'px');
    }
  }

  /**
   * Dock Archive / Contact (and About) toolbars into the sticky header
   * so they travel with the main nav as one functional bar.
   */
  function dockPageHud(hud) {
    if (!header || !headerDock || !hud || hud.getAttribute('data-docked') === '1') return;
    headerDock.appendChild(hud);
    hud.setAttribute('data-docked', '1');
    header.classList.add('has-dock');
    // Show dock whenever HUD is visible
    function refreshDock() {
      var visible = !hud.hasAttribute('hidden');
      if (visible) headerDock.removeAttribute('hidden');
      else {
        // Keep dock open if another docked bar is visible
        var any = headerDock.querySelector(
          '.gx-hud:not([hidden]), .gx-complete-bar:not([hidden]), .cx-hud:not([hidden]), .sj-hud:not([hidden])'
        );
        if (any) headerDock.removeAttribute('hidden');
        else headerDock.setAttribute('hidden', '');
      }
      header.classList.toggle('has-dock-live', !headerDock.hasAttribute('hidden'));
      syncChromeHeight();
    }
    refreshDock();
    try {
      var mo = new MutationObserver(refreshDock);
      mo.observe(hud, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
    } catch (e) {}
  }

  // Prefer early dock so first paint sticks correctly
  function dockAllPageHuds() {
    document.querySelectorAll('[data-gx-hud], [data-cx-hud], [data-sj-hud]').forEach(dockPageHud);
    syncChromeHeight();
  }
  dockAllPageHuds();
  on(window, 'resize', syncChromeHeight, { passive: true });
  if (window.ResizeObserver && header) {
    try {
      var ro = new ResizeObserver(function () {
        syncChromeHeight();
      });
      ro.observe(header);
    } catch (e) {}
  }

  /* ── Website music (Spotify) + walkthrough prev / play / next ─ */
  (function () {
    var player = document.querySelector('[data-spotify-player]');
    if (!player) return;
    var embedHost = player.querySelector('[data-spotify-iframe]');
    var toggles = Array.prototype.slice.call(document.querySelectorAll('[data-spotify-toggle]'));
    var closers = Array.prototype.slice.call(document.querySelectorAll('[data-spotify-close]'));
    var embed = player.getAttribute('data-spotify-embed') || '';
    var dockOpen = false; /* floating playlist panel visible */
    var sessionOn = false; /* music session active (iframe loaded) */
    var isPlaying = false;
    var embedController = null;
    var apiReady = null; /* Promise → Spotify IFrame API */
    var wantPlay = false;
    var plainIframe = null;
    var trackTitle = '';
    var trackUri = '';
    var trackFetchId = 0;
    var DEFAULT_TRACK_LABEL = 'Studio playlist';

    var walkMusic = document.querySelector('[data-walk-music]');
    var walkPlayBtn = document.querySelector('[data-walk-music-play]');
    var walkPrevBtn = document.querySelector('[data-walk-music-prev]');
    var walkNextBtn = document.querySelector('[data-walk-music-next]');
    var walkPlayIcon = document.querySelector('[data-walk-music-play-icon]');
    var walkPauseIcon = document.querySelector('[data-walk-music-pause-icon]');
    var walkTrackTitleEl = document.querySelector('[data-walk-track-title]');
    var playerMetaEm = player.querySelector('.sg-player__meta em');

    function embedToUri(url) {
      if (!url) return '';
      var m = String(url).match(
        /open\.spotify\.com\/(?:embed\/)?(playlist|album|track|episode|show)\/([a-zA-Z0-9]+)/
      );
      if (m) return 'spotify:' + m[1] + ':' + m[2];
      return '';
    }

    function spotifyUriToOpenUrl(uri) {
      var m = String(uri || '').match(/^spotify:(track|episode|album|playlist|show):([a-zA-Z0-9]+)/i);
      if (!m) return '';
      return 'https://open.spotify.com/' + m[1] + '/' + m[2];
    }

    function setTrackTitle(title, uri) {
      trackTitle = (title || '').trim();
      if (uri) trackUri = uri;
      paintMusicUi();
    }

    function resolveTrackTitle(uri) {
      if (!uri || uri === trackUri && trackTitle) return;
      var openUrl = spotifyUriToOpenUrl(uri);
      if (!openUrl) return;
      var id = ++trackFetchId;
      /* Public oEmbed — no auth; returns track/show title for the URI */
      var oembed =
        'https://open.spotify.com/oembed?url=' + encodeURIComponent(openUrl);
      fetch(oembed, { mode: 'cors', credentials: 'omit' })
        .then(function (r) {
          if (!r.ok) throw new Error('oembed ' + r.status);
          return r.json();
        })
        .then(function (j) {
          if (id !== trackFetchId) return;
          var t = (j && (j.title || j.name)) || '';
          if (t) setTrackTitle(t, uri);
        })
        .catch(function () {
          /* keep previous / default label */
        });
    }

    function handlePlaybackPayload(d) {
      if (!d || typeof d !== 'object') return;
      if (typeof d.isPaused === 'boolean') {
        isPlaying = !d.isPaused && sessionOn;
      } else if (typeof d.paused === 'boolean') {
        isPlaying = !d.paused && sessionOn;
      }
      var uri =
        d.playingURI ||
        d.playingUri ||
        d.uri ||
        (d.track && (d.track.uri || d.track.id)) ||
        '';
      if (uri && typeof uri === 'string') {
        if (uri.indexOf('spotify:') !== 0 && /^[a-zA-Z0-9]{10,}$/.test(uri)) {
          uri = 'spotify:track:' + uri;
        }
        if (uri !== trackUri) {
          trackUri = uri;
          resolveTrackTitle(uri);
        }
      }
      paintMusicUi();
    }

    function loadSpotifyApi() {
      if (apiReady) return apiReady;
      apiReady = new Promise(function (resolve) {
        if (window.__sgSpotifyIframeApi) {
          resolve(window.__sgSpotifyIframeApi);
          return;
        }
        var prev = window.onSpotifyIframeApiReady;
        window.onSpotifyIframeApiReady = function (API) {
          window.__sgSpotifyIframeApi = API;
          try {
            if (typeof prev === 'function') prev(API);
          } catch (ePrev) {}
          resolve(API);
        };
        if (!document.querySelector('script[data-spotify-iframe-api]')) {
          var s = document.createElement('script');
          s.src = 'https://open.spotify.com/embed/iframe-api/v1';
          s.async = true;
          s.setAttribute('data-spotify-iframe-api', '1');
          document.head.appendChild(s);
        }
        /* If script already present and ready fired before our hook */
        setTimeout(function () {
          if (window.__sgSpotifyIframeApi) resolve(window.__sgSpotifyIframeApi);
        }, 2500);
      });
      return apiReady;
    }

    function ensureController(cb) {
      if (embedController) {
        if (cb) cb(embedController);
        return;
      }
      if (!embedHost) return;
      loadSpotifyApi().then(function (API) {
        if (!API || embedController) {
          if (cb && embedController) cb(embedController);
          return;
        }
        var uri = embedToUri(embed);
        var options = { width: '100%', height: 152 };
        if (uri) options.uri = uri;
        try {
          API.createController(embedHost, options, function (controller) {
            embedController = controller;
            try {
              controller.addListener('playback_update', function (e) {
                handlePlaybackPayload((e && e.data) || e || {});
              });
            } catch (eL) {}
            try {
              controller.addListener('ready', function () {
                paintMusicUi();
              });
            } catch (eRdy) {}
            if (wantPlay) {
              try {
                controller.play();
                isPlaying = true;
              } catch (eP) {}
            }
            if (cb) cb(controller);
            paintMusicUi();
          });
        } catch (eC) {
          /* Fall back to plain iframe src */
          loadIframeSrc(true);
          if (cb) cb(null);
        }
      });
    }

    function getEmbedIframe() {
      if (plainIframe && plainIframe.isConnected) return plainIframe;
      if (!embedHost) return null;
      var existing = embedHost.querySelector('iframe');
      if (existing) return existing;
      return null;
    }

    function loadIframeSrc(autoplay) {
      if (!embedHost || !embed) return;
      /* If IFrame API already owns the host, don't inject a second iframe */
      if (embedController) return;
      var src = embed;
      if (autoplay && src.indexOf('autoplay=') === -1) {
        src += (src.indexOf('?') >= 0 ? '&' : '?') + 'autoplay=1';
      }
      if (src.indexOf('theme=') === -1) {
        src += (src.indexOf('?') >= 0 ? '&' : '?') + 'theme=0';
      }
      var el = getEmbedIframe();
      if (!el) {
        el = document.createElement('iframe');
        el.title = 'Seb Gower Art playlist';
        el.setAttribute('allow', 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture');
        el.style.cssText = 'border:0;border-radius:12px;width:100%;height:152px;display:block;';
        embedHost.innerHTML = '';
        embedHost.appendChild(el);
        plainIframe = el;
      }
      if (el.getAttribute('src') !== src) el.setAttribute('src', src);
    }

    function paintMusicUi() {
      var displayTitle =
        trackTitle ||
        (sessionOn ? (isPlaying ? 'Playing…' : DEFAULT_TRACK_LABEL) : DEFAULT_TRACK_LABEL);

      toggles.forEach(function (btn) {
        btn.setAttribute('aria-expanded', dockOpen ? 'true' : 'false');
        btn.setAttribute(
          'title',
          sessionOn
            ? dockOpen
              ? 'Hide playlist'
              : displayTitle
            : 'Add sound — studio atmosphere and gallery tones'
        );
        var icon = btn.querySelector('[data-spotify-icon]');
        var label = btn.querySelector('[data-spotify-label]');
        if (icon) icon.textContent = '♪';
        if (label) label.textContent = sessionOn ? 'Sound on' : 'Add sound';
        btn.classList.toggle('is-sound-on', sessionOn);
      });

      document.body.classList.toggle('spotify-open', dockOpen);
      document.body.classList.toggle('sound-on', sessionOn);
      player.classList.toggle(
        'sg-player--walk',
        sessionOn && document.body.classList.contains('walk-open')
      );

      if (playerMetaEm) {
        playerMetaEm.textContent = displayTitle;
        playerMetaEm.setAttribute('title', displayTitle);
      }

      if (walkMusic) {
        walkMusic.classList.toggle('is-sound-on', sessionOn);
        walkMusic.classList.toggle('is-playing', isPlaying);
        walkMusic.setAttribute(
          'aria-label',
          sessionOn ? 'Website music · ' + displayTitle : 'Website music'
        );
      }
      if (walkTrackTitleEl) {
        walkTrackTitleEl.textContent = displayTitle;
        walkTrackTitleEl.setAttribute('title', displayTitle);
      }
      if (walkPlayBtn) {
        walkPlayBtn.classList.toggle('is-playing', !!isPlaying);
        walkPlayBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
        walkPlayBtn.setAttribute(
          'title',
          isPlaying ? 'Pause · ' + displayTitle : 'Play · ' + displayTitle
        );
        walkPlayBtn.setAttribute(
          'aria-label',
          isPlaying ? 'Pause website music' : 'Play website music'
        );
      }
      /* Icons toggled purely via .is-playing CSS — one glyph visible at a time */
      if (walkPlayIcon) {
        walkPlayIcon.removeAttribute('hidden');
        walkPlayIcon.setAttribute('aria-hidden', 'true');
      }
      if (walkPauseIcon) {
        walkPauseIcon.removeAttribute('hidden');
        walkPauseIcon.setAttribute('aria-hidden', 'true');
      }
    }

    function showDock() {
      dockOpen = true;
      player.hidden = false;
      player.removeAttribute('hidden');
      paintMusicUi();
      if (drawer && !drawer.hasAttribute('hidden')) {
        drawer.setAttribute('hidden', '');
        if (menuBtn) menuBtn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('nav-open');
      }
    }

    function hideDock() {
      dockOpen = false;
      player.hidden = true;
      player.setAttribute('hidden', '');
      paintMusicUi();
    }

    function startSession(opts) {
      opts = opts || {};
      sessionOn = true;
      wantPlay = !!opts.play;
      Sound.enable();
      /* Prefer IFrame API (play/pause control); fall back to autoplay src */
      ensureController(function (ctrl) {
        if (ctrl && wantPlay) {
          try {
            ctrl.play();
            isPlaying = true;
          } catch (e1) {
            try {
              ctrl.resume();
              isPlaying = true;
            } catch (e2) {
              loadIframeSrc(true);
              isPlaying = true;
            }
          }
        } else if (!ctrl) {
          loadIframeSrc(!!opts.play);
          isPlaying = !!opts.play;
        }
        paintMusicUi();
      });
      /* Also set src if controller is slow so autoplay still works */
      if (!embedController && !getEmbedIframe() && embed) {
        loadIframeSrc(!!opts.play);
      }
      if (opts.showDock) showDock();
      else paintMusicUi();
    }

    function stopSession() {
      sessionOn = false;
      isPlaying = false;
      wantPlay = false;
      trackTitle = '';
      trackUri = '';
      trackFetchId++;
      Sound.disable();
      if (embedController) {
        try {
          embedController.pause();
        } catch (eP) {}
        try {
          embedController.destroy();
        } catch (eD) {}
      }
      embedController = null;
      plainIframe = null;
      if (embedHost) embedHost.innerHTML = '';
      hideDock();
      paintMusicUi();
    }

    function playMusic() {
      if (!sessionOn) {
        startSession({ play: true, showDock: false });
        return;
      }
      wantPlay = true;
      ensureController(function (ctrl) {
        if (ctrl) {
          try {
            ctrl.resume();
          } catch (eR) {
            try {
              ctrl.play();
            } catch (eP) {
              loadIframeSrc(true);
            }
          }
        } else {
          loadIframeSrc(true);
        }
        isPlaying = true;
        paintMusicUi();
      });
    }

    function pauseMusic() {
      wantPlay = false;
      if (embedController) {
        try {
          embedController.pause();
        } catch (eP) {}
      }
      isPlaying = false;
      paintMusicUi();
    }

    function togglePlayMusic() {
      if (!sessionOn || !isPlaying) playMusic();
      else pauseMusic();
    }

    function postToEmbed(cmd) {
      try {
        var frame = getEmbedIframe() || (embedHost && embedHost.querySelector('iframe'));
        if (!frame || !frame.contentWindow) return;
        var origin = 'https://open.spotify.com';
        var payloads = [
          { command: cmd },
          { type: 'spotify:command', command: cmd },
          { type: 'command', command: cmd },
          { type: 'set_message', payload: { name: 'player_command', payload: { command: cmd } } },
          {
            type: 'message_type_playback_control',
            payload: { command: cmd },
          },
        ];
        for (var i = 0; i < payloads.length; i++) {
          try {
            frame.contentWindow.postMessage(payloads[i], origin);
            frame.contentWindow.postMessage(payloads[i], '*');
          } catch (eOne) {}
        }
      } catch (ePost) {}
    }

    function tryControllerSkip(ctrl, dir) {
      if (!ctrl) return false;
      var names =
        dir > 0
          ? ['nextTrack', 'skipNext', 'next', 'forward']
          : ['previousTrack', 'skipPrevious', 'previous', 'back', 'restart'];
      for (var i = 0; i < names.length; i++) {
        var fn = ctrl[names[i]];
        if (typeof fn === 'function') {
          try {
            fn.call(ctrl);
            return true;
          } catch (eFn) {}
        }
      }
      /* Some builds expose methods under a nested player */
      try {
        if (ctrl._playbackController) {
          return tryControllerSkip(ctrl._playbackController, dir);
        }
      } catch (eNest) {}
      return false;
    }

    function skipTrack(dir) {
      dir = dir >= 0 ? 1 : -1;
      if (!sessionOn) {
        startSession({ play: true, showDock: false });
      }
      ensureController(function (ctrl) {
        var usedApi = tryControllerSkip(ctrl, dir);
        if (dir > 0) {
          postToEmbed('next_track');
          postToEmbed('skip_next');
          postToEmbed('next');
        } else {
          /* Double-tap prev: first restart, second true previous via postMessage */
          if (ctrl) {
            try {
              ctrl.restart();
            } catch (eR) {}
          }
          postToEmbed('previous_track');
          postToEmbed('skip_prev');
          postToEmbed('previous');
        }
        if (!isPlaying) {
          try {
            if (ctrl) ctrl.play();
          } catch (eN) {}
          isPlaying = true;
        }
        /*
          Keep dock closed by default for clean walk UI; Spotify may still
          advance the playlist via postMessage when the embed is loaded.
        */
        if (!usedApi && !dockOpen) {
          /* Brief dock open gives native skip affordance if embed ignored messages */
          showDock();
          setTimeout(function () {
            if (document.body.classList.contains('walk-open') && dockOpen) {
              /* leave open a moment so user can confirm skip, then keep if they interact */
            }
          }, 100);
        }
        paintMusicUi();
      });
    }

    function prevTrack() {
      skipTrack(-1);
    }

    function nextTrack() {
      skipTrack(1);
    }

    function openPlayer() {
      startSession({ play: true, showDock: true });
    }

    function closePlayer() {
      /* Header “Hide” / Escape — stop session fully (same as before) */
      stopSession();
    }

    function togglePlayer() {
      if (dockOpen) {
        hideDock();
        /* Keep music playing when only hiding the dock from header */
        if (!sessionOn) stopSession();
      } else if (sessionOn) {
        showDock();
      } else {
        openPlayer();
      }
    }

    /* Keep dock placement in sync when walkthrough opens/closes */
    try {
      var walkObs = new MutationObserver(function () {
        paintMusicUi();
        if (!sessionOn) player.classList.remove('sg-player--walk');
      });
      walkObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    } catch (eWalkObs) {}

    /* Catch track metadata / playback events from Spotify iframe */
    on(window, 'message', function (e) {
      try {
        if (!e || !e.origin || String(e.origin).indexOf('spotify.com') < 0) return;
        var data = e.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (eJ) {
            return;
          }
        }
        if (!data || typeof data !== 'object') return;
        var payload = data.payload || data.data || data;
        if (data.type === 'playback_update' || data.type === 'spotify:playback_update') {
          handlePlaybackPayload(payload && payload.data ? payload.data : payload);
          return;
        }
        if (payload && (payload.playingURI || payload.isPaused != null || payload.paused != null)) {
          handlePlaybackPayload(payload);
        }
      } catch (eMsg) {}
    });

    /* Prefetch playlist name as default label */
    (function prefetchPlaylistTitle() {
      var openUrl = '';
      try {
        var uri = embedToUri(embed);
        openUrl = spotifyUriToOpenUrl(uri);
      } catch (eU) {}
      if (!openUrl && embed) {
        openUrl = String(embed).replace('/embed/', '/').split('?')[0];
      }
      if (!openUrl) return;
      fetch('https://open.spotify.com/oembed?url=' + encodeURIComponent(openUrl), {
        mode: 'cors',
        credentials: 'omit',
      })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (j) {
          if (j && j.title && !trackTitle) {
            DEFAULT_TRACK_LABEL = j.title;
            paintMusicUi();
          }
        })
        .catch(function () {});
    })();

    toggles.forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        togglePlayer();
      });
    });
    closers.forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        closePlayer();
      });
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && dockOpen) hideDock();
    });

    /* Walkthrough transport */
    if (walkPlayBtn) {
      on(walkPlayBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        try {
          var walkRoot = document.querySelector('[data-walk]');
          if (walkRoot && walkRoot._sgCinema) walkRoot._sgCinema.stopTone();
        } catch (eTone) {}
        togglePlayMusic();
      });
    }
    if (walkPrevBtn) {
      on(walkPrevBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        prevTrack();
      });
    }
    if (walkNextBtn) {
      on(walkNextBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        nextTrack();
      });
    }

    /* Stop media bubbling into walk gestures */
    if (walkMusic) {
      on(walkMusic, 'pointerdown', function (e) {
        e.stopPropagation();
      });
      on(walkMusic, 'click', function (e) {
        e.stopPropagation();
      });
    }

    paintMusicUi();
    /* Warm the IFrame API so first play is snappy */
    loadSpotifyApi();

    window.SgMusic = {
      play: playMusic,
      pause: pauseMusic,
      toggle: togglePlayMusic,
      next: nextTrack,
      prev: prevTrack,
      isPlaying: function () {
        return isPlaying;
      },
      isOn: function () {
        return sessionOn;
      },
    };
  })();

  /* ── Scroll hub (single rAF) ─────────────────────────────
     Multiple scroll listeners were stacking layout work and
     freezing the gallery on long pages. One tick does it all. */
  var bar = document.createElement('div');
  bar.className = 'progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.prepend(bar);

  var sticky = document.querySelector('[data-sticky-bar]');
  if (sticky) document.body.classList.add('has-sticky');

  var parallaxLayers =
    !reduce && fine && !touch
      ? Array.prototype.slice.call(document.querySelectorAll('[data-parallax-layer]'))
      : [];
  var productStickies = Array.prototype.slice.call(
    document.querySelectorAll('[data-product-buy] [data-buy-sticky], .buy-sticky[data-buy-sticky]')
  );
  // Product page sticky claim bars registered later — re-query lightly
  function collectProductStickies() {
    productStickies = Array.prototype.slice.call(document.querySelectorAll('[data-buy-sticky]'));
  }
  collectProductStickies();

  var scrollQueued = false;
  function runScrollFrame() {
    scrollQueued = false;
    // Skip while walkthrough / modal locks the page
    if (document.body.classList.contains('walk-open') || document.body.classList.contains('nav-open')) {
      return;
    }
    var y = window.scrollY || window.pageYOffset || 0;
    var max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);

    document.documentElement.style.setProperty('--scroll', String(clamp(y / max, 0, 1)));
    onScrollHeader();

    if (sticky) sticky.classList.toggle('is-visible', y > 520);

    productStickies.forEach(function (el) {
      el.classList.toggle('is-visible', y > 520);
    });

    if (parallaxLayers.length) {
      var vh = window.innerHeight || 1;
      for (var pi = 0; pi < parallaxLayers.length; pi++) {
        var layer = parallaxLayers[pi];
        var speed = parseFloat(layer.getAttribute('data-parallax-layer') || '0.1');
        var parent = layer.closest('[data-parallax-root]') || layer.parentElement;
        if (!parent) continue;
        var r = parent.getBoundingClientRect();
        // Skip off-screen parallax parents
        if (r.bottom < -80 || r.top > vh + 80) continue;
        var mid = r.top + r.height / 2 - vh / 2;
        layer.style.transform =
          'translate3d(0,' + (-mid * speed).toFixed(1) + 'px,0) scale(1.08)';
      }
    }
  }
  function queueScroll() {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(runScrollFrame);
  }
  on(window, 'scroll', queueScroll, { passive: true });
  on(window, 'resize', queueScroll, { passive: true });
  runScrollFrame();

  /* ── Scroll reveals ────────────────────────────────────── */
  if (!reduce && 'IntersectionObserver' in window) {
    var revObs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add('is-in');
            revObs.unobserve(en.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' }
    );
    document.querySelectorAll('[data-reveal]').forEach(function (el, i) {
      el.style.setProperty('--d', (i % 6) * 0.06 + 's');
      revObs.observe(el);
    });
  } else {
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-in');
    });
  }

  /* ── Magnetic buttons (desktop only, skip touch) ───────── */
  if (fine && !reduce && !touch) {
    document.querySelectorAll('[data-magnetic]').forEach(function (btn) {
      var strength = 14;
      var raf = 0;
      var lx = 0,
        ly = 0;
      on(btn, 'pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        lx = ((e.clientX - (r.left + r.width / 2)) / r.width) * strength;
        ly = ((e.clientY - (r.top + r.height / 2)) / r.height) * strength;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          btn.style.transform = 'translate3d(' + lx + 'px,' + ly + 'px,0)';
        });
      });
      on(btn, 'pointerleave', function () {
        btn.style.transform = '';
      });
    });
  }

  /* ── 3D tilt cards (desktop only; not on every gallery tile) ─ */
  if (fine && !reduce && !touch) {
    document.querySelectorAll('[data-tilt]').forEach(function (card) {
      // Gallery wall uses content-visibility + many nodes — never tilt those
      if (card.closest('[data-archive], [data-gx-grid], .arc__grid, .gx-grid')) return;
      var media =
        card.querySelector('img, video, .artist-card__media, .home-model__media') || card;
      var raf = 0;
      var next = '';
      on(card, 'pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        var rx = (-py * 8).toFixed(2);
        var ry = (px * 10).toFixed(2);
        next =
          'perspective(900px) rotateX(' +
          rx +
          'deg) rotateY(' +
          ry +
          'deg) scale3d(1.015,1.015,1.015)';
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          card.style.transform = next;
          if (media && media !== card) {
            media.style.transform =
              'translate3d(' + (px * 8).toFixed(1) + 'px,' + (py * 8).toFixed(1) + 'px,0) scale(1.04)';
          }
        });
      });
      on(card, 'pointerleave', function () {
        card.style.transform = '';
        if (media && media !== card) media.style.transform = '';
      });
    });
  }

  /* ── Archive gallery · page-flow works ───────────────────
     Paintings live in normal document flow (one page scroll).
     Full-screen mode uses native overflow on the viewport only. */
  document.querySelectorAll('[data-gx-wall-viewport]').forEach(function (vp) {
    var wall = vp.closest('[data-gx-wall]') || vp.parentElement;
    var gxRoot = vp.closest('[data-gx], .gx') || null;

    vp.classList.add('is-page-flow', 'is-native-scroll');
    if (wall) wall.classList.add('is-page-flow');

    /* Clear any inline styles left from older drag/pan builds */
    try {
      vp.style.touchAction = '';
      vp.style.overflowX = '';
      vp.style.overflowY = '';
      vp.style.cursor = '';
      vp.style.overscrollBehaviorY = '';
      vp.style.overscrollBehaviorX = '';
    } catch (eClr) {}

    function inWallFs() {
      return !!(
        document.body.classList.contains('wall-fs-open') ||
        (gxRoot && gxRoot.classList.contains('is-wall-fs'))
      );
    }

    /* Full-screen only: keyboard steps the nested stage */
    on(vp, 'keydown', function (e) {
      if (!inWallFs()) return;
      var reduceMotion = false;
      try {
        reduceMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (eRm) {}
      var step = e.shiftKey ? 180 : 90;
      var y = 0;
      if (e.key === 'ArrowUp' || e.key === 'k') y = -step;
      else if (e.key === 'ArrowDown' || e.key === 'j') y = step;
      else if (e.key === 'PageDown' || e.key === ' ') y = Math.round(vp.clientHeight * 0.85);
      else if (e.key === 'PageUp') y = -Math.round(vp.clientHeight * 0.85);
      else if (e.key === 'Home') {
        e.preventDefault();
        vp.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        return;
      } else if (e.key === 'End') {
        e.preventDefault();
        vp.scrollTo({
          top: vp.scrollHeight,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
        return;
      } else return;
      e.preventDefault();
      vp.scrollBy({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' });
    });

    /* Expose for wall full-screen focus */
    vp._sgWallFocus = function () {
      if (inWallFs()) {
        try {
          vp.scrollTop = 0;
        } catch (e0) {}
        try {
          vp.focus({ preventScroll: true });
        } catch (err4) {
          try {
            vp.focus();
          } catch (err5) {}
        }
      } else {
        /* Page-flow: scroll the gallery into view on the document */
        try {
          (wall || vp).scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch (e1) {}
      }
      if (wall) {
        wall.classList.add('is-active');
        window.setTimeout(function () {
          wall.classList.remove('is-active');
        }, 900);
      }
    };
  });

  /* ── Horizontal drag rail (desktop drag; touch uses native scroll) ─
     Only starts drag after a small move so links inside still click. */
  document.querySelectorAll('[data-h-scroll]').forEach(function (rail) {
    rail.style.webkitOverflowScrolling = 'touch';
    rail.style.overflowX = 'auto';
    /* On touch devices, leave native swipe alone — pointer capture fights it */
    if (touch || document.body.classList.contains('is-touch')) {
      rail.style.cursor = 'auto';
      return;
    }
    var down = false;
    var dragging = false;
    var startX = 0;
    var scrollL = 0;
    var DRAG_THRESHOLD = 8;
    rail.style.cursor = 'grab';
    on(rail, 'pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      /* Only primary button */
      if (e.button != null && e.button !== 0) return;
      down = true;
      dragging = false;
      startX = e.clientX;
      scrollL = rail.scrollLeft;
    });
    on(rail, 'pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        rail.style.cursor = 'grabbing';
        rail.classList.add('is-dragging');
        try {
          rail.setPointerCapture && rail.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      rail.scrollLeft = scrollL - dx;
    });
    function endDrag(e) {
      if (!down) return;
      var wasDragging = dragging;
      down = false;
      dragging = false;
      rail.style.cursor = 'grab';
      rail.classList.remove('is-dragging');
      /* Suppress click that follows a real drag so we don't navigate mid-swipe */
      if (wasDragging) {
        rail.dataset.suppressClick = '1';
        setTimeout(function () {
          delete rail.dataset.suppressClick;
        }, 80);
      }
    }
    on(rail, 'pointerup', endDrag);
    on(rail, 'pointercancel', endDrag);
    on(rail, 'pointerleave', function () {
      if (dragging) endDrag();
      else down = false;
    });
    on(
      rail,
      'click',
      function (e) {
        if (rail.dataset.suppressClick === '1') {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  });

  /* ── Marquee (duplicate already in markup) ─────────────── */
  /* CSS animation handles continuous scroll */

  /* ── Process video scrubber ────────────────────────────── */
  document.querySelectorAll('[data-scrub]').forEach(function (root) {
    var video = root.querySelector('[data-scrub-video]');
    var range = root.querySelector('[data-scrub-range]');
    var pct = root.querySelector('[data-scrub-pct]');
    if (!video || !range) return;
    video.pause();
    video.muted = true;
    function seekFromRange() {
      if (!video.duration || isNaN(video.duration)) return;
      var t = (Number(range.value) / 1000) * video.duration;
      video.currentTime = t;
      if (pct) pct.textContent = Math.round((Number(range.value) / 10)) + '%';
    }
    on(video, 'loadedmetadata', function () {
      seekFromRange();
    });
    on(range, 'input', seekFromRange);
    on(range, 'change', seekFromRange);
    /* also drag on video surface */
    var dragging = false;
    on(video, 'pointerdown', function (e) {
      dragging = true;
      video.setPointerCapture && video.setPointerCapture(e.pointerId);
      scrubAt(e);
    });
    on(video, 'pointermove', function (e) {
      if (dragging) scrubAt(e);
    });
    on(video, 'pointerup', function () {
      dragging = false;
    });
    function scrubAt(e) {
      var r = video.getBoundingClientRect();
      var p = clamp((e.clientX - r.left) / r.width, 0, 1);
      range.value = String(Math.round(p * 1000));
      seekFromRange();
    }
  });

  /* ── Reels hover / tap play (home satellites + process grid) ── */
  document.querySelectorAll('[data-reel]').forEach(function (card) {
    var v = card.querySelector('video');
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('muted', '');
    function play() {
      // pause other reels so only one moves
      document.querySelectorAll('[data-reel].is-playing').forEach(function (other) {
        if (other === card) return;
        var ov = other.querySelector('video');
        if (ov) {
          ov.pause();
          try {
            ov.currentTime = 0;
          } catch (e) {}
        }
        other.classList.remove('is-playing');
      });
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
      card.classList.add('is-playing');
    }
    function pause() {
      v.pause();
      card.classList.remove('is-playing');
    }
    if (touch) {
      on(card, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (v.paused) play();
        else pause();
      });
    } else {
      on(card, 'pointerenter', play);
      on(card, 'pointerleave', pause);
      on(card, 'focusin', play);
      on(card, 'focusout', pause);
    }
  });

  /* ── Reels montage (home cinematic short-form) ─────────── */
  document.querySelectorAll('[data-reel-montage]').forEach(function (root) {
    var jsonEl = root.querySelector('[data-mont-playlist]');
    var vidA = root.querySelector('[data-mont-video]');
    var vidB = root.querySelector('[data-mont-video-b]');
    var labelEl = root.querySelector('[data-mont-label]');
    var countEl = root.querySelector('[data-mont-count]');
    var progressEl = root.querySelector('[data-mont-progress]');
    var dotsEl = root.querySelector('[data-mont-dots]');
    var soundBtn = root.querySelector('[data-mont-sound]');
    var soundLabel = root.querySelector('[data-mont-sound-label]');
    var peekPrevImg = root.querySelector('[data-mont-peek-img-prev]');
    var peekNextImg = root.querySelector('[data-mont-peek-img-next]');
    var stage = root.querySelector('[data-mont-stage]') || root;
    if (!jsonEl || !vidA) return;

    var playlist = [];
    try {
      playlist = JSON.parse(jsonEl.textContent || '[]') || [];
    } catch (eJ) {
      playlist = [];
    }
    playlist = playlist.filter(function (c) {
      return c && c.src && String(c.src).indexOf('//') >= 0;
    });
    if (!playlist.length) {
      root.hidden = true;
      return;
    }

    var i = 0;
    var activeSlot = 'a';
    var muted = true;
    var playing = false;
    var busy = false;
    var progressRaf = 0;
    var reduceMont = reduce;

    function activeVid() {
      return activeSlot === 'a' ? vidA : vidB;
    }
    function idleVid() {
      return activeSlot === 'a' ? vidB : vidA;
    }

    function paintDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      playlist.forEach(function (_, idx) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'reel-mont__dot' + (idx === i ? ' is-active' : '');
        b.setAttribute('aria-label', 'Go to reel ' + (idx + 1));
        on(b, 'click', function () {
          showClip(idx, true);
        });
        dotsEl.appendChild(b);
      });
    }

    function paintMeta() {
      var clip = playlist[i];
      if (!clip) return;
      if (labelEl) labelEl.textContent = clip.title || 'Studio reel';
      if (countEl) {
        countEl.textContent =
          String(i + 1).padStart(2, '0') + ' / ' + String(playlist.length).padStart(2, '0');
      }
      var prev = playlist[(i - 1 + playlist.length) % playlist.length];
      var next = playlist[(i + 1) % playlist.length];
      if (peekPrevImg && prev && prev.poster) peekPrevImg.src = prev.poster;
      if (peekNextImg && next && next.poster) peekNextImg.src = next.poster;
      paintDots();
    }

    function stopProgress() {
      if (progressRaf) cancelAnimationFrame(progressRaf);
      progressRaf = 0;
      if (progressEl) {
        progressEl.style.transition = 'none';
        progressEl.style.width = '0%';
      }
    }

    function tickProgress() {
      var v = activeVid();
      if (!v || !playing || !v.duration || isNaN(v.duration)) {
        progressRaf = requestAnimationFrame(tickProgress);
        return;
      }
      var pct = Math.max(0, Math.min(100, (v.currentTime / v.duration) * 100));
      if (progressEl) {
        progressEl.style.transition = 'none';
        progressEl.style.width = pct.toFixed(2) + '%';
      }
      progressRaf = requestAnimationFrame(tickProgress);
    }

    function applyMute() {
      [vidA, vidB].forEach(function (v) {
        if (!v) return;
        v.muted = muted;
        v.defaultMuted = muted;
        if (muted) v.setAttribute('muted', '');
        else v.removeAttribute('muted');
      });
      if (soundBtn) {
        soundBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
        soundBtn.setAttribute('aria-label', muted ? 'Unmute reels' : 'Mute reels');
      }
      if (soundLabel) soundLabel.textContent = muted ? 'Sound off' : 'Sound on';
      root.classList.toggle('is-unmuted', !muted);
    }

    function loadClip(el, clip) {
      return new Promise(function (resolve) {
        if (!el || !clip || !clip.src) {
          resolve();
          return;
        }
        var done = false;
        function finish() {
          if (done) return;
          done = true;
          resolve();
        }
        el.onloadeddata = finish;
        el.onerror = finish;
        if (clip.poster) el.setAttribute('poster', clip.poster);
        if (el.getAttribute('src') !== clip.src) {
          el.src = clip.src;
          try {
            el.load();
          } catch (eL) {}
        }
        el.muted = muted;
        el.loop = false;
        el.playsInline = true;
        el.setAttribute('playsinline', '');
        if (el.readyState >= 2) finish();
        else setTimeout(finish, 1800);
      });
    }

    function showClip(index, user) {
      if (!playlist.length || busy) return;
      busy = true;
      i = ((index % playlist.length) + playlist.length) % playlist.length;
      var clip = playlist[i];
      var out = activeVid();
      var inn = idleVid() || activeVid();
      paintMeta();
      stopProgress();

      loadClip(inn, clip).then(function () {
        try {
          inn.currentTime = 0;
        } catch (eC) {}
        if (out && out !== inn) {
          out.classList.remove('is-active');
          try {
            out.pause();
          } catch (eP) {}
        }
        if (inn) inn.classList.add('is-active');
        activeSlot = inn === vidB ? 'b' : 'a';
        busy = false;
        if (playing || user) {
          playing = true;
          var p = inn.play();
          if (p && p.catch) p.catch(function () {});
          tickProgress();
        }
      });
    }

    function next(dir) {
      showClip(i + (dir || 1), true);
    }

    function togglePlay() {
      var v = activeVid();
      if (!v) return;
      if (v.paused) {
        playing = true;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
        tickProgress();
      } else {
        playing = false;
        v.pause();
        stopProgress();
      }
    }

    [vidA, vidB].forEach(function (v) {
      if (!v) return;
      on(v, 'ended', function () {
        if (v !== activeVid()) return;
        showClip(i + 1, false);
      });
    });

    root.querySelectorAll('[data-mont-prev]').forEach(function (b) {
      on(b, 'click', function () {
        next(-1);
      });
    });
    root.querySelectorAll('[data-mont-next]').forEach(function (b) {
      on(b, 'click', function () {
        next(1);
      });
    });
    /* Desktop side peeks advance the montage */
    root.querySelectorAll('[data-mont-peek="prev"]').forEach(function (el) {
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-label', 'Previous reel');
      on(el, 'click', function () {
        next(-1);
      });
      on(el, 'keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          next(-1);
        }
      });
    });
    root.querySelectorAll('[data-mont-peek="next"]').forEach(function (el) {
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-label', 'Next reel');
      on(el, 'click', function () {
        next(1);
      });
      on(el, 'keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          next(1);
        }
      });
    });
    root.querySelectorAll('[data-mont-tap]').forEach(function (b) {
      on(b, 'click', function (e) {
        e.preventDefault();
        togglePlay();
      });
    });
    if (soundBtn) {
      on(soundBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        muted = !muted;
        applyMute();
        if (!muted && playing) {
          var v = activeVid();
          if (v) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          }
        }
      });
    }

    /* Swipe on phone frame */
    (function swipeNav() {
      var sx = 0;
      var sy = 0;
      var phone = root.querySelector('[data-mont-phone]') || stage;
      on(
        phone,
        'touchstart',
        function (e) {
          if (!e.touches || !e.touches[0]) return;
          sx = e.touches[0].clientX;
          sy = e.touches[0].clientY;
        },
        { passive: true }
      );
      on(
        phone,
        'touchend',
        function (e) {
          if (!e.changedTouches || !e.changedTouches[0]) return;
          var dx = e.changedTouches[0].clientX - sx;
          var dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
          if (dx < 0) next(1);
          else next(-1);
        },
        { passive: true }
      );
    })();

    /* Peek click → skip */
    root.querySelectorAll('[data-mont-peek]').forEach(function (peek) {
      on(peek, 'click', function () {
        var which = peek.getAttribute('data-mont-peek');
        if (which === 'prev') next(-1);
        else next(1);
      });
    });

    applyMute();
    showClip(0, false);

    /* Autoplay when in view */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting && en.intersectionRatio > 0.35) {
              playing = true;
              var v = activeVid();
              if (v) {
                var p = v.play();
                if (p && p.catch) p.catch(function () {});
                tickProgress();
              }
            } else {
              playing = false;
              [vidA, vidB].forEach(function (v) {
                if (v) {
                  try {
                    v.pause();
                  } catch (eP) {}
                }
              });
              stopProgress();
            }
          });
        },
        { threshold: [0, 0.35, 0.6] }
      );
      io.observe(root);
    } else if (!reduceMont) {
      playing = true;
      showClip(0, true);
    }
  });

  /* ── Archive · soft immersive game ─────────────────────── */
  document.querySelectorAll('[data-archive]').forEach(function (root) {
    var items = Array.prototype.slice.call(
      root.querySelectorAll('[data-arc-item], [data-gx-card]')
    );
    /* Dedupe if both attributes on same node */
    (function () {
      var seen = [];
      items = items.filter(function (it) {
        if (seen.indexOf(it) >= 0) return false;
        seen.push(it);
        return true;
      });
    })();
    var filters = Array.prototype.slice.call(root.querySelectorAll('[data-arc-filter]'));
    var lb = root.querySelector('[data-lb]');
    /* Still wire filters/grid even if lightbox markup is missing */
    if (!items.length) {
      console.warn('[gallery] Archive wall has no works — Random/Walkthrough CTAs limited');
      /* Don’t bail entirely — still expose stubs so capture handler can retry */
    }

    var STORAGE = 'sg_archive_v3';
    /* Frame prefs persist; picker lives only in walkthrough (centre bottom) */
    var FRAME_STORAGE = 'sg_archive_frames';
    function loadFramePrefs() {
      try {
        return JSON.parse(localStorage.getItem(FRAME_STORAGE) || '{}') || {};
      } catch (e) {
        return {};
      }
    }
    function saveFramePrefs(map) {
      try {
        localStorage.setItem(FRAME_STORAGE, JSON.stringify(map || {}));
      } catch (e2) {}
    }
    var framePrefs = loadFramePrefs();
    /* Expose for walkthrough module */
    root._sgFramePrefs = framePrefs;
    root._sgSaveFramePrefs = saveFramePrefs;
    /* Art-world ranks — climb the collector trail */
    var RANKS = [
      { min: 0, name: 'Colour Wanderer', next: 3, nextLabel: 'Open 3 works → Colour Seeker' },
      { min: 3, name: 'Colour Seeker', next: 10, nextLabel: 'Open 10 works → Wall Walker' },
      { min: 10, name: 'Wall Walker', next: 25, nextLabel: 'Open 25 works → Connoisseur' },
      { min: 25, name: 'Connoisseur', next: 50, nextLabel: 'Open 50 works → Gallery Kin' },
      { min: 50, name: 'Gallery Kin', next: 9999, nextLabel: 'Full trail — keep wandering' },
    ];
    /* Also rank by heat (XP) for depth after volume milestones */
    var HEAT_RANKS = [
      { min: 0, name: null },
      { min: 400, name: 'Warm palette' },
      { min: 900, name: 'Hot field' },
      { min: 1600, name: 'Studio fire' },
      { min: 2500, name: 'Atomic' },
    ];
    var state = {
      xp: 0,
      found: {},
      streak: 0,
      bestStreak: 0,
      started: false,
      clearFog: false,
      uiHidden: false,
      achievements: {},
    };

    var panX = 0;
    var panY = 0;
    var lingerTimer = null;
    var lingerAwarded = false;

    var boot = root.querySelector('[data-gx-boot]');
    /* HUD may already be docked into the site header (moved out of root) */
    var hud =
      root.querySelector('[data-gx-hud]') ||
      document.querySelector('[data-gx-complete-bar]') ||
      document.querySelector('[data-gx-hud]');
    var toast = root.querySelector('[data-gx-toast]');
    var complete = root.querySelector('[data-gx-complete]');
    var mapEl = root.querySelector('[data-gx-map]');
    /* Null-safe lightbox refs — missing lb must not kill walkthrough JS later */
    var img = lb ? lb.querySelector('[data-lb-img]') : null;
    var zoomWrap = lb ? lb.querySelector('[data-lb-zoom]') : null;
    var stage = lb ? lb.querySelector('[data-lb-stage]') : null;
    var num = lb ? lb.querySelector('[data-lb-num]') : null;
    var title = lb ? lb.querySelector('[data-lb-title]') : null;
    var place = lb ? lb.querySelector('[data-lb-place]') : null;
    var count = lb ? lb.querySelector('[data-lb-count]') : null;
    var film = lb ? lb.querySelector('[data-lb-film]') : null;
    var scoreEl = lb ? lb.querySelector('[data-lb-score]') : null;
    var lingerEl = lb ? lb.querySelector('[data-gx-linger]') : root.querySelector('[data-gx-linger]');

    function persist() {
      try {
        localStorage.setItem(
          STORAGE,
          JSON.stringify({
            xp: state.xp,
            found: state.found,
            bestStreak: state.bestStreak,
            achievements: state.achievements,
            started: state.started,
            clearFog: state.clearFog,
          })
        );
      } catch (e) {}
    }

    function foundCount() {
      return Object.keys(state.found).length;
    }

    function trailRank() {
      var found = foundCount();
      var r = RANKS[0];
      for (var i = 0; i < RANKS.length; i++) {
        if (found >= RANKS[i].min) r = RANKS[i];
      }
      return r;
    }

    function rankName() {
      var base = trailRank().name;
      var heat = null;
      for (var i = 0; i < HEAT_RANKS.length; i++) {
        if (state.xp >= HEAT_RANKS[i].min && HEAT_RANKS[i].name) heat = HEAT_RANKS[i].name;
      }
      return heat ? base + ' · ' + heat : base;
    }

    function nextMilestoneText() {
      var found = foundCount();
      var total = items.length || 1;
      if (found >= total) return 'Trail complete — every work opened';
      var r = trailRank();
      if (r.next < 9999 && found < r.next) {
        var left = r.next - found;
        return left + ' more · ' + (r.nextLabel || 'next rank');
      }
      var leftAll = total - found;
      return leftAll + ' left on the wall';
    }

    function showToast(msg, kind) {
      if (!toast) return;
      toast.hidden = false;
      toast.textContent = msg;
      toast.className = 'gx-toast is-on' + (kind ? ' gx-toast--' + kind : '');
      clearTimeout(showToast._t);
      showToast._t = setTimeout(function () {
        toast.classList.remove('is-on');
        setTimeout(function () {
          toast.hidden = true;
        }, 280);
      }, kind === 'find' ? 1600 : 2400);
    }

    function unlock(id, label) {
      if (state.achievements[id]) return;
      state.achievements[id] = true;
      showToast(label, 'achieve');
      persist();
    }

    function updateHUD() {
      var total = items.length;
      var found = foundCount();
      var pct = total ? Math.min(100, (found / total) * 100) : 0;
      var el;
      var rank = rankName();
      /* Prefer docked completion bar (may live under site-header) */
      var barRoots = [root];
      if (hud && hud !== root) barRoots.push(hud);
      var dockBar = document.querySelector('[data-gx-complete-bar]');
      if (dockBar) barRoots.push(dockBar);
      barRoots.forEach(function (scope) {
        if (!scope) return;
        if ((el = scope.querySelector('[data-gx-found]'))) el.textContent = String(found);
        if ((el = scope.querySelector('[data-gx-total]'))) el.textContent = String(total);
        if ((el = scope.querySelector('[data-gx-bar]'))) {
          el.style.width = pct.toFixed(1) + '%';
        }
        if ((el = scope.querySelector('[data-gx-pct]'))) el.textContent = String(Math.round(pct));
        if ((el = scope.querySelector('[data-gx-xp]'))) el.textContent = String(state.xp);
        if ((el = scope.querySelector('[data-gx-rank]'))) el.textContent = rank;
        if ((el = scope.querySelector('[data-gx-next]'))) el.textContent = nextMilestoneText();
        if ((el = scope.querySelector('[data-gx-streak]'))) el.textContent = String(state.streak);
        if ((el = scope.querySelector('[data-gx-streak-wrap]'))) {
          el.hidden = state.streak < 2;
        }
        if ((el = scope.querySelector('[data-gx-seen-word]'))) {
          el.textContent = found === 1 ? 'found' : 'found';
        }
        if ((el = scope.querySelector('[data-gx-meter]'))) {
          el.setAttribute('aria-valuenow', String(Math.round(pct)));
        }
        /* Pulse trail when almost complete */
        var trail = scope.classList && scope.classList.contains('gx-trail') ? scope : scope.querySelector('.gx-trail');
        if (!trail && scope.matches && scope.matches('.gx-trail, [data-gx-complete-bar]')) trail = scope;
        if (trail && trail.classList) {
          trail.classList.toggle('is-hot', pct >= 40);
          trail.classList.toggle('is-blazing', pct >= 80);
          trail.classList.toggle('is-complete', found >= total && total > 0);
        }
      });
      if (hud) {
        hud.setAttribute('aria-valuenow', String(found));
        hud.setAttribute('aria-valuemax', String(total));
      }
      buildMap();
    }

    function buildMap() {
      if (!mapEl) return;
      mapEl.innerHTML = '';
      var maxDots = Math.min(items.length, 64);
      for (var i = 0; i < maxDots; i++) {
        var it = items[i];
        var d = document.createElement('span');
        var id = it.getAttribute('data-id');
        d.className = 'gx-map__dot';
        if (state.found[id]) d.classList.add('is-found');
        if (it.classList.contains('is-hidden')) d.classList.add('is-off');
        mapEl.appendChild(d);
      }
    }

    function applyFog() {
      items.forEach(function (it) {
        var id = it.getAttribute('data-id');
        var found = !!state.found[id] || state.clearFog;
        it.classList.toggle('is-fogged', !found);
        it.classList.toggle('is-found', found);
        var badge = it.querySelector('[data-gx-badge]');
        if (badge) badge.hidden = !state.found[id];
        it.setAttribute('data-cursor', 'View');
      });
    }

    function addXP(n) {
      var mult = Math.max(1, Math.min(state.streak, 12));
      var gain = Math.round(n * (1 + (mult - 1) * 0.12));
      state.xp += gain;
      updateHUD();
      persist();
      return gain;
    }

    function discover(it) {
      var id = it.getAttribute('data-id');
      var first = !state.found[id];
      var title = it.getAttribute('data-title') || 'Work';
      if (first) {
        var prevRank = trailRank().name;
        state.found[id] = { t: Date.now(), title: title };
        state.streak += 1;
        if (state.streak > state.bestStreak) state.bestStreak = state.streak;
        var base = parseInt(it.getAttribute('data-xp') || '80', 10);
        var gain = addXP(base);
        applyFog();
        updateHUD();
        showToast('Work found · +' + gain + ' heat', 'find');
        if (foundCount() === 1) unlock('first', 'Trail starts — first work found');
        if (foundCount() === 3) unlock('three', 'Colour Seeker unlocked');
        if (foundCount() >= 10) unlock('ten', 'Wall Walker — 10 works');
        if (foundCount() >= 25) unlock('twentyfive', 'Connoisseur — 25 works');
        if (state.streak === 3) unlock('streak3', 'Streak ×3 — keep going');
        if (state.streak >= 5) unlock('streak5', 'Streak ×5 — on fire');
        if (state.streak >= 10) unlock('streak10', 'Streak ×10 — unstoppable');
        if (trailRank().name !== prevRank) {
          unlock('rank_' + trailRank().name, 'Rank up · ' + trailRank().name);
        }
        if (foundCount() >= items.length) {
          unlock('all', 'Full Walkthrough — trail complete');
          if (complete) {
            complete.hidden = false;
            var cx = complete.querySelector('[data-gx-complete-xp]');
            if (cx) cx.textContent = state.xp + ' heat · ' + rankName();
          }
        }
        /* Flash the trail HUD */
        if (hud) {
          hud.classList.add('is-pulse');
          setTimeout(function () {
            hud.classList.remove('is-pulse');
          }, 700);
        }
      }
      return first;
    }

    function applyFilter(tag) {
      visible = [];
      items.forEach(function (it) {
        var t = (it.getAttribute('data-tag') || 'all').toLowerCase();
        var show = tag === 'all' || t === tag;
        it.classList.toggle('is-hidden', !show);
        if (show) visible.push(it);
      });
      filters.forEach(function (f) {
        f.classList.toggle('is-active', f.getAttribute('data-arc-filter') === tag);
      });
      buildFilm();
      buildMap();
    }
    filters.forEach(function (f) {
      on(f, 'click', function () {
        var tag = f.getAttribute('data-arc-filter') || 'all';
        applyFilter(tag);
        Sound.category(tag);
      });
    });

    function buildFilm() {
      if (!film) return;
      film.innerHTML = '';
      visible.forEach(function (it, i) {
        var b = document.createElement('button');
        b.type = 'button';
        var id = it.getAttribute('data-id');
        var known = state.found[id] || state.clearFog;
        b.className = 'lb__thumb' + (i === index ? ' is-active' : '') + (known ? ' is-found' : ' is-fog');
        if (known) {
          b.innerHTML =
            '<img src="' +
            (it.getAttribute('data-thumb') || it.getAttribute('data-src')) +
            '" alt="" loading="lazy">';
        } else {
          b.innerHTML = '<span class="lb__fog-thumb">' + (it.getAttribute('data-num') || '') + '</span>';
        }
        on(b, 'click', function () {
          Sound.artwork();
          openAt(i);
        });
        film.appendChild(b);
      });
    }

    function setZoomTransform() {
      if (!zoomWrap) return;
      zoomWrap.style.transform =
        'translate3d(' + panX + 'px,' + panY + 'px,0) scale(' + zoom + ')';
    }
    function resetZoom() {
      zoom = 1;
      panX = 0;
      panY = 0;
      setZoomTransform();
    }

    function paint() {
      var it = visible[index];
      if (!it) return;
      resetZoom();
      lingerAwarded = false;
      if (lingerEl) lingerEl.hidden = true;
      clearTimeout(lingerTimer);
      if (img) {
        img.src = it.getAttribute('data-src');
        img.alt = it.getAttribute('data-title') || '';
        img.classList.remove('is-swapped');
        void img.offsetWidth;
        img.classList.add('is-swapped');
      }
      if (num) num.textContent = it.getAttribute('data-num') || '';
      if (title) title.textContent = it.getAttribute('data-title') || '';
      if (place) place.textContent = it.getAttribute('data-place') || '';
      var lbq = lb ? lb.querySelector('[data-lb-quote]') : null;
      if (lbq) lbq.textContent = it.getAttribute('data-quote') || '';
      if (count)
        count.textContent =
          String(index + 1).padStart(3, '0') + ' / ' + String(visible.length).padStart(3, '0');
      if (scoreEl) scoreEl.textContent = rankName() + ' · ' + state.xp + ' XP';
      if (film) {
        Array.prototype.forEach.call(film.children, function (t, i) {
          t.classList.toggle('is-active', i === index);
        });
      }
      lingerTimer = setTimeout(function () {
        if (!lb || lb.hidden || lingerAwarded) return;
        lingerAwarded = true;
        var g = addXP(20);
        if (lingerEl) {
          lingerEl.hidden = false;
          lingerEl.textContent = '+ ' + g + ' calm';
        }
        unlock('linger', 'Patient eye');
      }, 5000);
    }

    function openAt(i) {
      if (!lb) return;
      index = Math.max(0, Math.min(visible.length - 1, i));
      var it = visible[index];
      if (!it) return;
      var id = it.getAttribute('data-id');
      try {
        discover(it);
      } catch (e) {}
      lb.hidden = false;
      lb.removeAttribute('hidden');
      document.body.classList.add('lb-open');
      root.classList.add('is-viewing');
      try {
        buildFilm();
        paint();
      } catch (e2) {}
    }

    function close() {
      if (!lb) return;
      lb.hidden = true;
      document.body.classList.remove('lb-open');
      root.classList.remove('is-viewing');
      clearTimeout(lingerTimer);
      resetZoom();
    }

    function next(d) {
      if (!visible.length) return;
      index = (index + d + visible.length) % visible.length;
      var it = visible[index];
      if (it) discover(it);
      paint();
      buildFilm();
    }

    function startGame(opts) {
      opts = opts || {};
      state.started = true;
      if (opts.clearFog) state.clearFog = true;
      if (boot) {
        boot.classList.add('is-done');
        setTimeout(function () {
          boot.hidden = true;
        }, 400);
      }
      if (hud) hud.hidden = false;
      root.classList.add('is-live');
      applyFog();
      updateHUD();
      persist();
    }

    root.querySelectorAll('[data-gx-start]').forEach(function (b) {
      on(b, 'click', function () {
        startGame({});
      });
    });
    root.querySelectorAll('[data-gx-start-clear]').forEach(function (b) {
      on(b, 'click', function () {
        startGame({ clearFog: true });
        var wall = root.querySelector('#gallery-wall') || root.querySelector('[data-gx-grid]');
        if (wall) wall.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    /* Full screen wall — tools above, JPEGs fill the viewport */
    var fsToolsToggle = root.querySelector('[data-gx-fs-tools-toggle]');
    var fsToolsPanel = root.querySelector('[data-gx-fs-tools-panel]');
    var fsToolsLabel = root.querySelector('[data-gx-fs-tools-toggle-label]');
    var fsToolsCollapsed = false;

    function measureWallFsTools() {
      var tools = root.querySelector('[data-gx-sticky-tools], .gx-page-tools');
      if (!tools) return;
      var h = Math.ceil(tools.getBoundingClientRect().height) || 0;
      root.style.setProperty('--wall-fs-tools-h', Math.max(h, 48) + 'px');
    }

    function setFsToolsCollapsed(collapsed) {
      fsToolsCollapsed = !!collapsed;
      root.classList.toggle('is-fs-tools-collapsed', fsToolsCollapsed);
      if (fsToolsToggle) {
        fsToolsToggle.setAttribute('aria-expanded', fsToolsCollapsed ? 'false' : 'true');
        fsToolsToggle.setAttribute(
          'title',
          fsToolsCollapsed ? 'Show selection tools' : 'Hide selection tools'
        );
      }
      if (fsToolsLabel) {
        fsToolsLabel.textContent = fsToolsCollapsed ? 'Show' : 'Hide';
      }
      if (fsToolsPanel) {
        if (fsToolsCollapsed) {
          fsToolsPanel.setAttribute('hidden', '');
          fsToolsPanel.hidden = true;
        } else {
          fsToolsPanel.removeAttribute('hidden');
          fsToolsPanel.hidden = false;
        }
      }
      requestAnimationFrame(measureWallFsTools);
    }

    function setWallFullscreen(on) {
      var active = !!on;
      root.classList.toggle('is-wall-fs', active);
      root.classList.toggle('is-live', true);
      document.body.classList.toggle('wall-fs-open', active);
      root.querySelectorAll('[data-gx-browse], [data-gx-wall-fs]').forEach(function (btn) {
        btn.classList.toggle('is-on', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (btn.tagName === 'BUTTON' || btn.getAttribute('role') === 'button') {
          btn.textContent = active ? 'Exit full screen' : 'Full screen';
        }
      });
      if (fsToolsToggle) {
        if (active) {
          fsToolsToggle.hidden = false;
          fsToolsToggle.removeAttribute('hidden');
        } else {
          fsToolsToggle.hidden = true;
          fsToolsToggle.setAttribute('hidden', '');
        }
      }
      if (active) {
        startGame({ clearFog: true });
        root.classList.remove('is-ui-hidden');
        /* Start expanded so tools are available */
        setFsToolsCollapsed(false);
        requestAnimationFrame(function () {
          measureWallFsTools();
          var vp =
            root.querySelector('[data-gx-wall-viewport]') ||
            root.querySelector('#gallery-wall');
          if (vp) {
            try {
              vp.scrollTop = 0;
              vp.scrollLeft = 0;
            } catch (errFs) {}
            if (typeof vp._sgWallFocus === 'function') {
              vp._sgWallFocus();
            } else {
              try {
                vp.focus({ preventScroll: true });
              } catch (errF2) {}
            }
          }
          /* Tools height may change after banner open */
          window.setTimeout(measureWallFsTools, 80);
        });
      } else {
        setFsToolsCollapsed(false);
        root.style.removeProperty('--wall-fs-tools-h');
      }
    }

    root._sgSetWallFullscreen = setWallFullscreen;
    root._sgToggleWallFullscreen = function () {
      setWallFullscreen(!root.classList.contains('is-wall-fs'));
    };

    root.querySelectorAll('[data-gx-browse], [data-gx-wall-fs]').forEach(function (b) {
      on(b, 'click', function (e) {
        e.preventDefault();
        setWallFullscreen(!root.classList.contains('is-wall-fs'));
      });
    });

    if (fsToolsToggle) {
      on(fsToolsToggle, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!root.classList.contains('is-wall-fs')) return;
        setFsToolsCollapsed(!fsToolsCollapsed);
      });
    }

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-wall-fs')) {
        /* Don’t steal Escape from open walkthrough / lightbox */
        if (document.body.classList.contains('walk-open')) return;
        if (document.body.classList.contains('lb-open')) return;
        /* If tools are hidden, first Escape reveals them; second exits FS */
        if (fsToolsCollapsed) {
          setFsToolsCollapsed(false);
          return;
        }
        setWallFullscreen(false);
      }
    });
    /* True when walkthrough open() is the real API (not the dead stub) */
    function walkApiReady() {
      if (!root._sgWalk || typeof root._sgWalk.open !== 'function') return false;
      if (root._sgWalk.ready === true) return true;
      /* ready flag lagging — still accept a non-stub open */
      try {
        var stub = root._sgWalkOpenStub;
        if (stub && root._sgWalk.open === stub) return false;
        /* Real openWalk takes (start, opts) — stub takes none usefully */
        return root._sgWalk.open.name === 'openWalk' || root._sgWalk.open.length >= 1;
      } catch (eR) {
        return false;
      }
    }

    /* Open walkthrough on the frame that matches a wall item */
    function openWalkForItem(it, opts, attempt) {
      opts = opts || {};
      attempt = attempt || 0;
      if (!it) return false;
      /* Clear stuck drag locks that zero-out pointer-events on cards */
      try {
        var vpLock = it.closest('[data-gx-wall-viewport]');
        if (vpLock) {
          vpLock.classList.remove('is-dragging', 'is-panning', 'is-pan-lock', 'is-coasting');
        }
      } catch (eL) {}
      var merged = { skipIntro: true, pinItem: true };
      if (opts) {
        for (var k in opts) {
          if (Object.prototype.hasOwnProperty.call(opts, k)) merged[k] = opts[k];
        }
      }
      /* Pass the DOM node + identity keys so walk always lands on THIS painting */
      merged.item = it;
      merged.id = it.getAttribute('data-id') || '';
      merged.slug = it.getAttribute('data-slug') || '';
      merged.src = (it.getAttribute('data-src') || '').trim();

      /* Ignore the early stub (ready:false) — wait for real openWalk */
      if (!walkApiReady()) {
        if (attempt < 60) {
          setTimeout(function () {
            openWalkForItem(it, opts, attempt + 1);
          }, 30);
        } else {
          try {
            if (typeof window.sgOpenWalk === 'function') {
              window.sgOpenWalk(it, merged);
              return true;
            }
          } catch (eWin) {}
          try {
            root.dispatchEvent(
              new CustomEvent('sg:open-walkthrough', {
                bubbles: true,
                detail: {
                  item: it,
                  id: merged.id,
                  slug: merged.slug,
                  src: merged.src,
                  skipIntro: true,
                  pinItem: true,
                },
              })
            );
          } catch (eEv) {}
        }
        return false;
      }
      var openFn = root._sgWalk.open;
      try {
        var ok = openFn(it, merged);
        if (ok === false && attempt < 10) {
          setTimeout(function () {
            openWalkForItem(it, opts, attempt + 1);
          }, 50);
          return false;
        }
      } catch (eOpen) {
        try {
          openFn(merged.item || it, merged);
        } catch (eOpen2) {
          console.warn('[gallery] openWalk failed', eOpen2);
          if (attempt < 20) {
            setTimeout(function () {
              openWalkForItem(it, opts, attempt + 1);
            }, 50);
          }
          return false;
        }
      }
      setTimeout(function () {
        try {
          discover(it);
        } catch (e0) {}
      }, 120);
      return true;
    }

    function collectRandomPool() {
      var pool = Array.prototype.slice
        .call(root.querySelectorAll('[data-arc-item], [data-gx-card]'))
        .filter(function (it) {
          if (!it || it.classList.contains('is-hidden')) return false;
          var src = (it.getAttribute('data-src') || '').trim();
          if (src) return true;
          var img = it.querySelector('img');
          return !!(img && (img.getAttribute('src') || img.currentSrc || img.src));
        });
      if (!pool.length && items && items.length) {
        pool = items.filter(function (it) {
          return it && !it.classList.contains('is-hidden');
        });
      }
      return pool;
    }

    function openRandomPiece(attempt) {
      attempt = attempt || 0;
      try {
        startGame({ clearFog: true });
      } catch (eSg) {}
      try {
        applyFilter('all');
      } catch (eF0) {}

      var pool = collectRandomPool();
      if (!pool.length) {
        if (attempt < 30) {
          setTimeout(function () {
            openRandomPiece(attempt + 1);
          }, 50);
          return false;
        }
        console.warn('[gallery] Random piece: no works on the wall');
        return false;
      }
      var pick = pool[Math.floor(Math.random() * pool.length)];
      if (!pick) return false;
      visible = pool;

      /* Prefer the live walkthrough API (same process as clicking a card) */
      if (walkApiReady()) {
        try {
          if (
            root._sgWalk.openRandom &&
            typeof root._sgWalk.openRandom === 'function'
          ) {
            var viaApi = root._sgWalk.openRandom();
            if (viaApi !== false) {
              setTimeout(function () {
                try {
                  discover(pick);
                } catch (eD0) {}
              }, 120);
              return true;
            }
          }
          var opened = root._sgWalk.open(pick, {
            skipIntro: true,
            pinItem: true,
            item: pick,
            id: pick.getAttribute('data-id') || '',
            slug: pick.getAttribute('data-slug') || '',
            src: (pick.getAttribute('data-src') || '').trim(),
          });
          if (opened !== false) {
            setTimeout(function () {
              try {
                discover(pick);
              } catch (eD) {}
            }, 120);
            return true;
          }
        } catch (eOpen) {
          console.warn('[gallery] Random open failed', eOpen);
        }
      }

      /* Walk not ready yet — retry, then fall back to openWalkForItem */
      if (attempt < 50) {
        setTimeout(function () {
          openRandomPiece(attempt + 1);
        }, 40);
        return false;
      }
      return openWalkForItem(pick, { skipIntro: true, pinItem: true });
    }
    function openWalkthroughFromStart(attempt) {
      attempt = attempt || 0;
      try {
        startGame({ clearFog: true });
      } catch (eSg2) {}
      /* Ensure full catalogue is walkable */
      try {
        applyFilter('all');
      } catch (eF) {}
      if (walkApiReady()) {
        try {
          var ok = root._sgWalk.open(0, { skipIntro: true });
          if (ok !== false) return true;
        } catch (eW) {}
      }
      if (attempt < 50) {
        setTimeout(function () {
          openWalkthroughFromStart(attempt + 1);
        }, 40);
        return false;
      }
      /* Fallback: open first visible card (retries until walk is ready) */
      var first =
        items.filter(function (it) {
          return !it.classList.contains('is-hidden');
        })[0] ||
        root.querySelector('[data-arc-item], [data-gx-card]') ||
        items[0];
      if (first) return openWalkForItem(first, { skipIntro: true, pinItem: true });
      return false;
    }
    root.querySelectorAll('[data-gx-random], #gx-random-btn').forEach(function (b) {
      on(b, 'click', function (e) {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        openRandomPiece();
      });
    });
    root.querySelectorAll('[data-gx-walkthrough], #gx-walkthrough-btn').forEach(function (b) {
      on(b, 'click', function (e) {
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopPropagation) e.stopPropagation();
        openWalkthroughFromStart();
      });
    });
    root._sgOpenRandom = openRandomPiece;
    root._sgOpenWalkthrough = openWalkthroughFromStart;
    root._sgOpenWalkForItem = openWalkForItem;
    var completeClose = root.querySelector('[data-gx-complete-close]');
    if (completeClose) {
      on(completeClose, 'click', function () {
        if (complete) complete.hidden = true;
      });
    }
    /* Walkthrough discoveries count on the trail */
    root.addEventListener('sg:walk-seen', function (ev) {
      var id = ev && ev.detail && ev.detail.id;
      if (!id) return;
      var it = items.filter(function (el) {
        return el.getAttribute('data-id') === id;
      })[0];
      if (it) discover(it);
    });

    // Immersive gallery: always open the wall — no gate, no fog
    var autoClear = root.hasAttribute('data-gx-auto-clear') || root.classList.contains('gx--immersive');
    if (boot) {
      boot.hidden = true;
      boot.setAttribute('hidden', '');
    }
    // Always start clear on this gallery so buttons feel alive immediately
    state.started = true;
    state.clearFog = true;
    if (hud) hud.hidden = false;
    root.classList.add('is-live');
    root.classList.remove('is-preview');
    items.forEach(function (it) {
      it.classList.remove('is-fogged');
      var fog = it.querySelector('.gx-card__fog');
      if (fog) fog.hidden = true;
    });
    /* Hide wall cards whose Shopify Files 404 (white box + alt text).
       Root cause is missing Content → Files uploads — re-upload Web JPEGs. */
    (function hideBrokenArchiveThumbs() {
      function dropBroken(it, img) {
        if (!it || it.getAttribute('data-img-broken') === '1') return;
        it.setAttribute('data-img-broken', '1');
        it.classList.add('is-img-broken', 'is-hidden');
        it.setAttribute('hidden', '');
        it.style.display = 'none';
        try {
          if (img) img.removeAttribute('src');
        } catch (eRm) {}
        try {
          var id = it.getAttribute('data-id') || it.getAttribute('data-slug') || '';
          console.warn('[gallery] archive image missing (upload to Content → Files):', it.getAttribute('data-title') || id);
        } catch (eLog) {}
      }
      items.forEach(function (it) {
        var img = it.querySelector('img');
        if (!img) return;
        if (img.complete && img.naturalWidth === 0 && (img.currentSrc || img.src)) {
          dropBroken(it, img);
          return;
        }
        on(img, 'error', function () {
          dropBroken(it, img);
        });
      });
    })();
    persist();
    applyFog();
    updateHUD();

    function workKeyFromUrl() {
      try {
        var q = new URLSearchParams(location.search || '').get('work');
        if (q) return String(q).toLowerCase().replace(/^sig-/, '').trim();
        var h = (location.hash || '').replace(/^#/, '');
        if (h.indexOf('work-') === 0) return h.slice(5).toLowerCase();
        if (h.indexOf('work=') === 0) return h.slice(5).toLowerCase();
      } catch (e) {}
      return '';
    }

    function findWorkIndex(key) {
      if (!key) return -1;
      var k = String(key).toLowerCase();
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var slug = (it.getAttribute('data-slug') || '').toLowerCase();
        var id = (it.getAttribute('data-id') || '').toLowerCase();
        var title = (it.getAttribute('data-title') || '').toLowerCase();
        var num = (it.getAttribute('data-num') || '').toLowerCase();
        if (
          slug === k ||
          id === k ||
          id === 'sig-' + k ||
          num === k ||
          title === k ||
          title.indexOf(k.replace(/-/g, ' ')) >= 0
        ) {
          return i;
        }
      }
      return -1;
    }

    function openWorkFromUrl() {
      var key = workKeyFromUrl();
      if (!key) return false;
      var fi = findWorkIndex(key);
      if (fi < 0) return false;
      startGame({ clearFog: true });
      var it = items[fi];
      if (!it) return false;
      applyFilter('all');
      visible = items.filter(function (el) {
        return !el.classList.contains('is-hidden');
      });
      if (!visible.length) visible = items.slice();
      // Brief beat so grid paints, then open walkthrough on that frame
      setTimeout(function () {
        try {
          it.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
        } catch (e) {}
        openWalkForItem(it, { skipIntro: true });
      }, reduce ? 50 : 420);
      return true;
    }

    /* ── Expand / collapse: glide image to fill screen ── */
    var expandEl = null;
    var expandScrim = null;
    var expandFly = null;
    var expandImg = null;
    var expandBusy = false;

    function ensureExpandUI() {
      if (expandScrim) return;
      expandScrim = document.createElement('div');
      expandScrim.className = 'gx-expand-scrim';
      expandScrim.setAttribute('aria-hidden', 'true');
      expandFly = document.createElement('button');
      expandFly.type = 'button';
      expandFly.className = 'gx-expand-fly';
      expandFly.setAttribute('aria-label', 'Close expanded artwork');
      expandImg = document.createElement('img');
      expandImg.alt = '';
      expandFly.appendChild(expandImg);
      document.body.appendChild(expandScrim);
      document.body.appendChild(expandFly);
      on(expandScrim, 'click', collapseExpand);
      on(expandFly, 'click', collapseExpand);
      on(document, 'keydown', function (ev) {
        if (ev.key === 'Escape' && expandEl) collapseExpand();
      });
    }

    var EXPAND_EASE = reduce
      ? 'none'
      : 'top 0.48s cubic-bezier(0.22, 1, 0.36, 1), left 0.48s cubic-bezier(0.22, 1, 0.36, 1), width 0.48s cubic-bezier(0.22, 1, 0.36, 1), height 0.48s cubic-bezier(0.22, 1, 0.36, 1), border-radius 0.48s ease';

    function collapseExpand() {
      if (!expandEl || !expandFly || expandBusy) return;
      expandBusy = true;
      var it = expandEl;
      var rect = it.getBoundingClientRect();
      var radius = getComputedStyle(it).borderRadius || '14px';
      expandScrim.classList.remove('is-on');
      expandFly.classList.remove('is-open');
      expandFly.style.transition = EXPAND_EASE;
      expandFly.style.top = rect.top + 'px';
      expandFly.style.left = rect.left + 'px';
      expandFly.style.width = Math.max(1, rect.width) + 'px';
      expandFly.style.height = Math.max(1, rect.height) + 'px';
      expandFly.style.transform = 'none';
      expandFly.style.borderRadius = radius;
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        expandFly.classList.remove('is-visible');
        expandScrim.classList.remove('is-visible');
        expandFly.style.transition = 'none';
        it.classList.remove('is-expand-source');
        document.body.classList.remove('gx-expand-open');
        expandEl = null;
        expandBusy = false;
      }
      function te(ev) {
        if (ev.propertyName && ev.propertyName !== 'top' && ev.propertyName !== 'width' && ev.propertyName !== 'height' && ev.propertyName !== 'left') return;
        expandFly.removeEventListener('transitionend', te);
        finish();
      }
      expandFly.addEventListener('transitionend', te);
      setTimeout(finish, reduce ? 40 : 520);
    }

    function sizeExpanded(rectFallback) {
      var maxW = Math.min(window.innerWidth * 0.94, 1100);
      var maxH = Math.min(window.innerHeight * 0.92, 1400);
      var natW = expandImg.naturalWidth || (rectFallback && rectFallback.width) || 800;
      var natH = expandImg.naturalHeight || (rectFallback && rectFallback.height) || 1000;
      var scale = Math.min(maxW / natW, maxH / natH);
      var endW = Math.max(180, natW * scale);
      var endH = Math.max(200, natH * scale);
      expandFly.style.top = Math.max(8, (window.innerHeight - endH) / 2) + 'px';
      expandFly.style.left = Math.max(8, (window.innerWidth - endW) / 2) + 'px';
      expandFly.style.width = endW + 'px';
      expandFly.style.height = endH + 'px';
      expandFly.style.borderRadius = '6px';
      expandFly.style.transform = 'none';
    }

    function expandItem(it) {
      if (!it || expandBusy) return;
      ensureExpandUI();
      /* Click expanded image / same card again → collapse */
      if (expandEl === it) {
        collapseExpand();
        return;
      }
      if (expandEl) {
        expandEl.classList.remove('is-expand-source');
        expandEl = null;
        expandScrim.classList.remove('is-on');
        expandFly.classList.remove('is-open', 'is-visible');
      }
      if (!state.started) startGame({ clearFog: true });
      var img = it.querySelector('img');
      var src = it.getAttribute('data-src') || (img && img.currentSrc) || (img && img.src) || '';
      if (!src) return;
      try {
        discover(it);
      } catch (e) {}
      var rect = it.getBoundingClientRect();
      var radius = getComputedStyle(it).borderRadius || '14px';
      expandImg.alt = it.getAttribute('data-title') || '';
      if (expandImg.getAttribute('src') !== src) expandImg.src = src;
      expandFly.style.transition = 'none';
      expandFly.style.top = rect.top + 'px';
      expandFly.style.left = rect.left + 'px';
      expandFly.style.width = Math.max(1, rect.width) + 'px';
      expandFly.style.height = Math.max(1, rect.height) + 'px';
      expandFly.style.transform = 'none';
      expandFly.style.borderRadius = radius;
      /* Force reflow so the start rect is committed before animating */
      void expandFly.offsetWidth;
      expandScrim.classList.add('is-visible');
      expandFly.classList.add('is-visible');
      it.classList.add('is-expand-source');
      document.body.classList.add('gx-expand-open');
      expandEl = it;
      try {
        Sound.artwork();
      } catch (e) {}
      function glideOpen() {
        if (expandEl !== it) return;
        expandFly.style.transition = EXPAND_EASE;
        expandScrim.classList.add('is-on');
        expandFly.classList.add('is-open');
        sizeExpanded(rect);
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (expandEl !== it) return;
          if (expandImg.complete && expandImg.naturalWidth) {
            glideOpen();
          } else {
            var once = function () {
              expandImg.removeEventListener('load', once);
              glideOpen();
            };
            expandImg.addEventListener('load', once);
            /* Fallback if load already fired or fails */
            setTimeout(function () {
              expandImg.removeEventListener('load', once);
              if (expandEl === it && !expandFly.classList.contains('is-open')) glideOpen();
            }, 280);
          }
        });
      });
    }

    items.forEach(function (it) {
      on(it, 'click', function (e) {
        /* Ignore clicks that were really a pan on the wall (non page-flow only) */
        var vp = it.closest('[data-gx-wall-viewport]');
        if (vp) {
          var dragging =
            vp.classList.contains('is-dragging') || vp.classList.contains('is-panning');
          vp.classList.remove('is-dragging', 'is-panning', 'is-pan-lock', 'is-coasting');
          if (dragging && !vp.classList.contains('is-page-flow')) {
            if (e) {
              e.preventDefault();
              e.stopPropagation();
            }
            return;
          }
        }
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        if (!state.started) startGame({ clearFog: true });
        /* Ensure the work is in the walkable set (filters off for open) */
        if (it.classList.contains('is-hidden')) {
          applyFilter('all');
        }
        var vi = visible.indexOf(it);
        if (vi < 0) {
          applyFilter('all');
          vi = visible.indexOf(it);
        }
        /* Always open THIS painting in the walkthrough */
        openWalkForItem(it, { skipIntro: true, pinItem: true });
      });
    });

    // Home carousel / featured rail → open exact work in walkthrough
    (function () {
      var key = workKeyFromUrl();
      if (!key) return;
      var fi = findWorkIndex(key);
      if (fi < 0) return;
      startGame({ clearFog: true });
      var it = items[fi];
      if (!it) return;
      applyFilter('all');
      setTimeout(function () {
        try {
          it.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
        } catch (err) {}
        openWalkForItem(it, { skipIntro: true });
      }, reduce ? 50 : 420);
    })();

    if (lb) {
    lb.querySelectorAll('[data-lb-close]').forEach(function (b) {
      on(b, 'click', close);
    });
    var prev = lb.querySelector('[data-lb-prev]');
    var nextBtn = lb.querySelector('[data-lb-next]');
    if (prev) on(prev, 'click', function () { next(-1); });
    if (nextBtn) on(nextBtn, 'click', function () { next(1); });

    var zin = lb.querySelector('[data-lb-zoom-in]');
    var zout = lb.querySelector('[data-lb-zoom-out]');
    var zres = lb.querySelector('[data-lb-zoom-reset]');
    if (zin)
      on(zin, 'click', function () {
        zoom = clamp(zoom + 0.35, 1, 4);
        setZoomTransform();
      });
    if (zout)
      on(zout, 'click', function () {
        zoom = clamp(zoom - 0.35, 1, 4);
        if (zoom === 1) {
          panX = 0;
          panY = 0;
        }
        setZoomTransform();
      });
    if (zres) on(zres, 'click', resetZoom);

    if (stage && zoomWrap) {
      var dragging = false;
      var lx = 0;
      var ly = 0;
      on(
        stage,
        'wheel',
        function (e) {
          if (!lb || lb.hidden) return;
          e.preventDefault();
          zoom = clamp(zoom + (e.deltaY > 0 ? -0.12 : 0.12), 1, 4);
          if (zoom === 1) {
            panX = 0;
            panY = 0;
          }
          setZoomTransform();
        },
        { passive: false }
      );
      on(stage, 'pointerdown', function (e) {
        if (zoom <= 1) return;
        dragging = true;
        lx = e.clientX;
        ly = e.clientY;
        stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
      });
      on(stage, 'pointermove', function (e) {
        if (!dragging) return;
        panX += e.clientX - lx;
        panY += e.clientY - ly;
        lx = e.clientX;
        ly = e.clientY;
        setZoomTransform();
      });
      on(stage, 'pointerup', function () {
        dragging = false;
      });
    }

    var sx = 0;
    on(lb, 'touchstart', function (e) {
      sx = e.changedTouches[0].clientX;
    }, { passive: true });
    on(lb, 'touchend', function (e) {
      if (zoom > 1.05) return;
      var dx = e.changedTouches[0].clientX - sx;
      if (Math.abs(dx) > 50) next(dx < 0 ? 1 : -1);
    }, { passive: true });
    } /* end if (lb) */

    function toggleUI() {
      state.uiHidden = !state.uiHidden;
      root.classList.toggle('is-ui-hidden', state.uiHidden);
    }
    var uiBtn = root.querySelector('[data-gx-ui-toggle]');
    if (uiBtn) on(uiBtn, 'click', toggleUI);

    var fsBtn = root.querySelector('[data-gx-fullscreen]');
    if (fsBtn)
      on(fsBtn, 'click', function () {
        if (!document.fullscreenElement) {
          (root.requestFullscreen || root.webkitRequestFullscreen || function () {}).call(root);
          root.classList.add('is-fs');
        } else {
          (document.exitFullscreen || document.webkitExitFullscreen || function () {}).call(document);
          root.classList.remove('is-fs');
        }
      });
    var completeClose = root.querySelector('[data-gx-complete-close]');
    if (completeClose)
      on(completeClose, 'click', function () {
        if (complete) complete.hidden = true;
      });

    on(document, 'keydown', function (e) {
      if (e.key === 'u' || e.key === 'U') {
        if (root.classList.contains('is-live') || state.started) toggleUI();
      }
      if (e.key === 'f' || e.key === 'F') {
        if (fsBtn && state.started) fsBtn.click();
      }
      if (!lb || lb.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next(1);
      if (e.key === 'ArrowLeft') next(-1);
      if (e.key === 'z' || e.key === 'Z') {
        zoom = zoom > 1.5 ? 1 : 2.2;
        if (zoom === 1) {
          panX = 0;
          panY = 0;
        }
        setZoomTransform();
      }
    });

    buildFilm();
  });

  /* ── Commission quote (from scratch) ───────────────────── */
  document.querySelectorAll('[data-cq]').forEach(function (root) {
    var hang = root.querySelector('[data-cq-hang]');
    var frameBox = root.querySelector('[data-cq-frame-box]');
    var previewImg = root.querySelector('[data-cq-preview-img]');
    var labelSize = root.querySelector('[data-cq-label-size]');
    var labelInches = root.querySelector('[data-cq-label-inches]');
    var labelFrame = root.querySelector('[data-cq-label-frame]');
    var labelShip = root.querySelector('[data-cq-label-ship]');
    var hiddenSize = root.querySelector('[data-cq-hidden-size]');
    var hiddenFrame = root.querySelector('[data-cq-hidden-frame]');
    var hiddenShip = root.querySelector('[data-cq-hidden-ship]');
    var hiddenPhotos = root.querySelector('[data-cq-hidden-photos]');
    var photoInput = root.querySelector('[data-cq-photos]');
    var photoThumbs = root.querySelector('[data-cq-thumbs]');
    var body = root.querySelector('[data-cq-body]');
    var form = root.querySelector('[data-cq-form]');
    var notes = root.querySelector('[data-cq-notes]');

    var previewArt = root.querySelector('[data-cq-preview-art]') || previewImg;
    var state = {
      size: 'statement',
      sizeName: 'Statement',
      inches: '~36–48″',
      scale: 0.52,
      frame: 'float',
      frameName: 'Studio float',
      ship: 'United States',
      photos: [],
    };
    /* Initial destination comes from the server-rendered .is-on button (US by default, or the visitor's detected country) */
    (function () {
      var onShip = root.querySelector('[data-cq-ship].is-on');
      if (onShip) state.ship = onShip.getAttribute('data-cq-ship') || state.ship;
    })();

    function pick(group, btn) {
      root.querySelectorAll('[data-cq-group="' + group + '"] [role="radio"]').forEach(function (b) {
        b.classList.remove('is-on');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-on');
      btn.setAttribute('aria-checked', 'true');
    }

    function sync() {
      if (hang) {
        hang.setAttribute('data-size', state.size);
        hang.setAttribute('data-frame', state.frame);
        hang.style.setProperty('--cq-scale', String(state.scale));
      }
      if (frameBox) {
        frameBox.className = 'cq__hang-frame cq__hang-frame--' + state.frame;
      }
      /* Gradient colour field — not photo art */
      if (previewArt) {
        previewArt.className = 'cq__hang-art cq__hang-art--' + state.size;
      }
      if (labelSize) labelSize.textContent = state.sizeName;
      if (labelInches) labelInches.textContent = state.inches;
      if (labelFrame) labelFrame.textContent = state.frameName;
      if (labelShip) labelShip.textContent = state.ship;
      if (hiddenSize) hiddenSize.value = state.sizeName + ' · ' + state.inches;
      if (hiddenFrame) hiddenFrame.value = state.frameName;
      if (hiddenShip) hiddenShip.value = state.ship;
      var objBtn = root.querySelector('[data-cq-scale-btn]');
      if (objBtn) {
        var obj = 1.32 - state.scale;
        if (obj > 1) obj = 1;
        if (obj < 0.6) obj = 0.6;
        objBtn.style.setProperty('--cq-obj', obj.toFixed(3));
      }
    }

    function buildBody() {
      var extra = notes && notes.value ? notes.value.trim() : '';
      var refLine = (root.getAttribute('data-cq-ref-line') || '').trim();
      /* Prefer live notes (already prefilled); fall back to stored walkthrough line */
      var inspired =
        extra && extra.indexOf('inspired by:') >= 0
          ? ''
          : refLine
            ? 'Inspired by (walkthrough): ' + refLine
            : '';
      return [
        'COMMISSION QUOTE REQUEST',
        'Units: inches',
        'Size: ' + state.sizeName + ' (' + state.inches + ')',
        'Frame: ' + state.frameName,
        'Destination: ' + state.ship,
        'Copyright: retained by the Artist (client ticked agreement)',
        inspired ? '\n' + inspired : '',
        extra ? '\nNOTES\n' + extra : '',
        state.photos && state.photos.length
          ? '\nPHOTOS\n' + state.photos.join(', ')
          : '',
        'Source: sebgowerart.com commission form · walkthrough CTA',
      ]
        .filter(Boolean)
        .join('\n');
    }

    root.querySelectorAll('[data-cq-size]').forEach(function (btn) {
      on(btn, 'click', function () {
        pick('size', btn);
        state.size = btn.getAttribute('data-cq-size') || state.size;
        state.sizeName = (btn.querySelector('.cq__size-name') || {}).textContent || state.size;
        state.inches = btn.getAttribute('data-inches') || state.inches;
        state.scale = parseFloat(btn.getAttribute('data-scale') || '0.5') || 0.5;
        sync();
      });
    });

    root.querySelectorAll('[data-cq-frame]').forEach(function (btn) {
      on(btn, 'click', function () {
        pick('frame', btn);
        state.frame = btn.getAttribute('data-cq-frame') || state.frame;
        state.frameName = btn.getAttribute('data-frame-label') || state.frameName;
        sync();
      });
    });

    root.querySelectorAll('[data-cq-ship]').forEach(function (btn) {
      on(btn, 'click', function () {
        pick('ship', btn);
        state.ship = btn.getAttribute('data-cq-ship') || state.ship;
        sync();
      });
    });

    function compressImage(file, maxW, quality) {
      return new Promise(function (resolve) {
        var img = new Image();
        var url = URL.createObjectURL(file);
        img.onload = function () {
          var w = img.width;
          var h = img.height;
          if (w > maxW) {
            h = Math.round((h * maxW) / w);
            w = maxW;
          }
          var c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(url);
          c.toBlob(function (blob) { resolve(blob || file); }, 'image/jpeg', quality);
        };
        img.onerror = function () {
          URL.revokeObjectURL(url);
          resolve(file);
        };
        img.src = url;
      });
    }

    function renderThumbs(files) {
      if (!photoThumbs) return;
      photoThumbs.innerHTML = '';
      if (!files.length) {
        photoThumbs.hidden = true;
        return;
      }
      photoThumbs.hidden = false;
      files.forEach(function (f) {
        var im = document.createElement('img');
        im.alt = f.name;
        im.src = URL.createObjectURL(f);
        photoThumbs.appendChild(im);
      });
    }

    if (photoInput) {
      on(photoInput, 'change', function () {
        var files = photoInput.files ? Array.prototype.slice.call(photoInput.files, 0, 6) : [];
        state.photos = files.map(function (f) { return f.name; });
        if (hiddenPhotos) hiddenPhotos.value = state.photos.join(', ');
        renderThumbs(files);
      });
    }

    /*
     * ── Commission send: how it works ─────────────────────────────────
     * 1. Validate (name, email, copyright tick). Nothing is sent if invalid.
     * 2. Build the brief (buildBody) and send it TWO ways at the same time:
     *    a) FormSubmit (primary, carries the photos):
     *       POST https://formsubmit.co/ajax/<quote email> as multipart FormData
     *       with name/email/message/size/frame/destination + up to 6 photos
     *       (each resized in the browser to 1400px JPEG, as before; FormSubmit
     *       caps a submission at 10 MB). _captcha=false so the visitor stays on
     *       the page. Counted as delivered ONLY if the HTTP status is 2xx AND the
     *       JSON reply says success ("true"/true). An un-activated FormSubmit
     *       endpoint replies success:"false", which counts as a failure.
     *    b) Shopify contact form (backup, text only, no photos):
     *       POST /contact with form_type=contact, utf8=✓, contact[name],
     *       contact[email], contact[body] (the full brief). Shopify saves it and
     *       emails the store's sender address. Counted as delivered ONLY if the
     *       request ends on a URL containing "contact_posted=true" (Shopify's
     *       success redirect). A redirect to /challenge (Shopify's spam captcha)
     *       or any error counts as a failure.
     *    Each send has its own timeout (FormSubmit 45s, Shopify 20s).
     * 3. Outcome, after BOTH have finished:
     *    - Either one confirmed → thanks screen. If FormSubmit failed but the
     *      visitor attached photos, the thanks screen also asks them to email
     *      the photos (mailto link) because only FormSubmit carries files.
     *    - Neither confirmed → the thanks screen is NOT shown. An error box under
     *      the button explains it and offers a mailto:<quote email> link
     *      prefilled with their full brief. Everything they typed and the photos
     *      they picked stay in the form, and they can press send again.
     * 4. The button is disabled with "Sending…" while in flight (no double sends).
     * The thanks screen can only be opened from the success branch in finish().
     */
    var thanks = document.querySelector('[data-cq-thanks]');
    var sendBtn = form ? form.querySelector('[data-cq-send]') || form.querySelector('button[type="submit"]') : null;
    var sendLabel = sendBtn ? sendBtn.textContent : '';
    var errorBox = root.querySelector('[data-cq-error]');
    var errorMailto = root.querySelector('[data-cq-error-mailto]');
    var sending = false;

    function showThanks(opts) {
      if (!thanks) return;
      var photoNote = thanks.querySelector('[data-cq-thanks-photos]');
      if (photoNote) {
        var needPhotos = !!(opts && opts.photosMissing);
        photoNote.hidden = !needPhotos;
        var pm = photoNote.querySelector('a[data-cq-thanks-photos-mailto]');
        if (pm && opts && opts.photoMailto) pm.setAttribute('href', opts.photoMailto);
      }
      thanks.hidden = false;
      thanks.removeAttribute('hidden');
      if (thanks.parentNode !== document.body) document.body.appendChild(thanks);
      document.documentElement.classList.add('cq-thanks-open');
      try { window.scrollTo(0, 0); } catch (errT) {}
    }

    function mailtoHref(to, subject, text) {
      var max = 1800; /* keep mailto URLs short enough for mail apps */
      var bodyText = text;
      var href = 'mailto:' + to + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(bodyText);
      while (href.length > max && bodyText.length > 200) {
        bodyText = bodyText.slice(0, Math.floor(bodyText.length * 0.85)) + '\n…';
        href = 'mailto:' + to + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(bodyText);
      }
      return href;
    }

    function setSending(on) {
      sending = on;
      if (!sendBtn) return;
      sendBtn.disabled = on;
      sendBtn.setAttribute('aria-busy', on ? 'true' : 'false');
      sendBtn.textContent = on ? 'Sending…' : sendLabel;
    }

    function hideError() {
      if (errorBox) errorBox.hidden = true;
    }

    function showError(href) {
      if (errorMailto && href) errorMailto.setAttribute('href', href);
      if (errorBox) {
        errorBox.hidden = false;
        try { errorBox.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (eS) {}
      } else {
        try { window.alert('Sorry, your request did not send. Please email seb@sebgowerart.com.'); } catch (eA) {}
      }
    }

    function withTimeout(promise, ms) {
      return new Promise(function (resolve, reject) {
        var t = setTimeout(function () { reject(new Error('timeout')); }, ms);
        promise.then(
          function (v) { clearTimeout(t); resolve(v); },
          function (e) { clearTimeout(t); reject(e); }
        );
      });
    }

    function sendFormSubmit(to, fd) {
      return withTimeout(
        fetch('https://formsubmit.co/ajax/' + encodeURIComponent(to), {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' }
        }),
        45000
      )
        .then(function (res) {
          if (!res || !res.ok) return false;
          return res.json().then(
            function (j) { return !!j && (j.success === true || j.success === 'true'); },
            function () { return false; }
          );
        })
        .catch(function () { return false; });
    }

    function sendShopify(name, email, brief) {
      var sfd = new FormData();
      sfd.append('form_type', 'contact');
      sfd.append('utf8', '\u2713');
      sfd.append('contact[name]', name);
      sfd.append('contact[email]', email);
      sfd.append('contact[body]', brief);
      var url = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'contact';
      return withTimeout(
        fetch(url, { method: 'POST', body: sfd, credentials: 'same-origin', redirect: 'follow' }),
        20000
      )
        .then(function (res) {
          if (!res || !res.ok) return false;
          var u = String(res.url || '');
          if (u.indexOf('/challenge') >= 0) return false;
          return u.indexOf('contact_posted=true') >= 0;
        })
        .catch(function () { return false; });
    }

    if (form) {
      var checkInput = form.querySelector('.cq__check input');
      var checkRow = form.querySelector('.cq__check');
      var nameInput = form.querySelector('[name="contact[name]"]');
      var emailInput = form.querySelector('[name="contact[email]"]');
      if (checkInput && checkRow) {
        on(checkInput, 'change', function () {
          if (checkInput.checked) checkRow.classList.remove('is-invalid');
        });
      }
      on(form, 'submit', function (e) {
        e.preventDefault();
        if (sending) return;
        var ok = true;
        if (checkInput && !checkInput.checked) {
          ok = false;
          if (checkRow) checkRow.classList.add('is-invalid');
        }
        if (nameInput && !String(nameInput.value || '').trim()) {
          ok = false;
          nameInput.focus();
        }
        var emailVal = String((emailInput && emailInput.value) || '').trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
          ok = false;
          if (emailInput) emailInput.focus();
        }
        if (!ok) {
          if (checkInput && !checkInput.checked && checkRow) {
            try { checkRow.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) {}
          }
          return;
        }
        hideError();
        setSending(true);
        var files = photoInput && photoInput.files ? Array.prototype.slice.call(photoInput.files, 0, 6) : [];
        state.photos = files.map(function (f) { return f.name; });
        if (hiddenPhotos) hiddenPhotos.value = state.photos.join(', ');
        if (body) body.value = buildBody();
        var nameVal = String((nameInput && nameInput.value) || '').trim();
        var brief = (body && body.value) || buildBody();
        var fullBrief = 'Name: ' + nameVal + '\nEmail: ' + emailVal + '\n\n' + brief;
        var to = (root.getAttribute('data-cq-photo-to') || 'seb@sebgowerart.com').trim();
        var subject = 'Commission quote · sebgowerart.com';
        var fd = new FormData();
        fd.append('_subject', subject);
        fd.append('_captcha', 'false');
        fd.append('_template', 'table');
        fd.append('_replyto', emailVal);
        fd.append('name', nameVal);
        fd.append('email', emailVal);
        fd.append('message', brief);
        fd.append('size', (hiddenSize && hiddenSize.value) || '');
        fd.append('frame', (hiddenFrame && hiddenFrame.value) || '');
        fd.append('destination', (hiddenShip && hiddenShip.value) || '');
        fd.append('photos', (hiddenPhotos && hiddenPhotos.value) || '');
        var sends = files.slice(0, 6).map(function (f, i) {
          return compressImage(f, 1400, 0.78).then(function (blob) {
            var base = (f.name || 'photo').replace(/\.[^.]+$/, '');
            fd.append('photo_' + (i + 1), blob || f, base + '.jpg');
          });
        });

        var primary = Promise.all(sends)
          .then(function () { return sendFormSubmit(to, fd); })
          .catch(function () { return false; });
        var backup = sendShopify(nameVal, emailVal, fullBrief);

        Promise.all([primary, backup]).then(function (r) {
          var formsubmitOk = r[0] === true;
          var shopifyOk = r[1] === true;
          setSending(false);
          if (formsubmitOk || shopifyOk) {
            var photoText =
              'Hi Seb, here are the photos for my commission request (sent from the website form).\n\n' + fullBrief;
            showThanks({
              photosMissing: !formsubmitOk && files.length > 0,
              photoMailto: mailtoHref(to, subject + ' · photos', photoText)
            });
          } else {
            showError(mailtoHref(to, subject, fullBrief));
          }
        });
      });
    }

    /* Prefill notes when arriving from walkthrough (“I want one like this”) */
    (function applyWalkthroughRef() {
      try {
        var q = new URLSearchParams(location.search || '');
        var from = (q.get('from') || '').toLowerCase();
        var work = (q.get('work') || '').trim();
        var slug = (q.get('slug') || '').trim();
        var num = (q.get('num') || '').trim();
        var place = (q.get('place') || '').trim();
        if (!work && !slug && !num) return;

        var bits = [];
        if (work) bits.push(work);
        if (num) bits.push('(' + num + ')');
        var line =
          "I'd like a commission inspired by: " +
          bits.join(' ') +
          (place ? ' · ' + place : '') +
          (slug ? ' · slug: ' + slug : '') +
          (from === 'studio' ? ' — from the studio.' : ' — from the walkthrough.');

        if (notes) {
          var existing = (notes.value || '').trim();
          if (!existing) {
            notes.value = line;
          } else if (existing.indexOf(line) < 0 && existing.indexOf('inspired by:') < 0) {
            notes.value = line + '\n\n' + existing;
          }
        }

        /* Also store on the form for buildBody if notes empty somehow */
        root.setAttribute('data-cq-ref-work', work || slug || num);
        root.setAttribute('data-cq-ref-line', line);

        /* Soft focus the notes so the visitor sees the prefilled intent */
        if (notes && (from === 'walkthrough' || from === 'studio')) {
          setTimeout(function () {
            try {
              notes.focus({ preventScroll: true });
            } catch (eF) {
              try {
                notes.focus();
              } catch (eF2) {}
            }
          }, 280);
        }
      } catch (eRef) {}
    })();

    /* 5–9″ scale objects — click cycles */
    var scaleBtn = root.querySelector('[data-cq-scale-btn]');
    var scaleItems = root.querySelectorAll('[data-cq-obj]');
    var scaleLabel = root.querySelector('[data-cq-scale-label]');
    if (scaleBtn && scaleItems.length) {
      var scaleIdx = 0;
      function showScale(i) {
        scaleIdx = ((i % scaleItems.length) + scaleItems.length) % scaleItems.length;
        scaleItems.forEach(function (el, n) {
          el.classList.toggle('is-on', n === scaleIdx);
        });
        var label = scaleItems[scaleIdx].getAttribute('data-label') || '';
        if (scaleLabel && label) scaleLabel.textContent = label;
        scaleBtn.setAttribute('aria-label', label + '. Click for another object.');
      }
      on(scaleBtn, 'click', function (e) {
        e.preventDefault();
        showScale(scaleIdx + 1);
      });
    }

    sync();
  });
  /* ── Contact · follow / connected tracking ─────────────── */
  document.querySelectorAll('[data-contact]').forEach(function (root) {
    var STORAGE = 'sg_contact_v2';
    var RANKS = [
      { min: 0, name: 'Silent' },
      { min: 40, name: 'Ping' },
      { min: 100, name: 'Linked' },
      { min: 200, name: 'Online' },
      { min: 320, name: 'Amplified' },
      { min: 450, name: 'Full mesh' },
    ];
    var state = {
      xp: 0,
      connected: {},
      followed: {},
      started: false,
      intent: 'hello',
      step: 0,
      fieldXP: {},
      achievements: {},
    };
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE) || 'null');
      if (saved) {
        state.xp = saved.xp || 0;
        state.connected = saved.connected || saved.unlocked || {};
        state.followed = saved.followed || {};
        state.achievements = saved.achievements || {};
        state.started = !!saved.started;
      } else {
        try {
          var old = JSON.parse(localStorage.getItem('sg_contact_v1') || 'null');
          if (old) {
            state.xp = old.xp || 0;
            state.connected = old.unlocked || {};
            state.started = !!old.started;
            state.achievements = old.achievements || {};
          }
        } catch (e2) {}
      }
    } catch (e) {}

    var channels = Array.prototype.slice.call(root.querySelectorAll('[data-cx-channel]'));
    var boot = root.querySelector('[data-cx-boot]');
    var hud = root.querySelector('[data-cx-hud]');
    var toast = root.querySelector('[data-cx-toast]');
    var complete = root.querySelector('[data-cx-complete]');
    var mapEl = root.querySelector('[data-cx-map]');
    var form = root.querySelector('[data-cx-form]');
    var panels = root.querySelectorAll('[data-cx-panel]');
    var stepTabs = root.querySelectorAll('[data-cx-step-tab]');

    function persist() {
      try {
        localStorage.setItem(
          STORAGE,
          JSON.stringify({
            xp: state.xp,
            connected: state.connected,
            followed: state.followed,
            achievements: state.achievements,
            started: state.started,
          })
        );
      } catch (e) {}
    }

    function rankName() {
      var r = RANKS[0].name;
      for (var i = 0; i < RANKS.length; i++) {
        if (state.xp >= RANKS[i].min) r = RANKS[i].name;
      }
      return r;
    }

    function countObj(o) {
      return Object.keys(o).length;
    }

    function showToast(msg, kind) {
      /* Quiet contact page — no XP / signal pop-ups */
      return;
    }

    function unlockAchieve(id, label) {
      if (state.achievements[id]) return;
      state.achievements[id] = true;
      /* no toast — achievements still save silently */
      persist();
    }

    function statusLabel(kind, id) {
      if (state.followed[id]) {
        if (kind === 'follow') return 'Followed';
        if (kind === 'connect') return 'Connected';
        return 'Visited';
      }
      if (state.connected[id]) {
        if (kind === 'follow') return 'Connected · mark follow?';
        if (kind === 'connect') return 'Opened';
        return 'Opened';
      }
      return 'Not yet';
    }

    function followBtnLabel(kind) {
      if (kind === 'follow') return 'Mark followed';
      if (kind === 'connect') return 'Mark connected';
      return 'Mark visited';
    }

    function paintChannels() {
      channels.forEach(function (ch) {
        var id = ch.getAttribute('data-id');
        var kind = ch.getAttribute('data-kind') || 'follow';
        var connected = !!state.connected[id];
        var followed = !!state.followed[id];
        ch.classList.toggle('is-connected', connected && !followed);
        ch.classList.toggle('is-followed', followed);
        ch.classList.toggle('is-fresh', !connected);
        var st = ch.querySelector('[data-cx-status]');
        if (st) st.textContent = statusLabel(kind, id);
        var fb = ch.querySelector('[data-cx-follow]');
        if (fb) {
          fb.hidden = !connected || followed;
          fb.textContent = followBtnLabel(kind);
        }
      });
    }

    function buildMap() {
      if (!mapEl) return;
      mapEl.innerHTML = '';
      channels.forEach(function (ch) {
        var id = ch.getAttribute('data-id');
        var d = document.createElement('span');
        d.className = 'cx-map__dot';
        if (state.connected[id]) d.classList.add('is-on');
        if (state.followed[id]) d.classList.add('is-followed');
        mapEl.appendChild(d);
      });
    }

    function updateHUD() {
      var el;
      if ((el = root.querySelector('[data-cx-xp]'))) el.textContent = String(state.xp);
      if ((el = root.querySelector('[data-cx-connected]'))) el.textContent = String(countObj(state.connected));
      if ((el = root.querySelector('[data-cx-followed]'))) el.textContent = String(countObj(state.followed));
      if ((el = root.querySelector('[data-cx-rank]'))) el.textContent = rankName();
      if ((el = root.querySelector('[data-cx-bar]'))) {
        var t = channels.length || 1;
        el.style.width = ((countObj(state.followed) / t) * 100).toFixed(1) + '%';
      }
      buildMap();
      paintChannels();
    }

    function addXP(n) {
      var prev = rankName();
      state.xp += n;
      updateHUD();
      persist();
      if (rankName() !== prev) unlockAchieve('rank_' + rankName(), 'Rank · ' + rankName());
      return n;
    }

    function markConnected(ch) {
      var id = ch.getAttribute('data-id');
      if (!id) return;
      if (!state.connected[id]) {
        state.connected[id] = Date.now();
        var base = parseInt(ch.getAttribute('data-xp') || '25', 10);
        addXP(base);
        showToast('Connected · ' + (ch.getAttribute('data-label') || id), 'find');
        if (countObj(state.connected) === 1) unlockAchieve('first_connect', 'First connection');
        if (countObj(state.connected) >= 5) unlockAchieve('five_connect', 'Five channels opened');
      }
      updateHUD();
      persist();
    }

    function markFollowed(ch) {
      var id = ch.getAttribute('data-id');
      if (!id || state.followed[id]) return;
      if (!state.connected[id]) state.connected[id] = Date.now();
      state.followed[id] = Date.now();
      var kind = ch.getAttribute('data-kind') || 'follow';
      addXP(kind === 'follow' ? 35 : 20);
      var word = kind === 'follow' ? 'Followed' : kind === 'connect' ? 'Connected' : 'Visited';
      showToast(word + ' · ' + (ch.getAttribute('data-label') || id), 'achieve');
      if (countObj(state.followed) >= 3) unlockAchieve('three_follow', 'Three platforms locked in');
      if (countObj(state.followed) >= channels.length) {
        unlockAchieve('all_follow', 'Full mesh followed');
        if (complete) {
          complete.hidden = false;
          var m = complete.querySelector('[data-cx-complete-msg]');
          if (m)
            m.textContent =
              countObj(state.followed) +
              ' channels · ' +
              state.xp +
              ' signal · ' +
              rankName();
        }
      }
      updateHUD();
      persist();
    }

    function startGame() {
      state.started = true;
      if (boot) {
        boot.classList.add('is-done');
        setTimeout(function () {
          boot.hidden = true;
        }, 350);
      }
      if (hud) hud.hidden = false;
      root.classList.add('is-live');
      updateHUD();
      persist();
    }

    root.querySelectorAll('[data-cx-start]').forEach(function (b) {
      on(b, 'click', startGame);
    });
    root.querySelectorAll('[data-cx-skip]').forEach(function (b) {
      on(b, 'click', function () {
        if (!state.started) startGame();
        scrollToForm();
      });
    });

    if (state.started) {
      if (boot) boot.hidden = true;
      if (hud) hud.hidden = false;
      root.classList.add('is-live');
    }
    updateHUD();

    channels.forEach(function (ch) {
      var open = ch.querySelector('[data-cx-open]');
      if (open) {
        on(open, 'click', function () {
          if (!state.started) startGame();
          markConnected(ch);
        });
      }
      var fb = ch.querySelector('[data-cx-follow]');
      if (fb) {
        on(fb, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!state.started) startGame();
          markFollowed(ch);
        });
      }
    });

    // Returning from another tab — offer follow confirm on focus
    on(window, 'focus', function () {
      if (!state.started) return;
      // soft nudge: nothing forced
    });

    function scrollToForm() {
      var wrap = root.querySelector('[data-cx-form-wrap]');
      if (wrap) wrap.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
    root.querySelectorAll('[data-cx-to-form], [data-cx-to-form-2]').forEach(function (b) {
      on(b, 'click', function () {
        if (!state.started) startGame();
        if (complete) complete.hidden = true;
        scrollToForm();
      });
    });
    var completeClose = root.querySelector('[data-cx-complete-close]');
    if (completeClose)
      on(completeClose, 'click', function () {
        if (complete) complete.hidden = true;
      });

    root.querySelectorAll('[data-cx-intent]').forEach(function (btn) {
      on(btn, 'click', function () {
        state.intent = btn.getAttribute('data-cx-intent') || 'hello';
        root.querySelectorAll('[data-cx-intent]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        var sub = root.querySelector('[data-cx-subject]');
        if (sub) sub.value = 'Contact · ' + state.intent + ' · ' + rankName();
        if (!state.fieldXP['intent_' + state.intent]) {
          state.fieldXP['intent_' + state.intent] = true;
          addXP(parseInt(btn.getAttribute('data-xp') || '10', 10));
        }
        updateRecap();
      });
    });

    function goStep(i) {
      state.step = clamp(i, 0, panels.length - 1);
      panels.forEach(function (p, idx) {
        p.classList.toggle('is-active', idx === state.step);
      });
      stepTabs.forEach(function (t, idx) {
        t.classList.toggle('is-active', idx === state.step);
      });
      updateRecap();
    }
    stepTabs.forEach(function (t, i) {
      on(t, 'click', function () {
        goStep(i);
      });
    });
    root.querySelectorAll('[data-cx-next]').forEach(function (b) {
      on(b, 'click', function () {
        if (state.step === 0) {
          var name = root.querySelector('[data-cx-field="name"]');
          var email = root.querySelector('[data-cx-field="email"]');
          if (!name || !name.value || !email || !email.value) {
            showToast('Name + email first', 'hunt');
            return;
          }
          if (!state.fieldXP.identity) {
            state.fieldXP.identity = true;
            addXP(25);
          }
        }
        goStep(state.step + 1);
      });
    });
    root.querySelectorAll('[data-cx-prev]').forEach(function (b) {
      on(b, 'click', function () {
        goStep(state.step - 1);
      });
    });
    root.querySelectorAll('[data-cx-field]').forEach(function (inp) {
      on(inp, 'input', updateRecap);
    });

    function updateRecap() {
      var recap = root.querySelector('[data-cx-recap]');
      if (!recap) return;
      var name = (root.querySelector('[data-cx-field="name"]') || {}).value || '—';
      recap.textContent =
        'Intent: ' +
        state.intent +
        ' · ' +
        name +
        ' · Connected ' +
        countObj(state.connected) +
        ' · Followed ' +
        countObj(state.followed) +
        ' · ' +
        rankName();
    }

    if (form) {
      on(form, 'submit', function () {
        var hidden = root.querySelector('[data-cx-body-hidden]');
        var msg = (root.querySelector('[data-cx-field="message"]') || {}).value || '';
        var name = (root.querySelector('[data-cx-field="name"]') || {}).value || '';
        var place = (root.querySelector('[data-cx-field="place"]') || {}).value || '';
        var prefer = (root.querySelector('[data-cx-field="prefer"]') || {}).value || '';
        var phone = (root.querySelector('[data-cx-field="phone"]') || {}).value || '';
        if (hidden) {
          hidden.value = [
            'INTENT: ' + state.intent,
            'NAME: ' + name,
            'PLACE: ' + place,
            'PHONE: ' + phone,
            'PREFER: ' + prefer,
            'CONNECTED: ' + Object.keys(state.connected).join(', '),
            'FOLLOWED: ' + Object.keys(state.followed).join(', '),
            'SIGNAL: ' + state.xp + ' · ' + rankName(),
            '',
            'MESSAGE:',
            msg,
          ].join('\n');
        }
        if (!state.fieldXP.sent) {
          state.fieldXP.sent = true;
          addXP(80);
          unlockAchieve('sent', 'Message sent');
        }
      });
    }

    if (location.hash === '#cx-form') {
      startGame();
      setTimeout(scrollToForm, 80);
    }

    /* Prefill message when arriving from walkthrough “Ask Seb” */
    (function applyWalkthroughAsk() {
      try {
        var q = new URLSearchParams(location.search || '');
        var from = (q.get('from') || '').toLowerCase();
        var ask = q.get('ask') === '1' || q.get('ask') === 'true';
        var work = (q.get('work') || '').trim();
        if (!work && !ask) return;
        if (from && from !== 'walkthrough' && from !== 'studio' && !ask) return;

        var line =
          work
            ? 'I was looking at ' + work + ' and I have a question.'
            : (from === 'studio'
              ? 'I was looking at a work in the studio and I have a question.'
              : 'I was looking at a work in the walkthrough and I have a question.');

        var msg = root.querySelector('[data-cx-field="message"]');
        if (msg) {
          var existing = (msg.value || '').trim();
          if (!existing) {
            msg.value = line;
          } else if (existing.indexOf('I was looking at') < 0) {
            msg.value = line + '\n\n' + existing;
          }
        }

        if (!state.started) startGame();
        /* Jump to message step (panel 3) so the note is visible */
        try {
          goStep(2);
        } catch (eStep) {}
        setTimeout(function () {
          scrollToForm();
          if (msg) {
            try {
              msg.focus({ preventScroll: true });
            } catch (eF) {
              try {
                msg.focus();
              } catch (eF2) {}
            }
          }
        }, 120);
      } catch (eAsk) {}
    })();
  });


  /* ── Product buy experience ────────────────────────────── */
  document.querySelectorAll('[data-product-buy]').forEach(function (root) {
    var form = root.querySelector('[data-buy-form]');
    var main = root.querySelector('[data-buy-main]');
    var status = root.querySelector('[data-buy-status]');
    var submit = root.querySelector('[data-buy-submit]');
    var idInput = root.querySelector('[data-buy-id]');
    var priceEl = root.querySelector('[data-buy-price]');
    var priceSticky = root.querySelector('[data-buy-price-sticky]');
    var qtyInput = root.querySelector('[data-buy-qty-input]');
    var ctaLabel = root.querySelector('[data-buy-cta-label]');
    var xpEl = root.querySelector('[data-buy-xp]');
    var sticky = root.querySelector('[data-buy-sticky]');
    var stickyCta = root.querySelector('[data-buy-sticky-cta]');
    var comboEl = root.querySelector('[data-buy-combo]');
    var nudgeEl = root.querySelector('[data-buy-nudge]');
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var productData = null;
    try {
      var jsonEl = root.querySelector('[data-product-json]');
      if (jsonEl) productData = JSON.parse(jsonEl.textContent || '{}');
    } catch (e) {
      productData = null;
    }

    function formatMoney(cents) {
      var amount = (Number(cents) || 0) / 100;
      var fixed = amount.toFixed(2);
      var withComma = fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        try {
          return Shopify.formatMoney(cents, moneyFormat);
        } catch (e2) {}
      }
      return moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amount)))
        .replace(/\{\{\s*amount\s*\}\}/g, fixed);
    }

    function selectedOptions() {
      var opts = ['', '', ''];
      root.querySelectorAll('[data-buy-option-group]').forEach(function (group) {
        var idx = parseInt(group.getAttribute('data-option-index') || '0', 10);
        var sel = group.querySelector('[data-buy-option-value].is-selected');
        if (sel) opts[idx] = sel.getAttribute('data-value') || '';
      });
      return opts;
    }

    function findVariant(opts) {
      if (!productData || !productData.variants) return null;
      var list = productData.variants;
      for (var i = 0; i < list.length; i++) {
        var v = list[i];
        var match = true;
        for (var k = 0; k < 3; k++) {
          var have = v['option' + (k + 1)];
          if (have == null || have === '') continue; /* option not used on this product */
          if (have !== (opts[k] || '')) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    function applyVariant(variant) {
      if (!variant) {
        if (submit) {
          submit.disabled = true;
          if (ctaLabel) ctaLabel.textContent = 'Unavailable';
        }
        if (stickyCta) stickyCta.disabled = true;
        if (comboEl) comboEl.textContent = 'That size and frame combination isn’t available.';
        if (nudgeEl) nudgeEl.textContent = 'Try another size or frame.';
        return;
      }
      if (idInput) idInput.value = String(variant.id);
      var priceText = formatMoney(variant.price);
      if (priceEl) priceEl.textContent = priceText;
      if (priceSticky) priceSticky.textContent = priceText;
      var available = !!variant.available;
      if (submit) {
        submit.disabled = !available;
        if (ctaLabel) ctaLabel.textContent = available ? 'Claim this colour' : 'Held';
      }
      if (stickyCta) stickyCta.disabled = !available;
      if (comboEl) {
        var parts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        comboEl.textContent = parts.length ? parts.join(' · ') + ' — ' + priceText : priceText;
      }
      if (nudgeEl) {
        nudgeEl.textContent = available
          ? 'Secure it now — open edition, made to order.'
          : 'Currently unavailable.';
      }
      /* Media swap if variant has a featured image */
      if (main && variant.featured_image && variant.featured_image.src) {
        main.src = variant.featured_image.src;
        main.srcset = '';
      }
    }

    function refreshFromPills() {
      if (!root.querySelector('[data-buy-option-picker]')) return;
      var opts = selectedOptions();
      /* Disable frame/size pills that have no available combo with current selection */
      root.querySelectorAll('[data-buy-option-group]').forEach(function (group) {
        var gIdx = parseInt(group.getAttribute('data-option-index') || '0', 10);
        group.querySelectorAll('[data-buy-option-value]').forEach(function (btn) {
          var trial = opts.slice();
          trial[gIdx] = btn.getAttribute('data-value') || '';
          var v = findVariant(trial);
          var ok = !!(v && v.available);
          var exists = !!v;
          btn.disabled = !exists;
          btn.classList.toggle('is-unavailable', exists && !ok);
          btn.setAttribute('aria-disabled', exists ? 'false' : 'true');
        });
      });
      applyVariant(findVariant(opts));
    }

    root.querySelectorAll('[data-buy-thumb]').forEach(function (btn) {
      on(btn, 'click', function () {
        var full = btn.getAttribute('data-full');
        if (main && full) {
          main.src = full;
          main.srcset = '';
        }
        root.querySelectorAll('[data-buy-thumb]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
      });
    });

    /* Product hero tilt — homepage lerp, square card, bind once */
    (function bindBuyTilt() {
      if (reduce) return;
      var stage = root.querySelector('[data-buy-tilt]');
      if (!stage || stage.getAttribute('data-buy-tilt-bound') === '1') return;
      var card = stage.querySelector('.buy__tilt') || stage;
      var inner = stage.querySelector('.buy__tilt-inner');
      var glare = stage.querySelector('.buy__tilt-glare');
      if (!inner || !glare) return;
      stage.setAttribute('data-buy-tilt-bound', '1');
      function lerp(a, b, t) { return a + (b - a) * t; }
      var tx = 0, ty = 0, ts = 1, tg = 0;
      var cx = 0, cy = 0, cs = 1, cg = 0;
      var gx = 50, gy = 50, cgx = 50, cgy = 50;
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        tx = -((py * 2) - 1) * 14;
        ty = ((px * 2) - 1) * 14;
        ts = 1.04;
        tg = 1;
        gx = px * 100;
        gy = py * 100;
      });
      card.addEventListener('pointerleave', function () {
        tx = 0; ty = 0; ts = 1; tg = 0;
      });
      function tick() {
        var k = 0.12;
        cx = lerp(cx, tx, k);
        cy = lerp(cy, ty, k);
        cs = lerp(cs, ts, k);
        cg = lerp(cg, tg, k);
        cgx = lerp(cgx, gx, k);
        cgy = lerp(cgy, gy, k);
        inner.style.transform = 'perspective(900px) rotateX(' + cx.toFixed(2) + 'deg) rotateY(' + cy.toFixed(2) + 'deg) scale(' + cs.toFixed(3) + ')';
        glare.style.setProperty('--gx', cgx.toFixed(1) + '%');
        glare.style.setProperty('--gy', cgy.toFixed(1) + '%');
        glare.style.opacity = cg.toFixed(3);
        requestAnimationFrame(tick);
      }
      tick();
    })();

    /* Size / Frame pill picker */
    root.querySelectorAll('[data-buy-option-value]').forEach(function (btn) {
      on(btn, 'click', function () {
        if (btn.disabled) return;
        var group = btn.closest('[data-buy-option-group]');
        if (!group) return;
        group.querySelectorAll('[data-buy-option-value]').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        refreshFromPills();
      });
    });

    /* Legacy flat variant buttons (if any remain) */
    root.querySelectorAll('[data-buy-variant]').forEach(function (btn) {
      on(btn, 'click', function () {
        if (btn.disabled) return;
        root.querySelectorAll('[data-buy-variant]').forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        var id = btn.getAttribute('data-id');
        var price = btn.getAttribute('data-price');
        var available = btn.getAttribute('data-available') === 'true';
        if (idInput) idInput.value = id;
        if (priceEl) priceEl.textContent = price;
        if (priceSticky) priceSticky.textContent = price;
        if (submit) {
          submit.disabled = !available;
          if (ctaLabel) ctaLabel.textContent = available ? 'Claim this colour' : 'Held';
        }
        if (stickyCta) stickyCta.disabled = !available;
      });
    });

    if (root.querySelector('[data-buy-option-picker]')) {
      refreshFromPills();
    }

    root.querySelectorAll('[data-buy-qty]').forEach(function (btn) {
      on(btn, 'click', function () {
        if (!qtyInput) return;
        var d = parseInt(btn.getAttribute('data-buy-qty') || '0', 10);
        var v = Math.max(1, (parseInt(qtyInput.value || '1', 10) || 1) + d);
        qtyInput.value = String(v);
      });
    });

    function setStatus(msg, ok) {
      if (!status) return;
      status.hidden = !msg;
      status.textContent = msg || '';
      status.classList.toggle('is-ok', !!ok);
      status.classList.toggle('is-err', msg && !ok);
    }

    function addXP() {
      try {
        var k = storageKey('sg_collector_xp');
        var n = parseInt(localStorage.getItem(k) || localStorage.getItem('sg_collector_xp') || '0', 10) || 0;
        n += 40;
        localStorage.setItem(k, String(n));
        try { localStorage.setItem('sg_collector_xp', String(n)); } catch (e2) {}
        if (xpEl) {
          xpEl.hidden = false;
          xpEl.textContent = '+40 collector signal · ' + n + ' total';
        }
      } catch (e) {}
    }

    function addToCart(e) {
      if (e) e.preventDefault();
      if (!form || !idInput) return;
      if (submit && submit.disabled) return;
      setStatus('Securing…', true);
      if (submit) submit.disabled = true;
      var fd = new FormData(form);
      fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          setStatus('In your cart — continue or checkout.', true);
          addXP();
          if (ctaLabel) ctaLabel.textContent = 'Added ✓';
          // refresh cart count
          return fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart.js').then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = cart && cart.item_count ? String(cart.item_count) : '';
          });
          if (submit) {
            submit.disabled = false;
            setTimeout(function () {
              if (ctaLabel) ctaLabel.textContent = 'Claim this colour';
            }, 1800);
          }
        })
        .catch(function () {
          // fallback native submit
          setStatus('Opening cart…', true);
          form.removeEventListener('submit', addToCart);
          form.submit();
        });
    }

    if (form) on(form, 'submit', addToCart);
    if (stickyCta)
      on(stickyCta, 'click', function () {
        addToCart();
      });

    // Sticky visibility is handled by the global scroll hub (collectProductStickies)
    if (typeof collectProductStickies === 'function') collectProductStickies();
  });



  /* ── Immersive walkthrough · one work at a time ───────────
     Full-screen cinema slideshow. Crossfade + gentle Ken Burns.
     Timer-driven (no fragile CSS animationend dependency). */
  document.querySelectorAll('[data-archive]').forEach(function (root) {
    var walk = root.querySelector('[data-walk]');
    if (!walk) return;

    var imgA = walk.querySelector('[data-walk-img]');
    var imgB = walk.querySelector('[data-walk-img-b]');
    var intro = walk.querySelector('[data-walk-intro]');
    var title = walk.querySelector('[data-walk-title]');
    var place = walk.querySelector('[data-walk-place]');
    var num = walk.querySelector('[data-walk-num]');
    var count = walk.querySelector('[data-walk-count]');
    var progress = walk.querySelector('[data-walk-progress]');
    var timerBar = walk.querySelector('[data-walk-timer]');
    var playBtn = walk.querySelector('[data-walk-play]');
    var stage = walk.querySelector('[data-walk-plate]') || walk;
    var distRange = walk.querySelector('[data-walk-distance]');
    var distFeetEl = walk.querySelector('[data-walk-feet]');
    var inspectRange = walk.querySelector('[data-walk-inspect-range]');
    var inspectInEl = walk.querySelector('[data-walk-inspect-in]');
    var inspectRail = walk.querySelector('[data-walk-inspect]');
    var printBadge = walk.querySelector('[data-walk-print]');
    var commissionCta = walk.querySelector('[data-walk-commission]');
    var askCta = walk.querySelector('[data-walk-ask]');
    var commissionBase =
      (walk.getAttribute('data-commission-url') || '').trim() ||
      (commissionCta && commissionCta.getAttribute('href')) ||
      '/pages/commission';
    var contactBase =
      (walk.getAttribute('data-contact-url') || '').trim() ||
      (askCta && (askCta.getAttribute('href') || '').split('#')[0]) ||
      '/pages/contact-me-directly';

    var items = [];
    var i = 0;
    var playing = false;
    var open = false;
    var busy = false;
    /* Stub so card/CTA clicks never race past init */
    var walkOpenStub = function () {
      return false;
    };
    root._sgWalkOpenStub = walkOpenStub;
    root._sgWalk = root._sgWalk || {
      open: walkOpenStub,
      close: function () {},
      ready: false,
    };
    /*
      Function decls below are hoisted — publish the real API on the next tick
      so a throw in paint/atmosphere setup can never leave ready:false forever.
    */
    setTimeout(function () {
      try {
        if (typeof publishWalkApi === 'function') publishWalkApi();
      } catch (ePub) {}
    }, 0);
    var timer = null;
    var startedAt = 0;
    var pausedLeft = 0;
    var activeSlot = 'a';
    /* Autoplay hold: 2 / 5 / 10 / 30 seconds (persisted) */
    var HOLD_OPTS = [5];
    var HOLD_STORAGE = 'sg_walk_hold_sec';
    function loadHoldSec() {
      return 5;
    }
    var holdSec = 5;
    var HOLD_MS = 5000;
    var FADE_MS = reduce ? 240 : 1200; /* very soft crossfade */
    var homeParent = walk.parentNode;
    var printProducts = null;

    /*
      Quality split:
      · Archive wall / thumbs → hyperspeed Web JPEGs only (Liquid data-src / thumb)
      · Walkthrough stage → always museum CDN/file WEBP when available
      · No “Load museum quality” step in walkthrough
    */
    var walkQuality = 'museum';
    var museumBtn = walk.querySelector('[data-walk-museum-load]');
    try {
      walk.setAttribute('data-walk-quality', 'museum');
    } catch (eWq) {}
    /* Hide legacy museum-load chip if still in markup */
    if (museumBtn) {
      try {
        museumBtn.hidden = true;
        museumBtn.setAttribute('hidden', '');
        museumBtn.style.display = 'none';
      } catch (eHide) {}
    }

    function paintHoldButtons() {
      walk.querySelectorAll('[data-walk-hold-sec]').forEach(function (btn) {
        var sec = parseInt(btn.getAttribute('data-walk-hold-sec') || '0', 10);
        var on = sec === holdSec;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      var holdLabel = walk.querySelector('[data-walk-hold-label]');
      if (holdLabel) holdLabel.textContent = holdSec + 's';
      var holdToggle = walk.querySelector('[data-walk-hold-toggle]');
      if (holdToggle) {
        holdToggle.setAttribute('title', 'Timer · ' + holdSec + ' seconds per work');
        holdToggle.setAttribute('aria-label', 'Timer, ' + holdSec + ' seconds per work');
      }
    }

    function setHoldMenuOpen(on) {
      var holdRoot = walk.querySelector('[data-walk-hold]');
      var holdToggle = walk.querySelector('[data-walk-hold-toggle]');
      var holdPanel = walk.querySelector('[data-walk-hold-panel]');
      on = !!on;
      if (holdRoot) holdRoot.classList.toggle('is-open', on);
      if (holdToggle) holdToggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (holdPanel) {
        if (on) {
          holdPanel.hidden = false;
          holdPanel.removeAttribute('hidden');
        } else {
          holdPanel.hidden = true;
          holdPanel.setAttribute('hidden', '');
        }
      }
    }

    function currentItem() {
      if (!items.length) return null;
      return items[i] || null;
    }

    function syncWalkQualityAttr(it) {
      var hasMuseum = !!(it && itemMuseumSrc(it));
      walkQuality = hasMuseum ? 'museum' : 'hyperspeed';
      try {
        walk.setAttribute('data-walk-quality', walkQuality);
      } catch (ePq) {}
      if (hasMuseum) {
        try {
          syncInspectTransform();
        } catch (eSi) {}
      } else {
        try {
          resetInspect(true);
        } catch (eRi) {}
      }
    }

    function setHoldSec(sec, fromUser) {
      sec = 5;
      holdSec = sec;
      HOLD_MS = holdSec * 1000;
      paintHoldButtons();
      try {
        localStorage.setItem(HOLD_STORAGE, String(holdSec));
      } catch (eHs) {}
      if (fromUser) setHoldMenuOpen(false);
      /* Re-arm autoplay with the new duration if currently playing */
      if (fromUser && open && playing) {
        pausedLeft = 0;
        armAdvance(HOLD_MS);
      } else if (fromUser && open && !playing) {
        pausedLeft = HOLD_MS;
        stopProgressAnim();
      }
    }

    function loadPrintProducts() {
      if (printProducts) return printProducts;
      printProducts = [];
      try {
        var el =
          root.querySelector('[data-print-products]') ||
          document.querySelector('[data-print-products]');
        if (el && el.textContent) {
          var parsed = JSON.parse(el.textContent);
          if (Array.isArray(parsed)) printProducts = parsed;
        }
      } catch (e) {
        printProducts = [];
      }
      return printProducts;
    }

    function normalizeKey(s) {
      return String(s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    function resolvePrintForItem(it) {
      if (!it) return null;
      var direct = (it.getAttribute('data-print-url') || '').trim();
      if (direct) {
        return { url: direct, available: it.getAttribute('data-has-print') !== 'false' };
      }
      var products = loadPrintProducts();
      if (!products.length) return null;
      var slug = normalizeKey(it.getAttribute('data-slug'));
      var title = normalizeKey(it.getAttribute('data-title'));
      var id = normalizeKey(it.getAttribute('data-id')).replace(/^sig /, '');
      var num = normalizeKey(it.getAttribute('data-num'));
      /* Catalogue titles like "No. 230302002" → digits for handle match */
      var digits = (it.getAttribute('data-title') || '').replace(/\D/g, '');
      var best = null;
      for (var p = 0; p < products.length; p++) {
        var prod = products[p];
        if (!prod || !prod.url) continue;
        var ph = normalizeKey(prod.handle);
        var pt = normalizeKey(prod.title);
        var hit = false;
        if (slug && (ph.indexOf(slug) >= 0 || pt.indexOf(slug) >= 0 || slug.indexOf(ph) >= 0)) hit = true;
        if (!hit && title && title.length > 2 && (pt.indexOf(title) >= 0 || title.indexOf(pt) >= 0 || ph.indexOf(title) >= 0))
          hit = true;
        if (!hit && id && (ph.indexOf(id) >= 0 || pt.indexOf(id) >= 0)) hit = true;
        if (!hit && digits && digits.length >= 6 && (ph.indexOf(digits) >= 0 || pt.indexOf(digits) >= 0)) hit = true;
        if (!hit && num && num.length > 1 && (ph.indexOf(num) >= 0 || pt.indexOf(num) >= 0)) hit = true;
        if (hit) {
          best = { url: prod.url, available: prod.available !== false, title: prod.title };
          break;
        }
      }
      if (best && best.url) {
        try {
          it.setAttribute('data-print-url', best.url);
          it.setAttribute('data-has-print', 'true');
        } catch (e2) {}
      }
      return best;
    }

    function updatePrintBadge(it) {
      if (!printBadge) return;
      var match = resolvePrintForItem(it);
      if (match && match.url) {
        printBadge.href = match.url;
        printBadge.hidden = false;
        printBadge.removeAttribute('hidden');
        printBadge.setAttribute('aria-hidden', 'false');
        printBadge.textContent = 'Available as print';
      } else {
        printBadge.hidden = true;
        printBadge.setAttribute('hidden', '');
        printBadge.setAttribute('aria-hidden', 'true');
      }
    }
    /* Distance: slider is 1–40ft. Zoom curve still uses the original 1–100ft math. */
    var feet = 24;
    var FEET_MIN = 1;
    var FEET_MAX = 40;
    var FEET_CURVE_MAX = 50; /* half the old 100ft span → 2× zoom per foot */
    var FEET_COMFORT = 24;
    var SCALE_NEAR = 4.65; /* 1ft — 3× prior close-up */
    var SCALE_MID = 0.92; /* comfortable mid view */
    var SCALE_FAR = 0.14; /* original 100ft far — unchanged */
    var panX = 0;
    var panY = 0;

    function feetToScale(ft) {
      var t = (ft - FEET_MIN) / (FEET_CURVE_MAX - FEET_MIN);
      t = Math.max(0, Math.min(1, t));
      var tMid = (FEET_COMFORT - FEET_MIN) / (FEET_CURVE_MAX - FEET_MIN);
      function smooth(u) {
        u = Math.max(0, Math.min(1, u));
        return u * u * (3 - 2 * u);
      }
      if (t <= tMid) {
        return SCALE_NEAR + (SCALE_MID - SCALE_NEAR) * smooth(t / tMid);
      }
      return SCALE_MID + (SCALE_FAR - SCALE_MID) * smooth((t - tMid) / (1 - tMid));
    }

    function inspectScaleNow() {
      return 1;
    }

    function syncInspectTransform() {
      /* Detail inch rail removed — ft slider owns scale only */
      panX = 0;
      panY = 0;
      if (stage) {
        stage.style.setProperty('--walk-inspect', '1');
        stage.style.setProperty('--walk-pan-x', '0px');
        stage.style.setProperty('--walk-pan-y', '0px');
      }
      walk.classList.remove('is-inspect-pan', 'is-inspect-scrub', 'is-panning');
    }

    function resetInspect() {
      syncInspectTransform();
    }

    function applyInspect() {
      /* no-op — inch detail zoom removed */
      syncInspectTransform();
    }

    function pauseWalkForUserView() {
      if (!playing) return;
      playing = false;
      clearTimer();
      stopProgressAnim();
      if (typeof paintWalkPlayBtn === 'function') paintWalkPlayBtn();
      else if (playBtn) {
        playBtn.classList.remove('is-playing');
        playBtn.setAttribute('aria-label', 'Play autoplay');
        playBtn.setAttribute('aria-pressed', 'false');
      }
    }

    function applyDistance(ft, fromUser) {
      feet = Math.max(FEET_MIN, Math.min(FEET_MAX, Math.round(Number(ft) || FEET_MIN)));
      if (distRange && String(distRange.value) !== String(feet)) {
        distRange.value = String(feet);
      }
      if (distFeetEl) distFeetEl.textContent = String(feet);
      if (distRange) {
        distRange.setAttribute('aria-valuenow', String(feet));
        distRange.setAttribute(
          'aria-valuetext',
          feet + ' feet' + (feet <= 3 ? ' · up close' : feet >= 36 ? ' · far' : '')
        );
      }
      var scale = feetToScale(feet);
      if (stage) {
        stage.style.setProperty('--walk-scale', scale.toFixed(4));
        /* Keep inspect multiplier neutral — distance is the only zoom */
        stage.style.setProperty('--walk-inspect', '1');
      }
      /* Re-align paint layer after distance scale changes */
      if (paintOn || paintDirty) {
        requestAnimationFrame(function () {
          if (typeof resizePaintCanvas === 'function') resizePaintCanvas();
        });
      }
      /* User scrubbing distance — pause autoplay so the view holds */
      if (fromUser) pauseWalkForUserView();
    }
    if (distRange) {
      try {
        distRange.max = String(FEET_MAX);
        distRange.setAttribute('max', String(FEET_MAX));
        distRange.setAttribute('aria-valuemax', String(FEET_MAX));
      } catch (eMax) {}
    }

    function itemHyperspeedSrc(it) {
      if (!it) return '';
      var src = (it.getAttribute('data-src') || '').trim();
      if (src) return src;
      var img = it.querySelector('img');
      if (img) return (img.currentSrc || img.src || '').trim();
      return '';
    }

    /*
      Museum quality URL resolution (priority):
      1) External CDN (settings.museum_cdn_base + data-museum-file) — full-res from TIFF
      2) Explicit data-src-museum (Shopify Files WEBP)
      3) Derive .webp basename from hyperspeed JPEG path
    */
    function museumCdnBase() {
      var b = '';
      try {
        b = (walk.getAttribute('data-museum-cdn') || '').trim();
      } catch (e1) {}
      if (!b) {
        try {
          b = String(window.sgMuseumCdn || '').trim();
        } catch (e2) {}
      }
      if (!b) return '';
      if (b.charAt(b.length - 1) !== '/') b += '/';
      return b;
    }

    function joinMuseumCdn(file) {
      var base = museumCdnBase();
      if (!base || !file) return '';
      file = String(file).replace(/^\/+/, '');
      /* Encode path segments (spaces, &, unicode) but keep slashes */
      return (
        base +
        file
          .split('/')
          .map(function (seg) {
            return encodeURIComponent(seg);
          })
          .join('/')
      );
    }

    function hyperspeedToMuseumUrl(src) {
      if (!src) return '';
      var s = String(src);
      var out = s
        .replace(/\/([^\/\?]+)\.(jpe?g|png)(\?[^#]*)?$/i, '/$1.webp$3')
        .replace(/-web-nb\.(jpe?g|png|webp)(\?[^#]*)?/i, '-print-nb.webp$2')
        .replace(/-web-nf\.(jpe?g|png|webp)(\?[^#]*)?/i, '-print-nf.webp$2')
        .replace(/-web\.(jpe?g|png|webp)(\?[^#]*)?/i, '-print.webp$2');
      if (out === s) return '';
      try {
        out = out.replace(/([?&])v=[^&]*/i, '$1').replace(/[?&]$/, '').replace(/\?&/, '?');
      } catch (eMu) {}
      return out;
    }

    function itemMuseumSrc(it) {
      if (!it) return '';
      var file =
        (it.getAttribute('data-museum-file') || '').trim() ||
        (it.getAttribute('data-museum-name') || '').trim();
      var cdnUrl = joinMuseumCdn(file);
      if (cdnUrl) return cdnUrl;

      var museum = (it.getAttribute('data-src-museum') || '').trim();
      if (museum) return museum;

      if (file && !museumCdnBase()) {
        /* CDN not configured — try Shopify Files by filename */
        /* file_url isn't available in JS; keep data-src-museum from Liquid */
      }

      var fromFast = hyperspeedToMuseumUrl(itemHyperspeedSrc(it));
      if (fromFast) return fromFast;

      var title = (it.getAttribute('data-title') || '').trim();
      if (title) {
        var viaTitle = joinMuseumCdn(title + '.webp');
        if (viaTitle) return viaTitle;
      }
      return '';
    }

    function itemSrc(it, preferQuality) {
      if (!it) return '';
      /* Cloudflare URL is on data-src when museum_cdn_base is set */
      var fast = itemHyperspeedSrc(it);
      if (fast) return fast;
      var museum = itemMuseumSrc(it);
      return museum || '';
    }

    /* Walkthrough + wall both prefer Cloudflare (data-src from Liquid CDN). */
    function itemWalkSrc(it) {
      return itemHyperspeedSrc(it) || itemMuseumSrc(it);
    }
    function itemWalkUpgradeSrc(it) {
      /* Same CDN file for wall and walk — no second upgrade hop needed */
      return '';
    }

    function collectItems(forceAll) {
      var all = Array.prototype.slice.call(root.querySelectorAll('[data-arc-item], [data-gx-card]'));
      /* Dedupe if both attributes on same node */
      var seen = [];
      all = all.filter(function (it) {
        if (seen.indexOf(it) >= 0) return false;
        seen.push(it);
        return true;
      });
      var vis = all.filter(function (it) {
        return !it.classList.contains('is-hidden');
      });
      var pool = forceAll || !vis.length ? all : vis;
      items = pool.filter(function (it) {
        return !!itemSrc(it);
      });
      return items;
    }

    function activeImg() {
      return activeSlot === 'a' ? imgA : imgB;
    }
    function idleImg() {
      return activeSlot === 'a' ? imgB : imgA;
    }

    function setMeta(it) {
      if (!it) return;
      if (title) title.textContent = it.getAttribute('data-title') || '';
      if (place) place.textContent = it.getAttribute('data-place') || '';
      if (num) num.textContent = it.getAttribute('data-num') || '';
      if (count) {
        count.textContent =
          String(i + 1).padStart(3, '0') +
          ' / ' +
          String(Math.max(items.length, 1)).padStart(3, '0');
      }
      updatePrintBadge(it);
      updateWalkCtas(it);
    }

    function workParams(it) {
      var params = new URLSearchParams();
      params.set('from', 'walkthrough');
      var workTitle = (it && it.getAttribute('data-title')) || '';
      var workPlace = (it && it.getAttribute('data-place')) || '';
      var workNum = (it && it.getAttribute('data-num')) || '';
      var workSlug = (it && it.getAttribute('data-slug')) || '';
      var workId = (it && it.getAttribute('data-id')) || '';
      if (workTitle) params.set('work', workTitle);
      if (workSlug) params.set('slug', workSlug);
      if (workNum) params.set('num', workNum);
      if (workPlace) params.set('place', workPlace);
      if (workId) params.set('id', workId);
      return {
        params: params,
        workTitle: workTitle,
        workPlace: workPlace,
        workNum: workNum,
        workSlug: workSlug,
        workId: workId,
      };
    }

    function withParams(base, params) {
      var b = (base || '').trim() || '/';
      var hash = '';
      var hashIdx = b.indexOf('#');
      if (hashIdx >= 0) {
        hash = b.slice(hashIdx);
        b = b.slice(0, hashIdx);
      }
      var sep = b.indexOf('?') >= 0 ? '&' : '?';
      return b + sep + params.toString() + hash;
    }

    function updateWalkCtas(it) {
      var info = workParams(it);
      if (commissionCta) {
        commissionCta.href = withParams(commissionBase || '/pages/commission', info.params);
        commissionCta.setAttribute(
          'aria-label',
          info.workTitle
            ? 'Commission a work like ' + info.workTitle
            : 'Commission a work like this'
        );
      }
      if (askCta) {
        var askParams = new URLSearchParams(info.params.toString());
        askParams.set('ask', '1');
        var contactHref = withParams(contactBase || '/pages/contact-me-directly', askParams);
        if (contactHref.indexOf('#') < 0) contactHref += '#cx-form';
        askCta.href = contactHref;
        askCta.setAttribute(
          'aria-label',
          info.workTitle ? 'Ask Seb about ' + info.workTitle : 'Ask Seb a question'
        );
      }
    }

    function loadInto(el, src, alt, fallback) {
      return new Promise(function (resolve) {
        if (!el || !src) {
          resolve(false);
          return;
        }
        var done = false;
        var triedFallback = false;
        var safety = setTimeout(function () {
          finish(!!(el.naturalWidth > 0));
        }, 4500);
        function finish(ok) {
          if (done) return;
          done = true;
          clearTimeout(safety);
          el.onload = null;
          el.onerror = null;
          resolve(!!ok);
        }
        el.onload = function () {
          if (el.naturalWidth > 0) finish(true);
          else if (fallback && !triedFallback && fallback !== src) {
            triedFallback = true;
            el.src = fallback;
          } else finish(false);
        };
        el.onerror = function () {
          if (fallback && !triedFallback && fallback !== src) {
            triedFallback = true;
            el.src = fallback;
            return;
          }
          finish(false);
        };
        el.alt = alt || '';
        try {
          if (el.getAttribute('src') !== src) el.src = src;
          else if (el.complete && el.naturalWidth > 0) finish(true);
        } catch (eSrc) {
          el.src = src;
        }
        if (el.complete && el.naturalWidth > 0) finish(true);
      });
    }

    function upgradeToMuseum(el, it) {
      if (!el || !it) return;
      var hi = itemWalkUpgradeSrc(it);
      if (!hi) return;
      var probe = new Image();
      probe.onload = function () {
        if (!open || !el || !el.classList.contains('is-active')) return;
        if (probe.naturalWidth < 1) return;
        /* Swap only if still on the same work */
        var cur = currentItem();
        if (cur !== it) return;
        el.src = hi;
      };
      probe.onerror = function () {};
      probe.src = hi;
    }

    function clearTimer() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function setTimerAria(pct) {
      if (!timerBar) return;
      var n = Math.max(0, Math.min(100, Math.round(pct)));
      timerBar.setAttribute('aria-valuenow', String(n));
    }

    function stopProgressAnim() {
      if (!progress) return;
      var w = 0;
      try {
        var rect = progress.getBoundingClientRect();
        var parent = progress.parentElement
          ? progress.parentElement.getBoundingClientRect().width
          : 1;
        w = parent ? (100 * rect.width) / parent : 0;
      } catch (e) {}
      progress.style.transition = 'none';
      progress.style.width = w + '%';
      setTimerAria(w);
    }

    function startProgress(ms) {
      if (!progress) return;
      progress.style.transition = 'none';
      progress.style.width = '0%';
      setTimerAria(0);
      void progress.offsetWidth;
      if (playing) {
        progress.style.transition = 'width ' + ms + 'ms linear';
        progress.style.width = '100%';
        setTimerAria(100);
        /* Snap aria to complete when fill ends */
        clearTimeout(startProgress._ariaT);
        startProgress._ariaT = setTimeout(function () {
          if (playing && open) setTimerAria(100);
        }, ms);
      }
    }

    function armAdvance(ms) {
      clearTimer();
      if (!playing || !open) return;
      startedAt = Date.now();
      pausedLeft = 0;
      var hold = typeof ms === 'number' ? ms : HOLD_MS;
      startProgress(hold);
      timer = setTimeout(function () {
        if (playing && open) go(i + 1, 1);
      }, hold);
    }

    function clearImgSlideClasses(el) {
      if (!el) return;
      el.classList.remove(
        'is-active',
        'is-ken',
        'is-enter',
        'is-exit',
        'is-from-left',
        'is-from-right',
        'is-exit-left',
        'is-exit-right'
      );
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
      el.style.filter = '';
    }

    function releaseBusy(ms) {
      clearTimeout(releaseBusy._t);
      releaseBusy._t = setTimeout(function () {
        busy = false;
      }, typeof ms === 'number' ? ms : FADE_MS + 80);
    }

    /**
     * Core slide renderer — dual buffer, opacity crossfade only.
     * No Ken Burns, no lateral slide, no bloom. force=true bypasses busy.
     */
    function showSlide(index, dir, force) {
      if (!items.length) {
        try {
          collectItems(true);
        } catch (eC) {}
      }
      if (!items.length) return Promise.resolve();
      if (busy && !force) return Promise.resolve();
      busy = true;
      releaseBusy(FADE_MS + 900);

      i = ((index % items.length) + items.length) % items.length;
      var it = items[i];
      if (!it) {
        busy = false;
        return Promise.resolve();
      }

      syncWalkQualityAttr(it);
      var src = itemWalkSrc(it);
      var fallback = itemHyperspeedSrc(it);
      if (fallback === src) fallback = '';
      var alt = it.getAttribute('data-title') || '';
      var out = activeImg();
      var inn = idleImg() || activeImg() || imgA;

      setMeta(it);
      clearTimer();
      if (typeof resetCardTransform === 'function') resetCardTransform();

      if (!src) {
        busy = false;
        return Promise.resolve();
      }
      if (!inn) {
        busy = false;
        return Promise.resolve();
      }

      /* Same buffer already showing this src — just refresh meta */
      if (
        out &&
        out === inn &&
        out.getAttribute('src') === src &&
        out.naturalWidth > 0
      ) {
        out.classList.add('is-active', 'is-enter');
        applyDistance(feet, false);
        busy = false;
        if (playing && open) armAdvance(HOLD_MS);
        return Promise.resolve();
      }

      return loadInto(inn, src, alt, fallback).then(function (ok) {
        if (!open) {
          busy = false;
          return;
        }
        /* If load failed and we have a live outgoing image, keep it */
        if (!ok && out && out !== inn && out.naturalWidth > 0) {
          clearImgSlideClasses(inn);
          busy = false;
          if (playing && open) armAdvance(HOLD_MS);
          return;
        }

        clearImgSlideClasses(inn);
        if (out && out !== inn) {
          out.classList.remove('is-enter', 'is-ken');
          out.classList.add('is-exit');
          out.classList.remove('is-active');
        }
        inn.classList.add('is-active', 'is-enter');
        activeSlot = inn === imgB ? 'b' : 'a';

        applyDistance(feet, false);
        panX = 0;
        panY = 0;
        syncInspectTransform();

        /* Progressive museum upgrade (non-blocking) */
        try {
          upgradeToMuseum(inn, it);
        } catch (eUp) {}

        try {
          var id = it.getAttribute('data-id');
          if (id) {
            root.dispatchEvent(
              new CustomEvent('sg:walk-seen', { detail: { id: id, index: i } })
            );
          }
        } catch (e) {}

        setTimeout(function () {
          if (out && out !== inn) {
            clearImgSlideClasses(out);
          }
          busy = false;
          if (playing && open) armAdvance(HOLD_MS);
        }, FADE_MS + 40);
      });
    }

    function resetCardTransform() {
      var frame = walkFrameShell || walk.querySelector('.walk__frame');
      if (!frame) return;
      frame.classList.remove('is-swiping', 'is-flying', 'is-swipe-left', 'is-swipe-right');
      frame.style.transition = '';
      frame.style.transform = '';
      frame.style.opacity = '';
      var nope = walk.querySelector('[data-walk-stamp-nope]');
      var like = walk.querySelector('[data-walk-stamp-like]');
      if (nope) nope.style.opacity = '0';
      if (like) like.style.opacity = '0';
    }

    /**
     * Tinder fly-off then run callback (or go to next index).
     * dir: -1 left (nope) · +1 right (keep / next)
     */
    function flyThen(dir, then) {
      var frame = walkFrameShell || walk.querySelector('.walk__frame');
      if (!frame || reduce) {
        if (typeof then === 'function') then();
        return;
      }
      busy = true;
      var sign = dir < 0 ? -1 : 1;
      frame.classList.add('is-flying', sign < 0 ? 'is-swipe-left' : 'is-swipe-right');
      frame.style.transition =
        'transform 0.42s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.38s ease';
      frame.style.transform =
        'translate3d(' +
        sign * 118 +
        'vw, ' +
        14 +
        'vh, 0) rotate(' +
        sign * 26 +
        'deg) scale(0.92)';
      frame.style.opacity = '0';
      
      setTimeout(function () {
        resetCardTransform();
        busy = false;
        if (typeof then === 'function') then();
      }, reduce ? 40 : 430);
    }

    function go(n, dir) {
      /* No swipe fling — plain crossfade only */
      goPlain(n, dir || 1);
    }

    function goPlain(n, dir) {
      if (!items.length || !open) return;
      clearTimer();
      stopProgressAnim();
      busy = false;
      if (typeof clearPaintLayer === 'function') clearPaintLayer();
      resetCardTransform();
      showSlide(n, dir || 1, true);
    }

    /* ── Site themes in walkthrough (same as main site) ─────
       Dawn · Dusk · Mind · Wild — via SgSkin / data-skin-set.
       Place atmospheres removed. */
    var walkFrameShell = walk.querySelector('[data-walk-frame-shell]') || walk.querySelector('.walk__frame');
    var walkThemesRoot = walk.querySelector('[data-walk-themes]');
    var walkThemesToggle = walk.querySelector('[data-walk-themes-toggle]');
    var walkThemesPanel = walk.querySelector('[data-walk-themes-panel]');
    var walkSkinOpts = Array.prototype.slice.call(
      walk.querySelectorAll('[data-walk-skins] [data-skin-set], .walk__themes-list [data-skin-set]')
    );
    var themesOpen = false;
    var SKIN_LABELS = {
      light: 'Dawn',
      dark: 'Dusk',
      color: 'Mind',
      wild: 'Wild',
    };
    function currentSiteSkin() {
      try {
        if (window.SgSkin && typeof window.SgSkin.current === 'function') {
          return window.SgSkin.current();
        }
      } catch (eSk) {}
      return (
        document.documentElement.getAttribute('data-skin') ||
        'light'
      ).toLowerCase();
    }

    function applyWalkFrameForSkin() {
      /* No decorative frames — clean work only */
      if (!walkFrameShell) return;
      try {
        walkFrameShell.removeAttribute('data-frame');
        walkFrameShell.removeAttribute('data-atm-frame');
        walkFrameShell.className = 'walk__frame';
      } catch (eFr) {}
    }

    function paintWalkThemeOpts(skin) {
      skin = String(skin || currentSiteSkin()).toLowerCase();
      walkSkinOpts.forEach(function (btn) {
        var id = String(btn.getAttribute('data-skin-set') || '').toLowerCase();
        var on = id === skin;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (walkThemesToggle) {
        var labelEl = walkThemesToggle.querySelector('.walk__themes-toggle-label');
        var name = SKIN_LABELS[skin] || 'Themes';
        if (labelEl) {
          labelEl.textContent = themesOpen ? 'Themes' : 'Themes';
        }
        walkThemesToggle.setAttribute('title', 'Themes · ' + name);
        walkThemesToggle.setAttribute(
          'aria-label',
          themesOpen ? 'Hide themes' : 'Themes, ' + name
        );
      }
      try {
        walk.removeAttribute('data-walk-bg');
      } catch (eRmBg) {}
      applyWalkFrameForSkin();
    }

    function setThemesOpen(on) {
      themesOpen = !!on;
      if (themesOpen) {
        try {
          var ss = walk.querySelector('[data-walk-sound-sheet]');
          if (ss) {
            ss.hidden = true;
            ss.setAttribute('hidden', '');
          }
          walk.classList.remove('is-sound-sheet-open');
        } catch (eSs) {}
      }
      if (walkThemesToggle) {
        walkThemesToggle.setAttribute('aria-expanded', themesOpen ? 'true' : 'false');
      }
      if (walkThemesPanel) {
        if (themesOpen) {
          walkThemesPanel.hidden = false;
          walkThemesPanel.removeAttribute('hidden');
          try {
            if (typeof setHoldMenuOpen === 'function') setHoldMenuOpen(false);
          } catch (eHoldClose) {}
        } else {
          walkThemesPanel.hidden = true;
          walkThemesPanel.setAttribute('hidden', '');
        }
      }
      if (walkThemesRoot) walkThemesRoot.classList.toggle('is-open', themesOpen);
      paintWalkThemeOpts(currentSiteSkin());
    }

    function setWalkSiteTheme(skinId) {
      try {
        if (window.SgSkin && typeof window.SgSkin.apply === 'function') {
          window.SgSkin.apply(skinId, true);
        } else {
          document.documentElement.setAttribute('data-skin', skinId);
        }
      } catch (eApply) {
        try {
          document.documentElement.setAttribute('data-skin', skinId);
        } catch (e2) {}
      }
      paintWalkThemeOpts(skinId);
      setThemesOpen(false);
    }

    try {
      /* Clear any stored place-atmosphere from older theme versions */
      try {
        localStorage.removeItem('sg_walk_bg');
      } catch (eBgClear) {}
      walk.removeAttribute('data-walk-bg');

      if (walkThemesToggle) {
        on(walkThemesToggle, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setThemesOpen(!themesOpen);
        });
      }
      walkSkinOpts.forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setWalkSiteTheme(btn.getAttribute('data-skin-set'));
        });
      });
      on(document, 'sg:skinchange', function (ev) {
        var sk = (ev && ev.detail && ev.detail.skin) || currentSiteSkin();
        paintWalkThemeOpts(sk);
      });
      paintWalkThemeOpts(currentSiteSkin());
      setThemesOpen(false);
    } catch (eThemes) {
      console.warn('[gallery] walk themes init', eThemes);
    }

    /* ── Walk rooms, cinema chrome, and room-tone audio ───── */
    try { (function initWalkCinema() {
      var ROOM_KEY = 'sg_walk_room';
      var TONE_KEY = 'sg_walk_tone';
      var VOL_KEY = 'sg_walk_tone_vol';
      var TAB_KEY = 'sg_walk_sound_tab';
      var ROOMS = {
        cube: 'White Cube',
        vault: 'Night Vault',
        salon: 'Gold Salon',
        coast: 'Fog Coast',
        studio: 'Studio Hour',
        rose: 'Rose Night'
      };
      var roomBtns = Array.prototype.slice.call(walk.querySelectorAll('[data-walk-room-set]'));
      var roomLabel = walk.querySelector('[data-walk-room-label]');
      var soundToggle = walk.querySelector('[data-walk-sound-toggle]');
      var soundSheet = walk.querySelector('[data-walk-sound-sheet]');
      var soundLabel = walk.querySelector('[data-walk-sound-label]');
      var toneBtns = Array.prototype.slice.call(walk.querySelectorAll('[data-walk-tone]'));
      var volRange = walk.querySelector('[data-walk-tone-vol]');
      var soundTabs = Array.prototype.slice.call(walk.querySelectorAll('[data-walk-sound-tab]'));
      var soundPanes = Array.prototype.slice.call(walk.querySelectorAll('[data-walk-sound-pane]'));
      var keysHint = walk.querySelector('[data-walk-keys]');
      var soundOpen = false;
      var currentRoom = 'vault';
      var currentTone = 'off';
      var currentTab = 'tone';
      var currentVol = 0.38;
      var chromeTimer = 0;
      var keysTimer = 0;
      var toneEngine = null;

      try {
        var savedRoom = localStorage.getItem(ROOM_KEY);
        if (savedRoom && ROOMS[savedRoom]) currentRoom = savedRoom;
        var savedTone = localStorage.getItem(TONE_KEY);
        if (savedTone) currentTone = savedTone;
        var savedVol = parseInt(localStorage.getItem(VOL_KEY) || '', 10);
        if (!isNaN(savedVol)) currentVol = Math.max(0, Math.min(1, savedVol / 100));
        var savedTab = localStorage.getItem(TAB_KEY);
        if (savedTab === 'spotify' || savedTab === 'tone') currentTab = savedTab;
      } catch (eLs) {}

      function makeNoiseBuffer(ctx, seconds, color) {
        var len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
        var buf = ctx.createBuffer(1, len, ctx.sampleRate);
        var data = buf.getChannelData(0);
        var last = 0;
        for (var n = 0; n < len; n++) {
          var white = Math.random() * 2 - 1;
          if (color === 'brown') {
            last = (last + 0.02 * white) / 1.02;
            data[n] = last * 3.5;
          } else if (color === 'pink') {
            last = 0.98 * last + 0.02 * white;
            data[n] = last * 2.2;
          } else {
            data[n] = white;
          }
        }
        return buf;
      }

      function createToneEngine() {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        var ctx = new AC();
        var master = ctx.createGain();
        master.gain.value = currentVol;
        master.connect(ctx.destination);
        var nodes = [];
        var timers = [];
        var live = 'off';

        function clearNodes() {
          timers.forEach(function (id) {
            try { clearInterval(id); } catch (eT) {}
            try { clearTimeout(id); } catch (eT2) {}
          });
          timers = [];
          nodes.forEach(function (node) {
            try { node.stop(); } catch (eS) {}
            try { node.disconnect(); } catch (eD) {}
          });
          nodes = [];
        }

        function resume() {
          if (ctx.state === 'suspended') {
            try { ctx.resume(); } catch (eR) {}
          }
        }

        function loopBuffer(buf, opts) {
          opts = opts || {};
          var src = ctx.createBufferSource();
          src.buffer = buf;
          src.loop = true;
          var filter = ctx.createBiquadFilter();
          filter.type = opts.filter || 'lowpass';
          filter.frequency.value = opts.freq || 800;
          filter.Q.value = opts.q || 0.7;
          var g = ctx.createGain();
          g.gain.value = opts.gain != null ? opts.gain : 0.08;
          src.connect(filter);
          filter.connect(g);
          g.connect(master);
          src.start();
          nodes.push(src);
          return { src: src, filter: filter, gain: g };
        }

        function startHush() {
          loopBuffer(makeNoiseBuffer(ctx, 3, 'brown'), {
            filter: 'lowpass',
            freq: 280,
            q: 0.5,
            gain: 0.055
          });
          var pad = ctx.createOscillator();
          var pg = ctx.createGain();
          pad.type = 'sine';
          pad.frequency.value = 58;
          pg.gain.value = 0.018;
          pad.connect(pg);
          pg.connect(master);
          pad.start();
          nodes.push(pad);
        }

        function startRain() {
          loopBuffer(makeNoiseBuffer(ctx, 2.4, 'white'), {
            filter: 'lowpass',
            freq: 1400,
            q: 0.4,
            gain: 0.07
          });
          loopBuffer(makeNoiseBuffer(ctx, 1.6, 'pink'), {
            filter: 'highpass',
            freq: 1800,
            q: 0.3,
            gain: 0.025
          });
          var drip = setInterval(function () {
            if (live !== 'rain') return;
            resume();
            var osc = ctx.createOscillator();
            var g = ctx.createGain();
            var f = ctx.createBiquadFilter();
            var t0 = ctx.currentTime;
            osc.type = 'triangle';
            osc.frequency.setValueAtTime(980 + Math.random() * 700, t0);
            osc.frequency.exponentialRampToValueAtTime(220, t0 + 0.12);
            f.type = 'lowpass';
            f.frequency.value = 2400;
            g.gain.setValueAtTime(0.0001, t0);
            g.gain.exponentialRampToValueAtTime(0.035 + Math.random() * 0.03, t0 + 0.012);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
            osc.connect(f);
            f.connect(g);
            g.connect(master);
            osc.start(t0);
            osc.stop(t0 + 0.18);
          }, 180 + Math.random() * 220);
          timers.push(drip);
        }

        function startVinyl() {
          loopBuffer(makeNoiseBuffer(ctx, 2, 'pink'), {
            filter: 'lowpass',
            freq: 900,
            q: 0.4,
            gain: 0.018
          });
          var crackle = setInterval(function () {
            if (live !== 'vinyl') return;
            resume();
            var osc = ctx.createOscillator();
            var g = ctx.createGain();
            var t0 = ctx.currentTime;
            osc.type = 'square';
            osc.frequency.value = 1400 + Math.random() * 2400;
            g.gain.setValueAtTime(0.0001, t0);
            g.gain.exponentialRampToValueAtTime(0.028 + Math.random() * 0.04, t0 + 0.004);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.045);
            osc.connect(g);
            g.connect(master);
            osc.start(t0);
            osc.stop(t0 + 0.06);
          }, 90 + Math.random() * 240);
          timers.push(crackle);
        }

        function startNight() {
          var drone = ctx.createOscillator();
          var dg = ctx.createGain();
          drone.type = 'sine';
          drone.frequency.value = 73;
          dg.gain.value = 0.02;
          drone.connect(dg);
          dg.connect(master);
          drone.start();
          nodes.push(drone);
          var chirp = setInterval(function () {
            if (live !== 'night') return;
            resume();
            var osc = ctx.createOscillator();
            var g = ctx.createGain();
            var t0 = ctx.currentTime;
            osc.type = 'sine';
            osc.frequency.setValueAtTime(3400 + Math.random() * 900, t0);
            osc.frequency.exponentialRampToValueAtTime(2800, t0 + 0.08);
            g.gain.setValueAtTime(0.0001, t0);
            g.gain.exponentialRampToValueAtTime(0.012, t0 + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.11);
            osc.connect(g);
            g.connect(master);
            osc.start(t0);
            osc.stop(t0 + 0.13);
          }, 700 + Math.random() * 1600);
          timers.push(chirp);
        }

        function startWarm() {
          [110, 164.8, 220].forEach(function (freq, idx) {
            var osc = ctx.createOscillator();
            var g = ctx.createGain();
            osc.type = idx === 2 ? 'triangle' : 'sine';
            osc.frequency.value = freq;
            osc.detune.value = idx === 1 ? 6 : -4;
            g.gain.value = idx === 0 ? 0.03 : 0.012;
            osc.connect(g);
            g.connect(master);
            osc.start();
            nodes.push(osc);
          });
          loopBuffer(makeNoiseBuffer(ctx, 3, 'brown'), {
            filter: 'lowpass',
            freq: 220,
            gain: 0.012
          });
        }

        return {
          play: function (id) {
            resume();
            if (id === live && id !== 'off') return;
            clearNodes();
            live = id || 'off';
            if (live === 'hush') startHush();
            else if (live === 'rain') startRain();
            else if (live === 'vinyl') startVinyl();
            else if (live === 'night') startNight();
            else if (live === 'warm') startWarm();
            else live = 'off';
          },
          stop: function () {
            clearNodes();
            live = 'off';
          },
          setVolume: function (v) {
            currentVol = Math.max(0, Math.min(1, v));
            try {
              master.gain.setTargetAtTime(currentVol, ctx.currentTime, 0.05);
            } catch (eV) {
              master.gain.value = currentVol;
            }
          },
          current: function () {
            return live;
          },
          resume: resume
        };
      }

      function ensureTone() {
        if (!toneEngine) toneEngine = createToneEngine();
        return toneEngine;
      }

      function paintRooms() {
        try {
          walk.setAttribute('data-walk-room', currentRoom);
        } catch (eR) {}
        roomBtns.forEach(function (btn) {
          var id = btn.getAttribute('data-walk-room-set');
          var on = id === currentRoom;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (roomLabel) roomLabel.textContent = ROOMS[currentRoom] || 'Room';
        if (walkThemesToggle) {
          walkThemesToggle.setAttribute('title', 'Room · ' + (ROOMS[currentRoom] || 'Room'));
          walkThemesToggle.setAttribute(
            'aria-label',
            themesOpen ? 'Hide rooms' : 'Change the room, ' + (ROOMS[currentRoom] || '')
          );
        }
      }

      function setRoom(id, persist) {
        if (!ROOMS[id]) id = 'vault';
        currentRoom = id;
        paintRooms();
        if (persist !== false) {
          try { localStorage.setItem(ROOM_KEY, currentRoom); } catch (eS) {}
        }
      }

      function paintTones() {
        toneBtns.forEach(function (btn) {
          var id = btn.getAttribute('data-walk-tone');
          var on = id === currentTone;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        walk.classList.toggle('is-tone-on', currentTone !== 'off');
        if (soundLabel) {
          soundLabel.textContent =
            currentTone === 'off'
              ? (window.SgMusic && window.SgMusic.isPlaying && window.SgMusic.isPlaying()
                  ? 'Playing'
                  : 'Sound')
              : 'Tone';
        }
        if (soundToggle) {
          soundToggle.classList.toggle('is-live', currentTone !== 'off' || !!(window.SgMusic && window.SgMusic.isPlaying && window.SgMusic.isPlaying()));
        }
      }

      function setTone(id, persist) {
        currentTone = id || 'off';
        var eng = ensureTone();
        if (eng) {
          if (currentTone === 'off') eng.stop();
          else {
            eng.play(currentTone);
            try {
              if (window.SgMusic && typeof window.SgMusic.pause === 'function') window.SgMusic.pause();
            } catch (ePause) {}
          }
        }
        paintTones();
        if (persist !== false) {
          try { localStorage.setItem(TONE_KEY, currentTone); } catch (eT) {}
        }
      }

      function setSoundTab(tab) {
        currentTab = tab === 'spotify' ? 'spotify' : 'tone';
        soundTabs.forEach(function (btn) {
          var on = btn.getAttribute('data-walk-sound-tab') === currentTab;
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        soundPanes.forEach(function (pane) {
          var on = pane.getAttribute('data-walk-sound-pane') === currentTab;
          pane.classList.toggle('is-active', on);
          if (on) {
            pane.hidden = false;
            pane.removeAttribute('hidden');
          } else {
            pane.hidden = true;
            pane.setAttribute('hidden', '');
          }
        });
        try { localStorage.setItem(TAB_KEY, currentTab); } catch (eTb) {}
      }

      function setSoundOpen(on) {
        soundOpen = !!on;
        if (soundToggle) soundToggle.setAttribute('aria-expanded', soundOpen ? 'true' : 'false');
        if (soundSheet) {
          if (soundOpen) {
            soundSheet.hidden = false;
            soundSheet.removeAttribute('hidden');
            try { if (typeof setThemesOpen === 'function') setThemesOpen(false); } catch (eTh) {}
            try { if (typeof setHoldMenuOpen === 'function') setHoldMenuOpen(false); } catch (eH) {}
          } else {
            soundSheet.hidden = true;
            soundSheet.setAttribute('hidden', '');
          }
        }
        walk.classList.toggle('is-sound-sheet-open', soundOpen);
        pokeChrome();
      }

      var _setThemesOpen = setThemesOpen;
      setThemesOpen = function (on) {
        if (on) setSoundOpen(false);
        _setThemesOpen(on);
        pokeChrome();
      };

      function pokeChrome() {
        walk.classList.remove('is-chrome-idle');
        if (chromeTimer) clearTimeout(chromeTimer);
        if (!open || dockHidden || soundOpen || themesOpen) return;
        chromeTimer = setTimeout(function () {
          if (!open || dockHidden || soundOpen || themesOpen) return;
          walk.classList.add('is-chrome-idle');
        }, 3200);
      }

      function flashKeys() {
        if (!keysHint) return;
        keysHint.classList.add('is-on');
        if (keysTimer) clearTimeout(keysTimer);
        keysTimer = setTimeout(function () {
          keysHint.classList.remove('is-on');
        }, 4200);
      }

      roomBtns.forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setRoom(btn.getAttribute('data-walk-room-set'), true);
          setThemesOpen(false);
        });
      });

      if (soundToggle) {
        on(soundToggle, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setSoundOpen(!soundOpen);
        });
      }
      toneBtns.forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setTone(btn.getAttribute('data-walk-tone'), true);
        });
      });
      soundTabs.forEach(function (btn) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setSoundTab(btn.getAttribute('data-walk-sound-tab'));
        });
      });
      if (volRange) {
        volRange.value = String(Math.round(currentVol * 100));
        on(volRange, 'input', function () {
          var v = parseInt(volRange.value || '38', 10) / 100;
          var eng = ensureTone();
          if (eng) eng.setVolume(v);
          try { localStorage.setItem(VOL_KEY, String(Math.round(v * 100))); } catch (eV) {}
        });
        on(volRange, 'pointerdown', function (e) { e.stopPropagation(); });
      }

      on(walk, 'pointermove', pokeChrome, { passive: true });
      on(walk, 'pointerdown', pokeChrome, { passive: true });
      on(walk, 'keydown', pokeChrome);

      walk.querySelectorAll('[data-walk-playlist]').forEach(function (a) {
        var url = a.getAttribute('href') || '';
        if (!url || url.charAt(0) === '#') return;
        var cover = a.querySelector('[data-walk-playlist-cover]');
        var titleEl = a.querySelector('[data-walk-playlist-title]');
        fetch('https://open.spotify.com/oembed?url=' + encodeURIComponent(url.split('?')[0]), {
          mode: 'cors',
          credentials: 'omit'
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (!j) return;
            if (cover && j.thumbnail_url) {
              cover.src = j.thumbnail_url;
              cover.alt = j.title || '';
            }
            if (titleEl && j.title) titleEl.textContent = j.title;
          })
          .catch(function () {});
      });

      setRoom(currentRoom, false);
      setSoundTab(currentTab);
      paintTones();
      if (volRange) volRange.value = String(Math.round(currentVol * 100));
      setSoundOpen(false);

      walk._sgCinema = {
        poke: pokeChrome,
        flashKeys: flashKeys,
        startTone: function () {
          if (currentTone && currentTone !== 'off') setTone(currentTone, false);
        },
        stopTone: function () {
          currentTone = 'off';
          if (toneEngine) toneEngine.stop();
          paintTones();
        },
        closeSheets: function () {
          setSoundOpen(false);
        }
      };
    })(); } catch (eCinema) {
      console.warn('[gallery] walk cinema init', eCinema);
    }

    /* ── Oil paint over the work (buttery brush) ──────────── */
    var paintRoot = walk.querySelector('[data-walk-paint]');
    var paintToggle = walk.querySelector('[data-walk-paint-toggle]');
    var paintPanel = walk.querySelector('[data-walk-paint-panel]');
    var paintHideBtn = walk.querySelector('[data-walk-paint-ui-hide]');
    var paintShowBtn = walk.querySelector('[data-walk-paint-ui-show]');
    var paintCanvas = walk.querySelector('[data-walk-paint-canvas]');
    var paintSize = walk.querySelector('[data-walk-paint-size]');
    var paintSizeVal = walk.querySelector('[data-walk-paint-size-val]');
    var paintClear = walk.querySelector('[data-walk-paint-clear]');
    var paintDone = walk.querySelector('[data-walk-paint-done]');
    var paintCtx = null;
    var paintOn = false;
    var painting = false;
    var paintDirty = false;
    var paintColor = '#c23b2a';
    var brushSize = 28;
    var lastPt = null;
    var lastT = 0;

    function parseHexColor(hex) {
      var h = String(hex || '#c23b2a').replace('#', '');
      if (h.length === 3) {
        h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      }
      var n = parseInt(h, 16);
      if (isNaN(n)) return { r: 194, g: 59, b: 42 };
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    }

    function clampByte(n) {
      return Math.max(0, Math.min(255, Math.round(n)));
    }

    function resizePaintCanvas() {
      if (!paintCanvas) return;
      var frame = walkFrameShell || walk.querySelector('.walk__frame');
      if (!frame) return;

      /*
        Frame uses transform: scale() for distance. Canvas is a child of the
        frame, so size it in pre-transform layout space only (clientWidth /
        clientHeight). Never use getBoundingClientRect for canvas size —
        that is post-scale and left a gap at the bottom of every piece.

        Cover the FULL frame. The art always sits inside the frame, so this
        guarantees every piece is paintable edge-to-edge (including bottom).
      */
      var w = Math.max(1, Math.ceil(frame.clientWidth || frame.offsetWidth || 1));
      var h = Math.max(1, Math.ceil(frame.clientHeight || frame.offsetHeight || 1));

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var same =
        paintCanvas._cssW === w &&
        paintCanvas._cssH === h &&
        paintCtx;

      paintCanvas.style.position = 'absolute';
      paintCanvas.style.left = '0px';
      paintCanvas.style.top = '0px';
      paintCanvas.style.right = 'auto';
      paintCanvas.style.bottom = 'auto';
      paintCanvas.style.width = w + 'px';
      paintCanvas.style.height = h + 'px';
      paintCanvas.style.inset = 'auto';
      paintCanvas.style.transform = 'none';
      paintCanvas.style.maxWidth = 'none';
      paintCanvas.style.maxHeight = 'none';
      paintCanvas.style.boxSizing = 'border-box';

      if (same) return;

      var prev = null;
      if (paintCtx && paintCanvas.width && paintCanvas.height) {
        try {
          prev = paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height);
        } catch (e) {
          prev = null;
        }
      }
      paintCanvas._cssW = w;
      paintCanvas._cssH = h;
      paintCanvas._cssL = 0;
      paintCanvas._cssT = 0;
      paintCanvas.width = Math.round(w * dpr);
      paintCanvas.height = Math.round(h * dpr);
      paintCtx = paintCanvas.getContext('2d', { willReadFrequently: false });
      if (paintCtx) {
        paintCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        paintCtx.lineCap = 'round';
        paintCtx.lineJoin = 'round';
        if (prev && prev.width && prev.height) {
          try {
            var tmp = document.createElement('canvas');
            tmp.width = prev.width;
            tmp.height = prev.height;
            var tctx = tmp.getContext('2d');
            tctx.putImageData(prev, 0, 0);
            paintCtx.drawImage(tmp, 0, 0, w, h);
          } catch (e2) {}
        }
      }
    }

    function clearPaintLayer() {
      if (paintCtx && paintCanvas) {
        paintCtx.save();
        paintCtx.setTransform(1, 0, 0, 1, 0, 0);
        paintCtx.clearRect(0, 0, paintCanvas.width, paintCanvas.height);
        paintCtx.restore();
      }
      paintDirty = false;
      if (paintCanvas && !paintOn) {
        paintCanvas.hidden = true;
        paintCanvas.setAttribute('hidden', '');
        paintCanvas.setAttribute('aria-hidden', 'true');
      }
    }

    var paintUiHidden = false;

    function setPaintUiHidden(hidden) {
      paintUiHidden = !!hidden && paintOn;
      walk.classList.toggle('is-paint-ui-hidden', paintUiHidden);
      if (paintHideBtn) {
        if (paintOn && !paintUiHidden) {
          paintHideBtn.hidden = false;
          paintHideBtn.removeAttribute('hidden');
        } else {
          paintHideBtn.hidden = true;
          paintHideBtn.setAttribute('hidden', '');
        }
      }
      if (paintShowBtn) {
        if (paintUiHidden) {
          paintShowBtn.hidden = false;
          paintShowBtn.removeAttribute('hidden');
        } else {
          paintShowBtn.hidden = true;
          paintShowBtn.setAttribute('hidden', '');
        }
      }
      if (paintPanel) {
        if (paintOn && !paintUiHidden) {
          paintPanel.hidden = false;
          paintPanel.removeAttribute('hidden');
        } else {
          paintPanel.hidden = true;
          paintPanel.setAttribute('hidden', '');
        }
      }
    }

    function setPaintMode(on) {
      paintOn = !!on;
      walk.classList.toggle('is-painting', paintOn);
      if (!paintOn) paintUiHidden = false;
      if (paintToggle) {
        paintToggle.setAttribute('aria-expanded', paintOn && !paintUiHidden ? 'true' : 'false');
        paintToggle.textContent = paintOn ? 'Painting' : 'Paint';
        paintToggle.classList.toggle('is-on', paintOn);
      }
      setPaintUiHidden(paintOn ? paintUiHidden : false);
      if (paintCanvas) {
        if (paintOn || paintDirty) {
          paintCanvas.hidden = false;
          paintCanvas.removeAttribute('hidden');
          paintCanvas.setAttribute('aria-hidden', paintOn ? 'false' : 'true');
          if (paintOn) resizePaintCanvas();
        } else {
          paintCanvas.hidden = true;
          paintCanvas.setAttribute('hidden', '');
          paintCanvas.setAttribute('aria-hidden', 'true');
        }
      }
      if (paintOn) {
        /* Hold the view while painting */
        if (playing) togglePlay();
        /* Image may still be settling — size after layout */
        requestAnimationFrame(function () {
          resizePaintCanvas();
          requestAnimationFrame(resizePaintCanvas);
        });
        setTimeout(resizePaintCanvas, 120);
        setTimeout(resizePaintCanvas, 400);
      }
    }

    if (paintHideBtn) {
      on(paintHideBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (paintOn) setPaintUiHidden(true);
      });
    }
    if (paintShowBtn) {
      on(paintShowBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (paintOn) setPaintUiHidden(false);
      });
    }

    /* ── Hide / show bottom control console ─────────────────── */
    var dockHideBtn = walk.querySelector('[data-walk-dock-hide]');
    var dockShowBtn = walk.querySelector('[data-walk-dock-show]');
    var DOCK_STORAGE = 'sg_walk_dock_hidden';
    var dockHidden = false;
    try {
      dockHidden = localStorage.getItem(DOCK_STORAGE) === '1';
    } catch (eDockLs) {
      dockHidden = false;
    }

    function setDockHidden(hidden, persist) {
      dockHidden = !!hidden;
      walk.classList.toggle('is-dock-hidden', dockHidden);
      if (dockHideBtn) {
        if (dockHidden) {
          dockHideBtn.hidden = true;
          dockHideBtn.setAttribute('hidden', '');
        } else {
          dockHideBtn.hidden = false;
          dockHideBtn.removeAttribute('hidden');
        }
      }
      if (dockShowBtn) {
        if (dockHidden) {
          dockShowBtn.hidden = false;
          dockShowBtn.removeAttribute('hidden');
        } else {
          dockShowBtn.hidden = true;
          dockShowBtn.setAttribute('hidden', '');
        }
      }
      /* Collapse expanded panels when hiding the dock */
      if (dockHidden) {
        try {
          if (typeof setThemesOpen === 'function') setThemesOpen(false);
        } catch (eTh) {}
        try {
          if (walk._sgCinema) walk._sgCinema.closeSheets();
        } catch (eSnd) {}
        try {
          if (paintOn && typeof setPaintUiHidden === 'function') setPaintUiHidden(true);
          else if (paintPanel) {
            paintPanel.hidden = true;
            paintPanel.setAttribute('hidden', '');
            if (paintToggle) paintToggle.setAttribute('aria-expanded', 'false');
          }
        } catch (ePn) {}
      }
      if (persist !== false) {
        try {
          localStorage.setItem(DOCK_STORAGE, dockHidden ? '1' : '0');
        } catch (eDockSave) {}
      }
    }

    if (dockHideBtn) {
      on(dockHideBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDockHidden(true);
      });
    }
    if (dockShowBtn) {
      on(dockShowBtn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDockHidden(false);
      });
    }
    /* Apply saved preference once shell is live */
    setDockHidden(dockHidden, false);

    function canvasPoint(e) {
      if (!paintCanvas) return null;
      var rect = paintCanvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      var cx = e.clientX;
      var cy = e.clientY;
      if (e.touches && e.touches[0]) {
        cx = e.touches[0].clientX;
        cy = e.touches[0].clientY;
      } else if (e.changedTouches && e.changedTouches[0]) {
        cx = e.changedTouches[0].clientX;
        cy = e.changedTouches[0].clientY;
      }
      var cssW = paintCanvas._cssW || rect.width;
      var cssH = paintCanvas._cssH || rect.height;
      return {
        x: ((cx - rect.left) / rect.width) * cssW,
        y: ((cy - rect.top) / rect.height) * cssH,
      };
    }

    /**
     * Wet oil stamp — sharper blobs, wet sheen, ink-like spatters.
     * Avoid soft radial fade-to-zero (that read as blurry airbrush).
     */
    function oilStamp(x, y, size, pressure) {
      if (!paintCtx) return;
      paintDirty = true;
      var col = parseHexColor(paintColor);
      var p = Math.max(0.25, Math.min(1.4, pressure || 1));
      var blobs = Math.max(4, Math.min(11, Math.floor(size * 0.28)));
      paintCtx.save();
      paintCtx.globalCompositeOperation = 'source-over';

      /* Core wet mass — solid, crisp edges */
      for (var i = 0; i < blobs; i++) {
        var ox = (Math.random() - 0.5) * size * 0.55;
        var oy = (Math.random() - 0.5) * size * 0.55;
        var s = size * (0.18 + Math.random() * 0.42) * (0.8 + p * 0.28);
        var dr = (Math.random() - 0.5) * 14;
        var dg = (Math.random() - 0.5) * 12;
        var db = (Math.random() - 0.5) * 12;
        var r = clampByte(col.r + dr + 3);
        var g = clampByte(col.g + dg + 1);
        var b = clampByte(col.b + db - 1);
        /* Higher alpha = wetter, less airbrush haze */
        var a = (0.42 + Math.random() * 0.38) * Math.min(1.15, p);
        var cx = x + ox;
        var cy = y + oy;
        var rx = s * (0.75 + Math.random() * 0.45);
        var ry = s * (0.55 + Math.random() * 0.55);
        var rot = Math.random() * Math.PI;

        paintCtx.fillStyle = 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
        paintCtx.beginPath();
        paintCtx.ellipse(cx, cy, rx, ry, rot, 0, Math.PI * 2);
        paintCtx.fill();

        /* Tight wet rim (hard edge, not a soft bloom) */
        if (Math.random() > 0.4) {
          paintCtx.strokeStyle =
            'rgba(' +
            clampByte(r - 18) +
            ',' +
            clampByte(g - 14) +
            ',' +
            clampByte(b - 10) +
            ',' +
            a * 0.55 +
            ')';
          paintCtx.lineWidth = Math.max(0.6, s * 0.06);
          paintCtx.stroke();
        }

        /* Wet highlight — small hard glint, not a soft glow */
        if (Math.random() > 0.55) {
          paintCtx.fillStyle =
            'rgba(255,252,240,' + (0.12 + Math.random() * 0.18) * p + ')';
          paintCtx.beginPath();
          paintCtx.ellipse(
            cx - rx * 0.22,
            cy - ry * 0.28,
            rx * (0.12 + Math.random() * 0.12),
            ry * (0.08 + Math.random() * 0.1),
            rot * 0.5,
            0,
            Math.PI * 2
          );
          paintCtx.fill();
        }
      }

      /* Spatter / drip satellites — the wet flick */
      var spatters = Math.floor(2 + Math.random() * 5 + p * 3);
      for (var j = 0; j < spatters; j++) {
        var ang = Math.random() * Math.PI * 2;
        var dist = size * (0.35 + Math.random() * 1.15);
        /* Bias a few drops "down" like wet gravity */
        if (Math.random() > 0.55) {
          ang = Math.PI * 0.5 + (Math.random() - 0.5) * 0.9;
          dist = size * (0.4 + Math.random() * 1.4);
        }
        var sx = x + Math.cos(ang) * dist;
        var sy = y + Math.sin(ang) * dist;
        var ss = size * (0.03 + Math.random() * 0.12) * (0.7 + p * 0.4);
        var sa = (0.5 + Math.random() * 0.45) * Math.min(1.1, p);
        var sr = clampByte(col.r + (Math.random() - 0.5) * 18);
        var sg = clampByte(col.g + (Math.random() - 0.5) * 14);
        var sb = clampByte(col.b + (Math.random() - 0.5) * 14);
        paintCtx.fillStyle = 'rgba(' + sr + ',' + sg + ',' + sb + ',' + sa + ')';
        paintCtx.beginPath();
        if (Math.random() > 0.65) {
          /* Tear-shaped drip */
          var dRot = ang + Math.PI * 0.5;
          paintCtx.ellipse(sx, sy, ss * (0.55 + Math.random() * 0.4), ss * (1.1 + Math.random() * 1.4), dRot, 0, Math.PI * 2);
        } else {
          paintCtx.arc(sx, sy, ss, 0, Math.PI * 2);
        }
        paintCtx.fill();
      }

      /* Occasional hard micro-flecks (dry fleck in wet paint) */
      if (p > 0.55 && Math.random() > 0.45) {
        var flecks = 1 + Math.floor(Math.random() * 4);
        for (var k = 0; k < flecks; k++) {
          var fx = x + (Math.random() - 0.5) * size * 1.1;
          var fy = y + (Math.random() - 0.5) * size * 1.1;
          paintCtx.fillStyle =
            'rgba(' +
            clampByte(col.r + 30) +
            ',' +
            clampByte(col.g + 20) +
            ',' +
            clampByte(col.b + 10) +
            ',' +
            (0.35 + Math.random() * 0.4) +
            ')';
          paintCtx.fillRect(fx, fy, 0.8 + Math.random() * 1.6, 0.8 + Math.random() * 1.6);
        }
      }

      paintCtx.restore();
    }

    function paintStroke(from, to, pressure) {
      if (!from || !to) {
        if (to) oilStamp(to.x, to.y, brushSize, pressure);
        return;
      }
      var dx = to.x - from.x;
      var dy = to.y - from.y;
      var dist = Math.sqrt(dx * dx + dy * dy) || 0;
      /* Slightly looser spacing so blobs stay discrete (less mushy blur) */
      var step = Math.max(1.6, brushSize * 0.22);
      var n = Math.max(1, Math.ceil(dist / step));
      for (var i = 0; i <= n; i++) {
        var t = i / n;
        var fall = 0.9 + 0.15 * Math.sin(t * Math.PI);
        /* Jitter path for wet drag */
        var jx = (Math.random() - 0.5) * brushSize * 0.08;
        var jy = (Math.random() - 0.5) * brushSize * 0.08;
        oilStamp(from.x + dx * t + jx, from.y + dy * t + jy, brushSize * fall, pressure);
        /* Extra spatters when moving fast */
        if (dist > brushSize * 0.8 && Math.random() > 0.72) {
          var side = (Math.random() > 0.5 ? 1 : -1) * brushSize * (0.4 + Math.random() * 0.7);
          oilStamp(
            from.x + dx * t + (dy / (dist || 1)) * side * 0.35,
            from.y + dy * t - (dx / (dist || 1)) * side * 0.35,
            brushSize * (0.35 + Math.random() * 0.35),
            pressure * 0.75
          );
        }
      }
    }

    function pressureFromEvent(e, pt) {
      var now = performance.now ? performance.now() : Date.now();
      var p = 1;
      if (e.pressure && e.pressure > 0) p = 0.45 + e.pressure * 1.1;
      else if (lastPt && lastT) {
        var dt = Math.max(8, now - lastT);
        var d = Math.sqrt(Math.pow(pt.x - lastPt.x, 2) + Math.pow(pt.y - lastPt.y, 2));
        var speed = d / dt;
        /* Slower = more paint laid down */
        p = Math.max(0.35, Math.min(1.35, 1.15 - speed * 0.9));
      }
      lastT = now;
      return p;
    }

    function onPaintDown(e) {
      if (!paintOn || !paintCanvas) return;
      e.preventDefault();
      e.stopPropagation();
      painting = true;
      resizePaintCanvas();
      var pt = canvasPoint(e);
      lastPt = pt;
      lastT = performance.now ? performance.now() : Date.now();
      if (pt) oilStamp(pt.x, pt.y, brushSize, pressureFromEvent(e, pt));
    }

    function onPaintMove(e) {
      if (!painting || !paintOn) return;
      e.preventDefault();
      var pt = canvasPoint(e);
      if (!pt) return;
      var pr = pressureFromEvent(e, pt);
      paintStroke(lastPt, pt, pr);
      lastPt = pt;
    }

    function onPaintUp(e) {
      if (!painting) return;
      if (e) e.preventDefault();
      painting = false;
      lastPt = null;
    }

    if (paintToggle) {
      on(paintToggle, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setPaintMode(!paintOn);
      });
    }
    if (paintDone) {
      on(paintDone, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setPaintMode(false);
      });
    }
    if (paintClear) {
      on(paintClear, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearPaintLayer();
      });
    }
    if (paintSize) {
      on(paintSize, 'input', function () {
        brushSize = parseInt(paintSize.value, 10) || 28;
        if (paintSizeVal) paintSizeVal.textContent = String(brushSize);
      });
    }
    walk.querySelectorAll('[data-walk-paint-color]').forEach(function (sw) {
      on(sw, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        paintColor = sw.getAttribute('data-walk-paint-color') || paintColor;
        walk.querySelectorAll('[data-walk-paint-color]').forEach(function (s) {
          var onSw = s === sw;
          s.classList.toggle('is-active', onSw);
          s.setAttribute('aria-pressed', onSw ? 'true' : 'false');
        });
      });
    });
    if (paintCanvas) {
      on(paintCanvas, 'pointerdown', onPaintDown);
      on(paintCanvas, 'pointermove', onPaintMove);
      on(paintCanvas, 'pointerup', onPaintUp);
      on(paintCanvas, 'pointercancel', onPaintUp);
      on(paintCanvas, 'pointerleave', onPaintUp);
      /* Touch */
      on(paintCanvas, 'touchstart', onPaintDown, { passive: false });
      on(paintCanvas, 'touchmove', onPaintMove, { passive: false });
      on(paintCanvas, 'touchend', onPaintUp);
    }
    on(window, 'resize', function () {
      if (open && paintOn) resizePaintCanvas();
    });

    function mountWalk() {
      // Escape section stacking / overflow traps
      if (walk.parentNode !== document.body) {
        document.body.appendChild(walk);
      }
    }

    function unmountWalk() {
      if (homeParent && walk.parentNode === document.body) {
        try {
          homeParent.appendChild(walk);
        } catch (e) {}
      }
    }

    function resolveStartIndex(start, opts) {
      opts = opts || {};
      /* Prefer full catalogue when opening a specific painting so filters don't hide it */
      var pin =
        (start && start.nodeType === 1) ||
        (typeof start === 'string' && start) ||
        (opts && (opts.item || opts.id || opts.slug || opts.src));
      collectItems(!!pin);
      if (!items.length) {
        collectItems(true);
      }
      if (!items.length) return 0;
      if (typeof start === 'number' && !isNaN(start)) {
        return Math.max(0, Math.min(items.length - 1, start));
      }

      function matchItem(it, want) {
        if (!it || !want) return false;
        if (want.nodeType === 1) {
          if (it === want) return true;
          var aId = want.getAttribute('data-id');
          var aSlug = want.getAttribute('data-slug');
          var aSrc = itemSrc(want);
          var bId = it.getAttribute('data-id');
          var bSlug = it.getAttribute('data-slug');
          var bSrc = itemSrc(it);
          if (aId && bId && aId === bId) return true;
          if (aSlug && bSlug && aSlug === bSlug) return true;
          if (aSrc && bSrc && aSrc === bSrc) return true;
          /* Filename leaf match (CDN vs asset URL variants) */
          if (aSrc && bSrc) {
            var leafA = aSrc.split('?')[0].split('/').pop();
            var leafB = bSrc.split('?')[0].split('/').pop();
            if (leafA && leafB && leafA === leafB) return true;
          }
          return false;
        }
        if (typeof want === 'string') {
          var k = want.toLowerCase();
          var slug = (it.getAttribute('data-slug') || '').toLowerCase();
          var id = (it.getAttribute('data-id') || '').toLowerCase();
          var title = (it.getAttribute('data-title') || '').toLowerCase();
          return (
            slug === k ||
            id === k ||
            id === 'sig-' + k ||
            title === k ||
            title.replace(/\s+/g, '-') === k
          );
        }
        return false;
      }

      /* Element / node from the wall */
      if (start && start.nodeType === 1) {
        for (var j = 0; j < items.length; j++) {
          if (matchItem(items[j], start)) return j;
        }
      }
      /* opts identity keys from openWalkForItem */
      if (opts.item && opts.item.nodeType === 1) {
        for (var j2 = 0; j2 < items.length; j2++) {
          if (matchItem(items[j2], opts.item)) return j2;
        }
      }
      if (opts.id || opts.slug || opts.src) {
        for (var j3 = 0; j3 < items.length; j3++) {
          var el = items[j3];
          if (opts.id && el.getAttribute('data-id') === opts.id) return j3;
          if (opts.slug && el.getAttribute('data-slug') === opts.slug) return j3;
          if (opts.src && itemSrc(el) === opts.src) return j3;
          if (opts.src) {
            var leafO = opts.src.split('?')[0].split('/').pop();
            var leafE = itemSrc(el).split('?')[0].split('/').pop();
            if (leafO && leafE && leafO === leafE) return j3;
          }
        }
      }
      /* String id/slug */
      if (typeof start === 'string' && start) {
        for (var t = 0; t < items.length; t++) {
          if (matchItem(items[t], start)) return t;
        }
      }
      return 0;
    }

    function publishWalkApi() {
      /* Publish ASAP so card/CTA clicks never hit a dead stub if later init throws */
      root._sgWalk = {
        open: openWalk,
        close: closeWalk,
        go: go,
        goPlain: goPlain,
        flyThen: flyThen,
        resolveIndex: resolveStartIndex,
        ready: true,
        openRandom: function () {
          try {
            collectItems(true);
          } catch (eC) {}
          if (!items.length) {
            console.warn('[gallery] Walk openRandom: empty wall');
            return false;
          }
          var pick = items[Math.floor(Math.random() * items.length)];
          if (!pick) return false;
          return openWalk(pick, {
            skipIntro: true,
            pinItem: true,
            item: pick,
            id: pick.getAttribute('data-id') || '',
            slug: pick.getAttribute('data-slug') || '',
            src: itemSrc(pick),
          });
        },
      };
      try {
        window.sgOpenWalk = function (start, opts) {
          return openWalk(start, opts || { skipIntro: true });
        };
        window.sgOpenRandomWalk = function () {
          if (root._sgWalk && typeof root._sgWalk.openRandom === 'function') {
            return root._sgWalk.openRandom();
          }
          if (typeof root._sgOpenRandom === 'function') return root._sgOpenRandom();
          return false;
        };
      } catch (eWin) {}
    }

    function openWalk(startIndex, opts) {
      opts = opts || {};
      /* Walkthrough uses museum WEBP; wall grid stays on JPEGs */
      walkQuality = 'museum';
      try {
        walk.setAttribute('data-walk-quality', 'museum');
      } catch (eOq) {}
      try {
        /* Clear any stuck wall drag locks that block card clicks next time */
        root.querySelectorAll('[data-gx-wall-viewport]').forEach(function (vp) {
          vp.classList.remove('is-dragging', 'is-panning', 'is-pan-lock', 'is-coasting');
        });
      } catch (eLock) {}

      try {
        /* Always resolve against full set when targeting a specific work */
        var startKey = startIndex;
        /* Prefer a concrete item/slug/id over a bare 0 when both are provided */
        if (opts.item && opts.item.nodeType === 1) {
          if (
            startIndex == null ||
            startIndex === 0 ||
            typeof startIndex === 'number' ||
            (startIndex && startIndex.nodeType === 1)
          ) {
            /* Element start OR pin open — land on the provided painting */
            if (
              opts.pinItem ||
              startIndex == null ||
              startIndex === 0 ||
              (startIndex && startIndex.nodeType === 1 && opts.item === startIndex)
            ) {
              startKey = opts.item;
            }
          }
        }
        if (startKey && startKey.nodeType === 1) {
          /* element path — keep as-is */
        } else if (typeof startIndex === 'string' && startIndex) {
          startKey = startIndex;
        } else if (startIndex && startIndex.nodeType === 1) {
          startKey = startIndex;
        }

        i = resolveStartIndex(startKey, opts);
        if (!items.length) {
          collectItems(true);
        }
        if (!items.length) {
          console.warn('[gallery] Walkthrough: no works found on the wall');
          return false;
        }
        /* Clamp index after final collect */
        if (i < 0 || i >= items.length) i = 0;

        mountWalk();
        open = true;
        playing = true;
        busy = false;
        clearTimer();
        /* Re-resolve after mount in case DOM shifted */
        i = resolveStartIndex(startKey, opts);
        if (i < 0 || i >= items.length) i = 0;

        walk.hidden = false;
        walk.removeAttribute('hidden');
        try {
          walk.style.display = '';
          walk.style.opacity = '';
          walk.style.visibility = 'visible';
          walk.style.pointerEvents = 'auto';
        } catch (eSt) {}
        walk.setAttribute('aria-hidden', 'false');
        document.body.classList.add('walk-open');
        root.classList.add('is-live');
        if (typeof paintWalkPlayBtn === 'function') paintWalkPlayBtn();
        else if (playBtn) {
          playBtn.classList.toggle('is-playing', !!playing);
          playBtn.setAttribute('aria-label', playing ? 'Pause autoplay' : 'Play autoplay');
          playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
        }

        /* Keep buffers — only reset classes. Blanking src caused empty stage flicker. */
        [imgA, imgB].forEach(function (el) {
          if (!el) return;
          if (typeof clearImgSlideClasses === 'function') clearImgSlideClasses(el);
        });
        activeSlot = 'a';
        try {
          if (typeof resetCardTransform === 'function') resetCardTransform();
        } catch (eRt) {}

        try {
          applyDistance(24, false);
        } catch (eAd) {}
        if (printBadge) {
          printBadge.hidden = true;
          printBadge.setAttribute('hidden', '');
        }
        try {
          if (typeof setPaintMode === 'function') setPaintMode(false);
        } catch (ePm) {}
        try {
          if (typeof clearPaintLayer === 'function') clearPaintLayer();
        } catch (eCl) {}
        try {
          /* Restore dock visibility preference when re-opening */
          if (typeof setDockHidden === 'function') {
            var preferHidden = false;
            try {
              preferHidden = localStorage.getItem('sg_walk_dock_hidden') === '1';
            } catch (eDh) {}
            setDockHidden(preferHidden, false);
          }
        } catch (eDockOpen) {}

        /* Reveal immediately — don't wait a frame or the stage stays blank */
        walk.classList.add('is-ready');
        requestAnimationFrame(function () {
          walk.classList.add('is-ready');
        });

        var skipIntro = opts.skipIntro !== false; /* default skip for instant open */
        if (reduce) skipIntro = true;
        if (intro && !skipIntro) {
          intro.classList.add('is-on');
          intro.setAttribute('aria-hidden', 'false');
          setTimeout(function () {
            intro.classList.remove('is-on');
            intro.setAttribute('aria-hidden', 'true');
          }, 1100);
          setTimeout(function () {
            try {
              showSlide(i, 1, true);
            } catch (eSs) {
              console.warn('[gallery] showSlide failed', eSs);
            }
          }, 420);
        } else {
          if (intro) {
            intro.classList.remove('is-on');
            intro.setAttribute('aria-hidden', 'true');
          }
          try {
            showSlide(i, 1, true);
          } catch (eSs2) {
            console.warn('[gallery] showSlide failed', eSs2);
          }
        }
        try {
          if (walk._sgCinema) {
            walk._sgCinema.flashKeys();
            walk._sgCinema.poke();
            walk._sgCinema.startTone();
          }
        } catch (eCin) {}
        return true;
      } catch (eOpen) {
        console.warn('[gallery] openWalk failed', eOpen);
        try {
          /* Last-ditch: still show the shell so the CTA never feels dead */
          mountWalk();
          walk.hidden = false;
          walk.removeAttribute('hidden');
          walk.classList.add('is-ready');
          document.body.classList.add('walk-open');
          open = true;
        } catch (eShell) {}
        return false;
      }
    }

    function closeWalk() {
      if (!open && walk.hidden) return;
      open = false;
      playing = false;
      busy = false;
      clearTimer();
      try {
        if (typeof setPaintMode === 'function') setPaintMode(false);
      } catch (ePm2) {}
      try {
        if (typeof clearPaintLayer === 'function') clearPaintLayer();
      } catch (eCl2) {}
      try {
        if (typeof resetCardTransform === 'function') resetCardTransform();
      } catch (eRt2) {}
      try {
        resetInspect(false);
      } catch (eRi) {}
      walk.classList.remove(
        'is-ready',
        'is-painting',
        'is-dock-hidden',
        'is-paint-ui-hidden',
        'is-inspect-pan',
        'is-inspect-scrub',
        'is-panning',
        'is-distance-drag',
        'is-swipe-nav',
        'is-chrome-idle',
        'is-sound-sheet-open',
        'is-tone-on'
      );
      try {
        if (walk._sgCinema) {
          walk._sgCinema.stopTone();
          walk._sgCinema.closeSheets();
        }
      } catch (eCinClose) {}
      document.body.classList.remove('walk-open');
      if (progress) {
        progress.style.transition = 'none';
        progress.style.width = '0%';
      }
      setTimerAria(0);
      if (intro) {
        intro.classList.remove('is-on');
        intro.setAttribute('aria-hidden', 'true');
      }
      setTimeout(function () {
        walk.hidden = true;
        walk.setAttribute('aria-hidden', 'true');
        [imgA, imgB].forEach(function (el) {
          if (!el) return;
          el.classList.remove('is-active', 'is-ken', 'is-enter', 'is-exit');
        });
        unmountWalk();
      }, 380);
    }

    /* Live API available before paint/atmosphere wiring — CTAs must not wait */
    publishWalkApi();

    /* Custom event open — register once, early */
    if (!root._sgWalkEventBound) {
      root._sgWalkEventBound = true;
      root.addEventListener('sg:open-walkthrough', function (ev) {
        var start = 0;
        var opts = { skipIntro: true };
        if (ev && ev.detail) {
          if (typeof ev.detail.index === 'number') start = ev.detail.index;
          else if (ev.detail.item) start = ev.detail.item;
          else if (ev.detail.slug) start = ev.detail.slug;
          else if (ev.detail.id) start = ev.detail.id;
          if (ev.detail.item) opts.item = ev.detail.item;
          if (ev.detail.id) opts.id = ev.detail.id;
          if (ev.detail.slug) opts.slug = ev.detail.slug;
          if (ev.detail.src) opts.src = ev.detail.src;
          if (ev.detail.pinItem) opts.pinItem = true;
          if (ev.detail.skipIntro === false) opts.skipIntro = false;
        }
        try {
          openWalk(start, opts);
        } catch (eEvOpen) {
          console.warn('[gallery] sg:open-walkthrough failed', eEvOpen);
        }
      });
    }

    function paintWalkPlayBtn() {
      if (!playBtn) return;
      playBtn.classList.toggle('is-playing', !!playing);
      playBtn.setAttribute('aria-label', playing ? 'Pause autoplay' : 'Play autoplay');
      playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
      playBtn.setAttribute('title', playing ? 'Pause autoplay' : 'Play autoplay');
    }

    function togglePlay() {
      if (!open) return;
      playing = !playing;
      paintWalkPlayBtn();
      if (playing) {
        var left = pausedLeft > 200 ? pausedLeft : HOLD_MS;
        pausedLeft = 0;
        armAdvance(left);
      } else {
        clearTimer();
        var elapsed = Date.now() - startedAt;
        pausedLeft = Math.max(400, HOLD_MS - elapsed);
        stopProgressAnim();
      }
    }

    // CTA buttons opened via capture handler + _sgWalk (avoids double-bind)
    if (printBadge) {
      on(printBadge, 'click', function (e) {
        /* Let the link navigate — stop stage edge-nav from eating the click */
        e.stopPropagation();
      });
    }

    walk.querySelectorAll('[data-walk-close]').forEach(function (b) {
      on(b, 'click', closeWalk);
    });
    walk.querySelectorAll('[data-walk-prev]').forEach(function (b) {
      on(b, 'click', function () {
        goPlain(i - 1, -1);
      });
    });
    walk.querySelectorAll('[data-walk-next]').forEach(function (b) {
      on(b, 'click', function () {
        goPlain(i + 1, 1);
      });
    });
    if (playBtn) on(playBtn, 'click', togglePlay);

    /* Timer dropdown · 2s / 5s / 10s / 30s */
    paintHoldButtons();
    (function initHoldMenu() {
      var holdToggle = walk.querySelector('[data-walk-hold-toggle]');
      var holdPanel = walk.querySelector('[data-walk-hold-panel]');
      var holdRoot = walk.querySelector('[data-walk-hold]');
      if (holdToggle) {
        on(holdToggle, 'click', function (e) {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          var openMenu = holdToggle.getAttribute('aria-expanded') === 'true';
          setHoldMenuOpen(!openMenu);
          /* Close themes if open */
          try {
            if (!openMenu && typeof setThemesOpen === 'function') setThemesOpen(false);
          } catch (eTh) {}
        });
      }
      walk.querySelectorAll('[data-walk-hold-sec]').forEach(function (btn) {
        on(btn, 'click', function (e) {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          setHoldSec(btn.getAttribute('data-walk-hold-sec'), true);
        });
      });
      /* Click outside closes timer menu */
      on(document, 'pointerdown', function (e) {
        if (!holdRoot || !holdRoot.classList.contains('is-open')) return;
        if (e.target && holdRoot.contains(e.target)) return;
        setHoldMenuOpen(false);
      });
      on(document, 'keydown', function (e) {
        if (!e || e.key !== 'Escape') return;
        if (!holdRoot || !holdRoot.classList.contains('is-open')) return;
        setHoldMenuOpen(false);
      });
      if (holdPanel) {
        on(holdPanel, 'pointerdown', function (e) {
          if (e) e.stopPropagation();
        });
      }
    })();

    /* Museum is always used in walkthrough — no load button */

    if (distRange) {
      on(distRange, 'input', function () {
        applyDistance(distRange.value, true);
      });
      on(distRange, 'change', function () {
        applyDistance(distRange.value, true);
      });
    }
    applyDistance(feet, false);
    syncInspectTransform();

    /*
      Scroll / trackpad while walkthrough is open → ft distance dial
      (scroll up = closer / grow · scroll down = farther / shrink)
      Range: 1ft (close) → 100ft (far). No separate detail rail.
    */
    (function initWalkScrollDistance() {
      var accum = 0;
      var STEP_PX = 48; /* wheel pixels ≈ one ft step */
      var FT_PER_STEP = 2;
      on(
        walk,
        'wheel',
        function (e) {
          if (!open || paintOn) return;
          /* Don’t steal wheel from open panels / range sliders */
          var t = e.target;
          if (
            t &&
            t.closest &&
            t.closest(
              'input, textarea, select, .walk__themes-panel, .walk__paint-panel, [data-walk-themes-panel], [data-walk-paint-panel]'
            )
          ) {
            return;
          }
          if (e.cancelable) e.preventDefault();
          /* Normalize: line/page modes → pixel-ish delta */
          var dy = e.deltaY;
          if (e.deltaMode === 1) dy *= 16;
          else if (e.deltaMode === 2) dy *= 48;

          accum += dy;
          while (Math.abs(accum) >= STEP_PX) {
            var dir = accum > 0 ? 1 : -1;
            accum -= dir * STEP_PX;
            /* positive deltaY (scroll down) → farther = larger ft = smaller piece */
            applyDistance(feet + dir * FT_PER_STEP, true);
          }
        },
        { passive: false }
      );
    })();

    /*
      Stage gestures:
      · Click / tap on or around the art → next work
      · Vertical drag → distance (ft) dial
      · Mobile / tablet: horizontal swipe → next (←) or previous (→)
      · Prev/next buttons + keyboard still work
      Disabled while oil-paint mode is active.
    */
    (function initDistanceGesture() {
      var active = false;
      var mode = null; /* null | 'distance' | 'nav' | 'pan' */
      var startX = 0;
      var startY = 0;
      var startFeet = feet;
      var startPanX = 0;
      var startPanY = 0;
      var pointerId = null;
      var pointerType = '';
      var skipClick = false; /* suppress synthetic click after pointer advance */
      var PX_FULL = 260;
      var CLICK_SLOP = 12; /* px — under this = click, not drag */
      var SWIPE_MIN = 48; /* px — horizontal swipe to change work */
      var CHROME =
        'button, a, input, label, select, textarea, ' +
        '.walk__chrome, .walk__paint, .walk__themes, .walk__tools, ' +
        '.walk__console, .walk__navbtn, .walk__cta, .walk__print, .walk__paint-canvas, ' +
        '.walk__bottom-dock, [data-walk-bottom-dock], [data-walk-console], ' +
        '[data-walk-paint], [data-walk-themes], [data-walk-tools], [data-walk-print], ' +
        '[data-walk-commission], [data-walk-ask], [data-walk-hold], .walk__hold, [data-walk-prev], [data-walk-next], ' +
        '[data-walk-inspect], .walk__inspect';

      function swipeNavAllowed(type) {
        /* Phones, tablets, coarse pointers — and any real touch */
        if (type === 'touch' || type === 'pen') return true;
        if (touch || narrow) return true;
        if (document.body.classList.contains('is-touch') || document.body.classList.contains('is-narrow')) {
          return true;
        }
        try {
          if (window.matchMedia('(max-width: 1024px) and (pointer: coarse)').matches) return true;
          if (window.matchMedia('(max-width: 1024px) and (hover: none)').matches) return true;
        } catch (eM) {}
        return false;
      }

      function onStage(t) {
        return !!(t && t.closest && t.closest('.walk__stage, .walk__frame, .walk__img, [data-walk-plate]'));
      }
      function isChrome(t) {
        return !!(t && t.closest && t.closest(CHROME));
      }
      function isInspectZoomed() {
        return walkQuality === 'museum' && inspectScaleNow() > 1.08;
      }
      function markSkipClick() {
        skipClick = true;
        setTimeout(function () {
          skipClick = false;
        }, 120);
      }
      function advanceFromArtClick() {
        if (!open || paintOn) return;
        /* When deeply zoomed, click does not advance — keep studying the surface */
        if (isInspectZoomed()) return;
        goPlain(i + 1, 1);
        markSkipClick();
      }
      function endDrag(e) {
        if (!active) return;
        var gesture = mode;
        var sx = startX;
        var sy = startY;
        active = false;
        mode = null;
        pointerId = null;
        walk.classList.remove('is-distance-drag', 'is-swipe-nav', 'is-panning');
        try {
          if (stage.releasePointerCapture && e && e.pointerId != null) {
            stage.releasePointerCapture(e.pointerId);
          }
        } catch (errR) {}

        if (!open || paintOn) return;

        var cx = e && e.clientX != null ? e.clientX : sx;
        var cy = e && e.clientY != null ? e.clientY : sy;
        var dx = cx - sx;
        var dy = cy - sy;

        /* Horizontal swipe on mobile/tablet → prev / next */
        if (gesture === 'nav') {
          if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 0.85) {
            if (dx < 0) goPlain(i + 1, 1); /* swipe left → next */
            else goPlain(i - 1, -1); /* swipe right → previous */
            markSkipClick();
          }
          return;
        }

        /* Vertical distance drag / pan — nothing on release */
        if (gesture === 'distance' || gesture === 'pan') return;

        /* Tap / click on or around the art → next image */
        if (Math.hypot(dx, dy) <= CLICK_SLOP) {
          advanceFromArtClick();
        }
      }

      on(stage, 'pointerdown', function (e) {
        if (!open || paintOn) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (isChrome(e.target) || !onStage(e.target)) return;
        active = true;
        mode = null;
        pointerType = e.pointerType || '';
        pointerId = e.pointerId != null ? e.pointerId : 1;
        startX = e.clientX;
        startY = e.clientY;
        startFeet = feet;
        startPanX = panX;
        startPanY = panY;
      });

      on(
        stage,
        'pointermove',
        function (e) {
          if (!active || !open || paintOn) return;
          if (pointerId != null && e.pointerId != null && e.pointerId !== pointerId) return;
          var dx = e.clientX - startX;
          var dy = e.clientY - startY;
          var dist = Math.hypot(dx, dy);

          if (!mode) {
            if (dist < CLICK_SLOP) return;
            var absX = Math.abs(dx);
            var absY = Math.abs(dy);
            /* Museum close-up → drag pans the surface */
            if (isInspectZoomed()) {
              mode = 'pan';
              walk.classList.add('is-panning');
              try {
                if (stage.setPointerCapture && e.pointerId != null) {
                  stage.setPointerCapture(e.pointerId);
                }
              } catch (errPan) {}
            } else if (swipeNavAllowed(pointerType) && absX > absY * 1.1) {
              /* Prefer horizontal swipe for nav on touch / tablet */
              mode = 'nav';
              walk.classList.add('is-swipe-nav');
              try {
                if (stage.setPointerCapture && e.pointerId != null) {
                  stage.setPointerCapture(e.pointerId);
                }
              } catch (errCap) {}
            } else if (absY >= absX) {
              mode = 'distance';
              walk.classList.add('is-distance-drag');
              try {
                if (stage.setPointerCapture && e.pointerId != null) {
                  stage.setPointerCapture(e.pointerId);
                }
              } catch (errCap2) {}
            } else {
              /* Desktop horizontal — ignore (click still works on up) */
              return;
            }
          }

          if (mode === 'pan') {
            panX = startPanX + dx;
            panY = startPanY + dy;
            syncInspectTransform();
            pauseWalkForUserView();
            if (e.cancelable) e.preventDefault();
          } else if (mode === 'distance') {
            var deltaFt = (dy / PX_FULL) * (FEET_MAX - FEET_MIN);
            applyDistance(startFeet + deltaFt, true);
            if (e.cancelable) e.preventDefault();
          } else if (mode === 'nav') {
            /* Keep the page from scrolling sideways while swiping works */
            if (e.cancelable) e.preventDefault();
          }
        },
        { passive: false }
      );

      on(stage, 'pointerup', endDrag);
      on(stage, 'pointercancel', endDrag);
      on(stage, 'lostpointercapture', endDrag);

      /* Fallback click if pointer path didn't run (some devices / accessibility) */
      on(stage, 'click', function (e) {
        if (skipClick) return;
        if (!open || paintOn) return;
        if (isChrome(e.target)) return;
        if (!onStage(e.target)) return;
        advanceFromArtClick();
      });
    })();

    on(document, 'keydown', function (e) {
      if (!open || walk.hidden) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeWalk();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goPlain(i + 1, 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPlain(i - 1, -1);
      } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        applyDistance(feet - 3, true);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        applyDistance(feet + 3, true);
      } else if (e.key === '+' || e.key === '=' || e.key === 'Add') {
        e.preventDefault();
        applyDistance(feet - (e.shiftKey ? 10 : 4), true);
      } else if (e.key === '-' || e.key === '_' || e.key === 'Subtract') {
        e.preventDefault();
        applyDistance(feet + (e.shiftKey ? 10 : 4), true);
      }
    });

    if (location.hash === '#walkthrough') {
      setTimeout(function () {
        openWalk(0, { skipIntro: true });
      }, 500);
    }

    /* Re-publish after paint/atmosphere so openRandom uses latest closures */
    publishWalkApi();
  });


  /* ── About story journey (true/false + XP + polaroids) ─── */
  document.querySelectorAll('[data-story-journey]').forEach(function (root) {
    var STORAGE = 'sg_story_v2';
    var STORAGE_LEGACY = 'sg_story_v1';
    var RANKS = [
      { min: 0, name: 'Witness' },
      { min: 80, name: 'Companion' },
      { min: 160, name: 'Confidant' },
      { min: 240, name: 'Insider' },
      { min: 320, name: 'Kin' },
    ];
    var BADGES = {
      first_true: 'First true call',
      streak3: 'Hot streak ×3',
      half_story: 'Halfway human',
      full_story: 'Full arc',
      perfect: 'Perfect 6/6',
      free_walk: 'Free walker',
    };

    function blankState() {
      return {
        started: false,
        current: 0,
        unlocked: 0,
        score: 0,
        xp: 0,
        streak: 0,
        bestStreak: 0,
        answered: {},
        skipped: false,
        badges: {},
        photos: {},
      };
    }

    /* Always open About as a clean slate — Play wipes any leftover memory */
    var state = blankState();

    var chapters = Array.prototype.slice.call(root.querySelectorAll('[data-sj-chapter]'));
    var welcome = root.querySelector('[data-sj-welcome]');
    var hud = root.querySelector('[data-sj-hud]');
    var life = root.querySelector('[data-sj-life]');
    var close = root.querySelector('[data-sj-close]');
    var toast = root.querySelector('[data-sj-toast]');
    var badgesEl = root.querySelector('[data-sj-badges]');
    var badgeRow = root.querySelector('[data-sj-badge-row]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-sj-dot]'));
    var polaroids = Array.prototype.slice.call(root.querySelectorAll('[data-sj-polaroid]'));
    var TOTAL = chapters.length;

    function storyKeys() {
      var keys = [STORAGE, STORAGE_LEGACY, storageKey(STORAGE), storageKey(STORAGE_LEGACY)];
      // Also clear any customer-scoped variants that may linger
      try {
        for (var i = 0; i < localStorage.length; i++) {
          var k = localStorage.key(i);
          if (k && (k.indexOf('sg_story_v') === 0 || k.indexOf('sg_story_v') > -1)) {
            if (keys.indexOf(k) < 0) keys.push(k);
          }
        }
      } catch (e) {}
      return keys;
    }

    function persist() {
      try {
        writeJSON(STORAGE, state);
        localStorage.setItem(STORAGE, JSON.stringify(state));
      } catch (e) {}
    }

    function clearStoryMemory() {
      storyKeys().forEach(function (k) {
        try {
          localStorage.removeItem(k);
        } catch (e) {}
      });
    }

    function rankName() {
      var r = RANKS[0].name;
      for (var i = 0; i < RANKS.length; i++) {
        if (state.xp >= RANKS[i].min) r = RANKS[i].name;
      }
      return r;
    }

    function showToast(msg) {
      if (!toast) return;
      toast.hidden = false;
      toast.textContent = msg;
      toast.classList.add('is-on');
      clearTimeout(showToast._t);
      showToast._t = setTimeout(function () {
        toast.classList.remove('is-on');
        setTimeout(function () {
          toast.hidden = true;
        }, 280);
      }, 2200);
    }

    function earnBadge(key) {
      if (state.badges[key]) return;
      state.badges[key] = true;
      showToast('🏅 ' + (BADGES[key] || key));
    }

    function photoCount() {
      return Object.keys(state.photos).length;
    }

    function unlockPhoto(key) {
      if (!key || state.photos[key]) return;
      state.photos[key] = true;
      if (photoCount() >= 3) earnBadge('half_story');
    }

    function paintBadges() {
      if (!badgeRow) return;
      var keys = Object.keys(state.badges);
      if (!keys.length) {
        if (badgesEl) badgesEl.hidden = true;
        return;
      }
      if (badgesEl) badgesEl.hidden = false;
      badgeRow.innerHTML = keys
        .map(function (k) {
          return '<span class="sj-badge">' + (BADGES[k] || k) + '</span>';
        })
        .join('');
    }

    function paintPolaroids() {
      polaroids.forEach(function (card) {
        var key = card.getAttribute('data-photo');
        var open = state.skipped || !!state.photos[key] || (key && key.indexOf('bonus') === 0 && state.unlocked >= TOTAL);
        card.classList.toggle('is-locked', !open);
        card.classList.toggle('is-unlocked', open);
      });
    }

    function updateHUD() {
      var el;
      if ((el = root.querySelector('[data-sj-rank]'))) el.textContent = rankName();
      if ((el = root.querySelector('[data-sj-xp]'))) el.textContent = String(state.xp);
      if ((el = root.querySelector('[data-sj-score]'))) el.textContent = String(state.score);
      if ((el = root.querySelector('[data-sj-score-max]'))) el.textContent = String(TOTAL);
      if ((el = root.querySelector('[data-sj-chapter]')))
        el.textContent = String(Math.min(state.unlocked, TOTAL));
      if ((el = root.querySelector('[data-sj-photos]'))) el.textContent = String(photoCount());
      if ((el = root.querySelector('[data-sj-bar]')))
        el.style.width = ((Math.min(state.unlocked, TOTAL) / TOTAL) * 100).toFixed(1) + '%';
      var streakEl = root.querySelector('[data-sj-streak]');
      var streakN = root.querySelector('[data-sj-streak-n]');
      if (streakEl) {
        streakEl.hidden = state.streak < 2;
        if (streakN) streakN.textContent = String(state.streak);
      }
      dots.forEach(function (d, idx) {
        d.classList.toggle('is-done', idx < state.unlocked);
        d.classList.toggle('is-active', idx === state.current && state.started && !state.skipped);
        d.classList.toggle('is-open', idx <= state.unlocked || state.skipped);
      });
      paintBadges();
      paintPolaroids();
    }

    function paintChapters() {
      chapters.forEach(function (ch, idx) {
        var id = ch.getAttribute('data-id');
        var answered = !!state.answered[id] || state.skipped;
        var current = state.started && idx === state.current && !state.skipped && !answered;
        var done = answered || (state.skipped && idx < TOTAL);
        ch.classList.toggle('is-locked', !current && !done && !state.skipped);
        ch.classList.toggle('is-current', current);
        ch.classList.toggle('is-done', done && !current);
        ch.classList.toggle('is-revealed', done);
        var lock = ch.querySelector('[data-sj-lock]');
        var rev = ch.querySelector('[data-sj-reveal]');
        if (lock) lock.hidden = done;
        if (rev) rev.hidden = !done;
        if (done && state.answered[id]) {
          var v = ch.querySelector('[data-sj-verdict]');
          var note = ch.querySelector('[data-sj-xp-note]');
          if (v) {
            var ok = !!state.answered[id].correct;
            var sk = !!state.answered[id].skipped;
            v.textContent = sk
              ? 'Free walk · chapter open'
              : ok
                ? 'True call · +' + (state.answered[id].xp || 40) + ' XP'
                : 'Plot twist — story unlocked · +' + (state.answered[id].xp || 15) + ' XP';
            v.classList.toggle('is-correct', ok && !sk);
            v.classList.toggle('is-miss', !ok || sk);
          }
          if (note && state.answered[id].xp) {
            note.textContent =
              '+' +
              state.answered[id].xp +
              ' XP' +
              (state.answered[id].correct ? ' · true call bonus' : '') +
              (state.answered[id].streakBonus ? ' · streak bonus' : '');
          }
        }
        if (done) unlockPhoto(ch.getAttribute('data-photo'));
      });
      if (state.unlocked >= TOTAL || state.skipped) {
        unlockPhoto('bonus');
        unlockPhoto('bonus2');
        earnBadge('full_story');
      }
      if (life) life.hidden = !(state.unlocked >= 1 || state.skipped);
      if (close) {
        var showClose = state.unlocked >= TOTAL || state.skipped;
        close.hidden = !showClose;
        if (showClose) {
          var r = root.querySelector('[data-sj-close-rank]');
          var s = root.querySelector('[data-sj-close-score]');
          var x = root.querySelector('[data-sj-close-xp]');
          var t = root.querySelector('[data-sj-close-true]');
          var p = root.querySelector('[data-sj-close-photos]');
          var st = root.querySelector('[data-sj-close-streak]');
          var burst = root.querySelector('[data-sj-burst]');
          if (r) r.textContent = rankName();
          if (s)
            s.textContent =
              state.skipped && state.score === 0
                ? 'Full story revealed · free walk'
                : state.score + ' of ' + TOTAL + ' true · Rank ' + rankName();
          if (x) x.textContent = state.xp + ' story XP earned';
          if (t) t.textContent = String(state.score);
          if (p) p.textContent = String(photoCount());
          if (st) st.textContent = String(state.bestStreak);
          if (burst) burst.classList.toggle('is-on', state.score >= TOTAL || state.xp >= 240);
          if (state.score >= TOTAL) earnBadge('perfect');
        }
      }
      updateHUD();
    }

    function hardResetUI() {
      chapters.forEach(function (ch) {
        ch.querySelectorAll('[data-sj-answer]').forEach(function (btn) {
          btn.disabled = false;
          btn.classList.remove('is-right', 'is-wrong');
        });
        var note = ch.querySelector('[data-sj-xp-note]');
        if (note) note.textContent = '';
        var v = ch.querySelector('[data-sj-verdict]');
        if (v) {
          v.textContent = '';
          v.classList.remove('is-correct', 'is-miss');
        }
      });
      if (welcome) {
        welcome.hidden = true;
        welcome.classList.remove('is-done');
      }
      if (life) life.hidden = true;
      if (close) close.hidden = true;
      if (badgesEl) badgesEl.hidden = true;
      if (badgeRow) badgeRow.innerHTML = '';
    }

    /** Always start from chapter 1 — wipe memory so anyone can replay */
    function startFresh() {
      clearStoryMemory();
      state = blankState();
      hardResetUI();
      state.started = true;
      state.current = 0;
      state.unlocked = 0;
      if (hud) hud.hidden = false;
      paintChapters();
      persist();
      scrollToChapter(0);
      showToast('Story reset · start from the beginning');
    }

    function start() {
      /* Play always restarts clean — no resume from account/local memory */
      startFresh();
    }

    function skipAll() {
      clearStoryMemory();
      state = blankState();
      hardResetUI();
      state.started = true;
      state.skipped = true;
      state.unlocked = TOTAL;
      state.xp = 30;
      earnBadge('free_walk');
      chapters.forEach(function (ch) {
        var id = ch.getAttribute('data-id');
        state.answered[id] = { correct: false, skipped: true, xp: 0 };
        unlockPhoto(ch.getAttribute('data-photo'));
      });
      unlockPhoto('bonus');
      unlockPhoto('bonus2');
      if (hud) hud.hidden = false;
      paintChapters();
      persist();
      showToast('Full story open — you can replay anytime');
      if (chapters[0]) chapters[0].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }

    function scrollToChapter(idx) {
      var ch = chapters[idx];
      if (ch) ch.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    }

    function answer(ch, correct, btn) {
      var id = ch.getAttribute('data-id');
      if (state.answered[id] && !state.answered[id].skipped) return;
      var idx = parseInt(ch.getAttribute('data-index') || '0', 10);
      var gain = correct ? 40 : 15;
      var streakBonus = 0;
      if (correct) {
        state.score += 1;
        state.streak += 1;
        state.bestStreak = Math.max(state.bestStreak, state.streak);
        if (state.streak >= 2) {
          streakBonus = Math.min(20, state.streak * 5);
          gain += streakBonus;
        }
        if (state.score === 1) earnBadge('first_true');
        if (state.streak >= 3) earnBadge('streak3');
      } else {
        state.streak = 0;
      }
      state.xp += gain;
      state.answered[id] = {
        correct: !!correct,
        t: Date.now(),
        xp: gain,
        streakBonus: streakBonus > 0,
      };
      state.unlocked = Math.max(state.unlocked, idx + 1);
      unlockPhoto(ch.getAttribute('data-photo'));
      ch.querySelectorAll('[data-sj-answer]').forEach(function (b) {
        b.disabled = true;
        var isC = b.getAttribute('data-correct') === 'true';
        b.classList.toggle('is-right', isC);
        if (b === btn && !correct) b.classList.add('is-wrong');
      });
      setTimeout(function () {
        paintChapters();
        persist();
        showToast(
          correct
            ? 'True · +' + gain + ' XP' + (streakBonus ? ' · streak!' : '')
            : 'Plot twist · +' + gain + ' XP · chapter open'
        );
      }, 280);
    }

    function continueFrom(ch) {
      var idx = parseInt(ch.getAttribute('data-index') || '0', 10);
      if (idx >= TOTAL - 1) {
        state.unlocked = TOTAL;
        paintChapters();
        persist();
        if (close) close.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        showToast('Journey complete · rank ' + rankName());
        return;
      }
      state.current = idx + 1;
      state.unlocked = Math.max(state.unlocked, state.current);
      paintChapters();
      persist();
      scrollToChapter(state.current);
    }

    function resetJourney() {
      clearStoryMemory();
      state = blankState();
      hardResetUI();
      if (hud) hud.hidden = true;
      paintChapters();
      persist(); // write blank so account memory is overwritten clean
      clearStoryMemory(); // then wipe keys so next visit is truly fresh
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      showToast('Story cleared · press Play to begin again');
    }

    /** Reset + immediately start chapter 1 (Replay) */
    function replayJourney() {
      startFresh();
    }

    root.querySelectorAll('[data-sj-start]').forEach(function (b) {
      on(b, 'click', start);
    });
    root.querySelectorAll('[data-sj-skip]').forEach(function (b) {
      on(b, 'click', skipAll);
    });
    root.querySelectorAll('[data-sj-restart]').forEach(function (b) {
      on(b, 'click', replayJourney);
    });
    root.querySelectorAll('[data-sj-reset]').forEach(function (b) {
      on(b, 'click', resetJourney);
    });

    chapters.forEach(function (ch) {
      ch.querySelectorAll('[data-sj-answer]').forEach(function (btn) {
        on(btn, 'click', function () {
          if (!state.started) startFresh();
          var correct = btn.getAttribute('data-correct') === 'true';
          answer(ch, correct, btn);
        });
      });
      var cont = ch.querySelector('[data-sj-continue], [data-sj-finish]');
      if (cont)
        on(cont, 'click', function () {
          continueFrom(ch);
        });
    });

    /* Do not auto-resume mid-story — always land ready to play again */
    paintChapters();
    if (hud) hud.hidden = true;
    if (life) life.hidden = true;
    if (close) close.hidden = true;
  });


  /* ── About story · true / false (no XP) ────────────────── */
  document.querySelectorAll('[data-about-story]').forEach(function (root) {
    /* Immersive open: smooth enter + gentle orbit parallax */
    var openRoot = root.querySelector('[data-about-open]');
    if (openRoot) {
      root.querySelectorAll('[data-about-scroll]').forEach(function (a) {
        on(a, 'click', function (e) {
          var href = a.getAttribute('href') || '';
          if (href.charAt(0) !== '#') return;
          var target = document.querySelector(href);
          if (!target) return;
          e.preventDefault();
          try {
            target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
          } catch (errAbout) {
            target.scrollIntoView(true);
          }
        });
      });
      if (!reduce && fine && !touch) {
        var sats = openRoot.querySelectorAll('.about-open__sat');
        var fig = openRoot.querySelector('.about-open__figure');
        if (sats.length || fig) {
          on(window, 'scroll', function () {
            var rect = openRoot.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > window.innerHeight) return;
            var p = Math.max(0, Math.min(1, -rect.top / Math.max(rect.height, 1)));
            sats.forEach(function (sat, idx) {
              var dir = idx % 2 === 0 ? -1 : 1;
              sat.style.transform =
                'rotate(var(--r, 0deg)) translate3d(0,' + (p * dir * 28).toFixed(1) + 'px,0)';
            });
            if (fig) {
              fig.style.transform = 'translate3d(0,' + (p * 36).toFixed(1) + 'px,0)';
            }
          }, { passive: true });
        }
      }
    }

    root.querySelectorAll('[data-about-item]').forEach(function (item) {
      var correct = String(item.getAttribute('data-correct') || '').toLowerCase() === 'true';
      var choices = item.querySelector('[data-about-choices]');
      var result = item.querySelector('[data-about-result]');
      var verdict = item.querySelector('[data-about-verdict]');
      if (!choices) return;

      choices.querySelectorAll('[data-about-answer]').forEach(function (btn) {
        on(btn, 'click', function () {
          if (item.classList.contains('is-answered')) return;
          var picked = String(btn.getAttribute('data-about-answer') || '').toLowerCase() === 'true';
          var isCorrect = picked === correct;

          item.classList.add('is-answered');
          choices.querySelectorAll('[data-about-answer]').forEach(function (b) {
            b.disabled = true;
            var val = String(b.getAttribute('data-about-answer') || '').toLowerCase() === 'true';
            b.classList.remove('is-picked', 'is-right', 'is-wrong');
            if (val === correct) b.classList.add('is-right');
            if (b === btn && !isCorrect) b.classList.add('is-wrong');
            if (b === btn) b.classList.add('is-picked');
          });

          if (result) result.hidden = false;
          if (verdict) {
            /* Always state the real answer so a FALSE statement can't be misread as fact */
            verdict.textContent = (isCorrect ? 'Correct' : 'Not quite') + ' — it’s ' + (correct ? 'TRUE' : 'FALSE') + '.';
            verdict.classList.toggle('is-correct', isCorrect);
            verdict.classList.toggle('is-incorrect', !isCorrect);
          }
        });
      });
    });
  });

    /* ── Home hero · stacked card slider (Swiper cards effect) ─
     Match https://card-slider-76f48e.webflow.io/ :
     effect: cards · perSlideOffset: 10 · perSlideRotate: 4 · speed: 600
     Swipe / drag / click / keyboard through the archive stack. */
  document.querySelectorAll('[data-hero-cards]').forEach(function (root) {
    var stack = root.querySelector('[data-hero-cards-stack]');
    var jsonEl = root.querySelector('[data-hero-cards-works]');
    var caption = root.querySelector('[data-hero-cards-caption]');
    var link = root.querySelector('[data-hero-cards-link]');
    var captionEl = root.querySelector('.home-calm__caption');
    if (!stack || !jsonEl) return;

    var works = [];
    try {
      works = JSON.parse(jsonEl.textContent || '[]') || [];
    } catch (eW) {
      works = [];
    }
    works = works.filter(function (w) {
      return w && w.src;
    });
    if (!works.length) return;
    /* Normalize video type when filename is an mp4 */
    works.forEach(function (w) {
      if (!w.type && /\.mp4($|\?)/i.test(w.src || '')) w.type = 'video';
    });

    var archiveBase = root.getAttribute('data-archive-base') || '/pages/archive';
    var index = 0;
    var busy = false;
    var slides = [];
    var OFFSET = 10; /* px — matches cardsEffect.perSlideOffset */
    var ROTATE = 4; /* deg — matches cardsEffect.perSlideRotate */
    var SPEED = reduce ? 280 : 600;
    var DRAG_COMMIT = 88;
    var VISIBLE = 5;
    var drag = null;

    function isVideo(work) {
      return !!(work && (work.type === 'video' || work.video || /\.mp4($|\?)/i.test(work.src || '')));
    }

    function workHref(work) {
      if (!work || !work.work) return archiveBase;
      return (
        archiveBase +
        (archiveBase.indexOf('?') >= 0 ? '&' : '?') +
        'work=' +
        encodeURIComponent(work.work)
      );
    }

    function setCaption(work, soft) {
      if (!work) return;
      var label = work.title || work.alt || (isVideo(work) ? 'Studio reel' : 'Original');
      if (caption) {
        if (soft && !reduce && captionEl && captionEl.animate) {
          captionEl.animate(
            [
              { opacity: 1, transform: 'translateY(0)' },
              { opacity: 0, transform: 'translateY(6px)' },
            ],
            { duration: Math.round(SPEED * 0.35), fill: 'forwards', easing: 'ease' }
          ).finished
            .then(function () {
              caption.textContent = label;
              return captionEl.animate(
                [
                  { opacity: 0, transform: 'translateY(-4px)' },
                  { opacity: 1, transform: 'translateY(0)' },
                ],
                {
                  duration: Math.round(SPEED * 0.45),
                  fill: 'forwards',
                  easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                }
              ).finished;
            })
            .then(function () {
              captionEl.style.opacity = '';
              captionEl.style.transform = '';
            })
            .catch(function () {
              caption.textContent = label;
            });
        } else {
          caption.textContent = label;
        }
      }
      if (link) {
        if (work.work) {
          link.href = workHref(work);
          link.hidden = false;
          link.textContent = 'Open in Walkthrough';
        } else {
          /* Reels stay in the stack — no archive deep-link */
          link.href = archiveBase;
          link.textContent = isVideo(work) ? 'Studio reel' : 'Open in Walkthrough';
        }
      }
      root.setAttribute(
        'aria-label',
        'Featured: ' + label + '. Swipe or click for next.'
      );
      syncMediaPlayback();
    }

    function workAt(i) {
      var n = works.length;
      return works[((i % n) + n) % n];
    }

    function syncMediaPlayback() {
      var n = slides.length;
      for (var i = 0; i < n; i++) {
        var slide = slides[i];
        if (!slide) continue;
        var depth = (i - index + n) % n;
        var vid = slide.querySelector('video');
        if (!vid) continue;
        vid.muted = true;
        vid.defaultMuted = true;
        vid.volume = 0;
        if (depth === 0 && !busy) {
          /* Active card — autoplay loop, no sound */
          try {
            vid.playsInline = true;
            var p = vid.play();
            if (p && p.catch) p.catch(function () {});
          } catch (ePlay) {}
        } else {
          try {
            vid.pause();
          } catch (ePause) {}
        }
      }
    }

    function buildStack() {
      stack.innerHTML = '';
      slides = [];
      for (var i = 0; i < works.length; i++) {
        var w = works[i];
        var slide = document.createElement('div');
        slide.className =
          'home-calm__cards-slide' + (isVideo(w) ? ' home-calm__cards-slide--video' : '');
        slide.setAttribute('data-hero-card-slide', '');
        slide.setAttribute('data-type', isVideo(w) ? 'video' : 'image');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute(
          'aria-label',
          i + 1 + ' of ' + works.length + ': ' + (w.title || w.alt || '')
        );
        slide.tabIndex = i === 0 ? 0 : -1;

        if (isVideo(w)) {
          var video = document.createElement('video');
          video.className = 'home-calm__cards-img home-calm__cards-video';
          video.src = w.src;
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.preload = i < 2 ? 'auto' : 'metadata';
          video.setAttribute('aria-label', w.alt || w.title || 'Studio reel');
          if (w.poster) video.poster = w.poster;
          /* No controls — pure ambient stack video */
          video.controls = false;
          video.disablePictureInPicture = true;
          video.draggable = false;
          slide.appendChild(video);
        } else {
          var img = document.createElement('img');
          img.className = 'home-calm__cards-img';
          img.src = w.src;
          img.alt = w.alt || w.title || '';
          img.decoding = 'async';
          img.loading = i < 3 ? 'eager' : 'lazy';
          if (i === 0) img.fetchPriority = 'high';
          img.draggable = false;
          img.onerror = (function (work) {
            return function () { dropBrokenWork(work); };
          })(w);
          slide.appendChild(img);
        }
        stack.appendChild(slide);
        slides.push(slide);
      }
      /* dots */
      var nav = root.querySelector('[data-hero-cards-nav]');
      if (!nav) {
        nav = document.createElement('div');
        nav.className = 'home-calm__cards-nav';
        nav.setAttribute('data-hero-cards-nav', '');
        nav.setAttribute('role', 'tablist');
        nav.setAttribute('aria-label', 'Featured works and reels');
        var cardsRoot = root.querySelector('[data-hero-cards-root]');
        if (cardsRoot && cardsRoot.parentNode) {
          cardsRoot.parentNode.insertBefore(nav, cardsRoot.nextSibling);
        } else {
          root.appendChild(nav);
        }
      }
      nav.innerHTML = '';
      works.forEach(function (w, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className =
          'home-calm__cards-dot' +
          (i === 0 ? ' is-active' : '') +
          (isVideo(w) ? ' home-calm__cards-dot--video' : '');
        dot.setAttribute('data-hero-cards-dot', String(i));
        dot.setAttribute(
          'aria-label',
          'Show ' + (w.title || w.alt || (isVideo(w) ? 'reel' : 'work') + ' ' + (i + 1))
        );
        on(dot, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          goTo(i);
        });
        nav.appendChild(dot);
      });
    }

    /* A photo that 404s (missing in Settings → Files) is dropped from the stack instead of showing an empty frame */
    var brokenQueue = [];
    var brokenTimer = null;
    function dropBrokenWork(work) {
      if (brokenQueue.indexOf(work) < 0) brokenQueue.push(work);
      clearTimeout(brokenTimer);
      brokenTimer = setTimeout(function flushBroken() {
        if (busy || drag) {
          brokenTimer = setTimeout(flushBroken, 400);
          return;
        }
        var cur = works[index];
        var next = works.filter(function (x) { return brokenQueue.indexOf(x) < 0; });
        brokenQueue = [];
        if (!next.length || next.length === works.length) return;
        works = next;
        index = works.indexOf(cur);
        if (index < 0) index = 0;
        buildStack();
        setCaption(workAt(index), false);
        paintDots();
        layout(0, 0, 0);
        syncMediaPlayback();
      }, 150);
    }

    function paintDots() {
      root.querySelectorAll('[data-hero-cards-dot]').forEach(function (dot) {
        var i = parseInt(dot.getAttribute('data-hero-cards-dot'), 10);
        dot.classList.toggle('is-active', i === index);
      });
    }

    function stackTransform(depth, dragX, dragY, dragRot) {
      dragX = dragX || 0;
      dragY = dragY || 0;
      dragRot = dragRot || 0;
      if (depth === 0) {
        return (
          'translate3d(' +
          dragX +
          'px,' +
          dragY +
          'px,0) rotateZ(' +
          dragRot +
          'deg)'
        );
      }
      var o = Math.min(depth, VISIBLE) * OFFSET;
      var r = Math.min(depth, VISIBLE) * ROTATE;
      var s = 1 - Math.min(depth, VISIBLE) * 0.025;
      return (
        'translate3d(0,' +
        o +
        'px,0) rotateZ(' +
        r +
        'deg) scale(' +
        s.toFixed(4) +
        ')'
      );
    }

    function layout(dragX, dragY, dragRot) {
      var n = slides.length;
      for (var i = 0; i < n; i++) {
        var slide = slides[i];
        var depth = (i - index + n) % n;
        /* Reorder so active is first in paint stack */
        if (depth > VISIBLE) {
          slide.classList.add('is-hidden-stack');
          slide.style.opacity = '0';
          slide.style.pointerEvents = 'none';
          slide.style.zIndex = '0';
          slide.tabIndex = -1;
          continue;
        }
        slide.classList.remove('is-hidden-stack');
        slide.style.opacity = depth === VISIBLE ? '0' : '1';
        slide.style.zIndex = String(n - depth);
        slide.style.pointerEvents = depth === 0 && !busy ? 'auto' : 'none';
        slide.tabIndex = depth === 0 ? 0 : -1;
        slide.classList.toggle('is-active', depth === 0);
        if (!(drag && depth === 0 && slide.classList.contains('is-dragging'))) {
          slide.style.transform = stackTransform(depth, depth === 0 ? dragX : 0, depth === 0 ? dragY : 0, depth === 0 ? dragRot : 0);
        }
      }
      syncMediaPlayback();
    }

    function topSlide() {
      return slides[index] || null;
    }

    /**
     * @param {number} dir index step (+1 next, -1 previous)
     * @param {object} [motion] optional { flyX, flyY, flyR } so exit continues the swipe
     */
    function advance(dir, motion) {
      if (busy || works.length < 2) return;
      dir = dir < 0 ? -1 : 1;
      var slide = topSlide();
      if (!slide) return;
      busy = true;
      motion = motion || {};
      /* Fly continues in the swipe direction (or default left for next / right for prev) */
      var flySign = motion.flySign != null ? motion.flySign : dir > 0 ? -1 : 1;
      var baseX = motion.fromX != null ? motion.fromX : 0;
      var baseY = motion.fromY != null ? motion.fromY : 0;
      var baseR = motion.fromR != null ? motion.fromR : 0;
      var dist = Math.max(window.innerWidth, 480) * 0.85 + 60;
      var flyX = flySign * dist;
      /* Keep momentum past the finger position so it never reverses */
      if (flySign > 0) flyX = Math.max(flyX, baseX + dist * 0.45);
      else flyX = Math.min(flyX, baseX - dist * 0.45);
      var flyY = baseY + (motion.flyY != null ? motion.flyY : 18 + Math.abs(baseY) * 0.15);
      var flyR = flySign * (22 + Math.min(18, Math.abs(baseR)));
      if (flySign > 0) flyR = Math.max(flyR, baseR + 8);
      else flyR = Math.min(flyR, baseR - 8);

      slide.classList.remove('is-dragging');
      /* Force current pose so CSS transition continues from the drag point */
      slide.style.transition = 'none';
      slide.style.transform =
        'translate3d(' + baseX + 'px,' + baseY + 'px,0) rotateZ(' + baseR + 'deg)';
      slide.style.opacity = '1';
      void slide.offsetWidth;
      slide.classList.add('is-flying');
      slide.style.transition = '';
      slide.style.transform =
        'translate3d(' + flyX + 'px,' + flyY + 'px,0) rotateZ(' + flyR + 'deg) scale(0.94)';
      slide.style.opacity = '0';

      window.setTimeout(function () {
        slide.classList.remove('is-flying', 'is-dragging');
        /*
          Instantly park the flown card (no transition). Right-swipe used to
          re-layout this same node into the deck while opacity/transform still
          transitioned — a brief transparent “snap back”. Left swipe hid it at
          the stack bottom so the glitch was invisible.
        */
        slide.style.transition = 'none';
        slide.style.opacity = '0';
        slide.style.transform = 'translate3d(0,0,0) scale(1)';
        void slide.offsetWidth;
        index = (index + dir + works.length) % works.length;
        setCaption(workAt(index), true);
        paintDots();
        layout(0, 0, 0);
        /* Re-enable transitions on the next frame so future swipes stay smooth */
        requestAnimationFrame(function () {
          if (slide && !slide.classList.contains('is-flying')) {
            slide.style.transition = '';
          }
          busy = false;
          syncMediaPlayback();
        });
      }, Math.max(SPEED, 520));
    }

    function goTo(i) {
      if (busy || i === index) return;
      var dir = i > index ? 1 : -1;
      /* For non-adjacent, jump with a single card throw in that direction */
      if (Math.abs(i - index) > 1) {
        busy = true;
        index = i;
        setCaption(workAt(index), true);
        paintDots();
        layout(0, 0, 0);
        busy = false;
        return;
      }
      advance(dir, { flySign: dir > 0 ? -1 : 1 });
    }

    function clearCardDrag(slide) {
      if (slide) {
        try {
          if (drag && drag.captured && drag.id != null) {
            slide.releasePointerCapture(drag.id);
          }
        } catch (errR) {}
        slide.classList.remove('is-dragging');
        slide.style.touchAction = '';
      }
      drag = null;
    }

    /** Calm spring back to top-of-stack rest pose */
    function springBackToStack(slide) {
      if (!slide) {
        layout(0, 0, 0);
        return;
      }
      slide.classList.remove('is-dragging');
      slide.style.transition =
        'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease';
      slide.style.transform = stackTransform(0, 0, 0, 0);
      slide.style.opacity = '1';
      window.setTimeout(function () {
        if (!slide) return;
        slide.style.transition = '';
        layout(0, 0, 0);
      }, 430);
    }

    function onPointerDown(e) {
      if (busy) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      var slide = topSlide();
      if (!slide || e.target.closest('a')) return;
      /* Never start a new drag while a previous one is stuck */
      if (drag) clearCardDrag(drag.slide);
      drag = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        x: 0,
        y: 0,
        moved: false,
        axis: null, /* 'h' = card swipe · 'v' = page scroll */
        slide: slide,
        captured: false,
        lastT: performance.now(),
        lastX: e.clientX,
        vx: 0,
      };
      /* Don’t capture yet — let vertical gestures become native page scroll */
    }

    function onPointerMove(e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.startX;
      var dy = e.clientY - drag.startY;
      var dist = Math.hypot(dx, dy);
      var now = performance.now();
      var dt = Math.max(8, now - (drag.lastT || now));
      drag.vx = (e.clientX - (drag.lastX || e.clientX)) / dt;
      drag.lastX = e.clientX;
      drag.lastT = now;

      /* Axis lock: decide horizontal card swipe vs vertical page scroll */
      if (!drag.axis) {
        if (dist < 12) return;
        if (Math.abs(dx) > Math.abs(dy) * 1.2) {
          drag.axis = 'h';
          drag.moved = true;
          try {
            drag.slide.setPointerCapture(e.pointerId);
            drag.captured = true;
          } catch (errC) {}
        } else {
          /* Vertical intent — abandon card drag so the page can scroll */
          var s = drag.slide;
          clearCardDrag(s);
          layout(0, 0, 0);
          return;
        }
      }

      if (drag.axis !== 'h') return;

      drag.moved = true;
      drag.x = dx;
      drag.y = dy * 0.28;
      var rot = dx * 0.04;
      drag.slide.classList.add('is-dragging');
      drag.slide.style.transition = 'none';
      drag.slide.style.transform = stackTransform(0, drag.x, drag.y, rot);
      /* subtle peek of next cards while dragging */
      var n = slides.length;
      for (var i = 0; i < n; i++) {
        var depth = (i - index + n) % n;
        if (depth === 0 || depth > VISIBLE) continue;
        var pull = Math.min(1, Math.abs(dx) / 160);
        var o = depth * OFFSET * (1 - pull * 0.35);
        var r = depth * ROTATE * (1 - pull * 0.25);
        var s2 = 1 - depth * 0.025 + pull * 0.01;
        slides[i].style.transition = 'none';
        slides[i].style.transform =
          'translate3d(0,' + o + 'px,0) rotateZ(' + r + 'deg) scale(' + s2.toFixed(4) + ')';
      }
      /* Only block scroll once we’ve locked to a horizontal card swipe */
      if (e.cancelable) e.preventDefault();
    }

    function onPointerUp(e) {
      if (!drag) return;
      if (e && e.pointerId != null && drag.id != null && e.pointerId !== drag.id) return;
      var slide = drag.slide;
      var dx = drag.x;
      var dy = drag.y;
      var moved = drag.moved;
      var axis = drag.axis;
      var vx = drag.vx || 0;
      var fromR = dx * 0.04;
      clearCardDrag(slide);

      /* Vertical scroll gesture — leave the stack alone */
      if (axis === 'v') {
        layout(0, 0, 0);
        return;
      }

      if (!moved || axis == null) {
        /* Clean tap → next card */
        advance(1, { flySign: -1 });
        return;
      }

      /*
        Commit only on a clear intentional throw past threshold.
        Flick / partial drag / “stuck” releases spring calmly home.
      */
      var farEnough = Math.abs(dx) >= DRAG_COMMIT;
      var flungHard = Math.abs(vx) > 0.85 && Math.abs(dx) >= DRAG_COMMIT * 0.72;
      if (axis === 'h' && (farEnough || flungHard)) {
        var flySign = dx > 0 ? 1 : -1;
        advance(1, {
          flySign: flySign,
          fromX: dx,
          fromY: dy,
          fromR: fromR,
        });
        return;
      }

      /* Calm return to top of stack */
      springBackToStack(slide);
    }

    function onPointerCancel(e) {
      if (!drag) return;
      if (e && e.pointerId != null && drag.id != null && e.pointerId !== drag.id) return;
      var slide = drag.slide;
      clearCardDrag(slide);
      springBackToStack(slide);
    }

    buildStack();
    setCaption(workAt(0), false);
    paintDots();
    layout(0, 0, 0);
    syncMediaPlayback();

    /* Bind once on stack for the active top card via event delegation */
    on(stack, 'pointerdown', onPointerDown);
    on(stack, 'pointermove', onPointerMove);
    on(stack, 'pointerup', onPointerUp);
    on(stack, 'pointercancel', onPointerCancel);
    on(stack, 'lostpointercapture', onPointerCancel);
    /* Safety: if pointer ends outside the stack, never stay stuck */
    on(window, 'pointerup', onPointerUp);
    on(window, 'pointercancel', onPointerCancel);
    on(window, 'blur', function () {
      if (!drag) return;
      var s = drag.slide;
      clearCardDrag(s);
      springBackToStack(s);
    });

    on(root, 'keydown', function (e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        advance(1, { flySign: -1 }); /* next — exits left */
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        advance(1, { flySign: 1 }); /* also next — exits right */
      }
    });

    /* Preload a few images ahead; warm nearby videos */
    for (var p = 1; p < Math.min(5, works.length); p++) {
      var wPre = works[p];
      if (!wPre) continue;
      if (isVideo(wPre)) {
        var vPre = document.createElement('video');
        vPre.preload = 'metadata';
        vPre.muted = true;
        vPre.src = wPre.src;
      } else {
        var im = new Image();
        im.src = wPre.src;
      }
    }

    /* Pause reels when the hero leaves the viewport */
    if (typeof IntersectionObserver === 'function') {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            if (!en.isIntersecting) {
              slides.forEach(function (sl) {
                var v = sl.querySelector('video');
                if (v) {
                  try {
                    v.pause();
                  } catch (eIo) {}
                }
              });
            } else {
              syncMediaPlayback();
            }
          });
        },
        { threshold: 0.25 }
      );
      io.observe(root);
    }

    /* Resume after tab focus (mobile browsers often pause media) */
    on(document, 'visibilitychange', function () {
      if (!document.hidden) syncMediaPlayback();
    });
  });

/* ── Auth recover toggle ───────────────────────────────── */
  document.querySelectorAll('[data-auth]').forEach(function (root) {
    var rec = root.querySelector('[data-auth-recover]');
    var tog = root.querySelector('[data-auth-recover-toggle]');
    if (tog && rec) {
      on(tog, 'click', function (e) {
        e.preventDefault();
        rec.hidden = !rec.hidden;
      });
    }
  });

  /* ── Membership hub ────────────────────────────────────── */
  document.querySelectorAll('[data-member-hub]').forEach(function (root) {
    var ordersEl = root.querySelector('[data-member-orders]');
    var orders = [];
    try {
      orders = ordersEl ? JSON.parse(ordersEl.textContent || '[]') : [];
    } catch (e) {
      orders = [];
    }

    // Award purchase heat once per order id
    var claimed = readJSON('sg_order_xp_claimed', {});
    var orderBonus = 0;
    var newClaim = false;
    orders.forEach(function (o) {
      var id = String(o.id);
      if (!claimed[id]) {
        claimed[id] = true;
        newClaim = true;
      }
      orderBonus += 120;
    });
    if (newClaim) writeJSON('sg_order_xp_claimed', claimed);

    var archive = readJSON('sg_archive_v3', null) || readJSON('sg_archive_v2', { found: {}, xp: 0 });
    var story = sumStory();
    var contact = sumContact();
    var foundN = archive.found ? Object.keys(archive.found).length : 0;
    var foundTotal = Math.max(foundN, 84);
    var storyN = story.unlocked || Object.keys(story.answered || {}).length || 0;
    var followed = contact.followed || {};
    var connected = contact.connected || contact.unlocked || {};
    var followN = Object.keys(followed).length;
    var connectN = Object.keys(connected).length;
    var channelN = Math.max(followN, connectN);
    var xp = totalMemberXP(orderBonus);
    var rank = memberRank(xp);

    var el;
    if ((el = root.querySelector('[data-member-rank]'))) el.textContent = rank;
    if ((el = root.querySelector('[data-member-xp]'))) el.textContent = xp + ' XP';
    if ((el = root.querySelector('[data-member-channels]'))) el.textContent = String(channelN);
    if ((el = root.querySelector('[data-member-works]'))) el.textContent = String(foundN);
    if ((el = root.querySelector('[data-bar-archive]')))
      el.style.width = Math.min(100, (foundN / foundTotal) * 100).toFixed(1) + '%';
    if ((el = root.querySelector('[data-bar-archive-label]'))) el.textContent = foundN + ' works';
    if ((el = root.querySelector('[data-bar-story]')))
      el.style.width = Math.min(100, (storyN / 6) * 100).toFixed(1) + '%';
    if ((el = root.querySelector('[data-bar-story-label]'))) el.textContent = storyN + '/6';
    if ((el = root.querySelector('[data-bar-social]')))
      el.style.width = Math.min(100, (channelN / 10) * 100).toFixed(1) + '%';
    if ((el = root.querySelector('[data-bar-social-label]')))
      el.textContent = followN + ' followed · ' + connectN + ' opened';
    if ((el = root.querySelector('[data-bar-orders]')))
      el.style.width = Math.min(100, orders.length * 25).toFixed(1) + '%';

    var chips = root.querySelector('[data-member-follows]');
    if (chips) {
      var keys = Object.keys(followed);
      if (!keys.length) keys = Object.keys(connected);
      if (keys.length) {
        chips.innerHTML = keys
          .map(function (k) {
            var followedYes = !!followed[k];
            return (
              '<span class="member__chip' +
              (followedYes ? ' is-followed' : '') +
              '">' +
              k +
              (followedYes ? ' · followed' : ' · connected') +
              '</span>'
            );
          })
          .join('');
      }
    }

    var missions = root.querySelector('[data-member-missions]');
    if (missions) {
      var list = [
        {
          done: foundN >= 5,
          html: foundN >= 5 ? '✓ Found 5+ works in the Walkthrough' : 'Find 5 works in the <a href="/pages/gallery">Walkthrough</a>',
        },
        {
          done: storyN >= 3,
          html: storyN >= 3 ? '✓ Opened 3 story chapters' : 'Walk 3 chapters of the <a href="/pages/about">story journey</a>',
        },
        {
          done: followN >= 1 || connectN >= 2,
          html:
            followN >= 1 || connectN >= 2
              ? '✓ Connected across the mesh'
              : 'Open channels on the <a href="/pages/contact-me-directly">contact mesh</a>',
        },
        {
          done: orders.length >= 1,
          html: orders.length >= 1 ? '✓ First work claimed' : 'Claim a print in the <a href="/collections/print-studio">Print Studio</a>',
        },
      ];
      missions.innerHTML = list
        .map(function (m) {
          return '<li class="' + (m.done ? 'is-done' : '') + '">' + m.html + '</li>';
        })
        .join('');
    }
  });

  /* ── Keyboard focus ring polish ────────────────────────── */
  on(document, 'keydown', function (e) {
    if (e.key === 'Tab') document.body.classList.add('kb');
  });
  on(document, 'mousedown', function () {
    document.body.classList.remove('kb');
  });

  /* ── Bulletproof gallery actions (capture) ───────────────
     Survives if nested archive setup throws; always wires CTA. */
  function sgWalkReady(root) {
    if (!root || !root._sgWalk || typeof root._sgWalk.open !== 'function') return false;
    if (root._sgWalk.ready === true) return true;
    try {
      if (root._sgWalkOpenStub && root._sgWalk.open === root._sgWalkOpenStub) return false;
    } catch (e) {}
    return true;
  }

  function sgForceOpenWalk(root, start, detail) {
    if (!root) root = document.querySelector('[data-archive]');
    if (!root) return false;
    detail = detail || {};
    detail.skipIntro = detail.skipIntro !== false;
    if (typeof root._sgOpenWalkForItem === 'function' && start && start.nodeType === 1) {
      try {
        root._sgOpenWalkForItem(start, detail);
        return true;
      } catch (e0) {}
    }
    if (typeof root._sgOpenRandom === 'function' && detail && detail.random) {
      try {
        root._sgOpenRandom();
        return true;
      } catch (eR) {}
    }
    if (
      typeof root._sgOpenWalkthrough === 'function' &&
      (start === 0 || start == null) &&
      !detail.item
    ) {
      try {
        root._sgOpenWalkthrough();
        return true;
      } catch (eW) {}
    }
    /* Live walk API — ignore dead stub */
    if (sgWalkReady(root)) {
      try {
        if (detail.random && typeof root._sgWalk.openRandom === 'function') {
          root._sgWalk.openRandom();
          return true;
        }
        root._sgWalk.open(start != null ? start : 0, detail);
        return true;
      } catch (e1) {}
    }
    try {
      if (typeof window.sgOpenWalk === 'function') {
        window.sgOpenWalk(start != null ? start : 0, detail);
        return true;
      }
    } catch (eWin) {}
    try {
      root.dispatchEvent(
        new CustomEvent('sg:open-walkthrough', {
          bubbles: true,
          detail: detail.item
            ? detail
            : {
                index: typeof start === 'number' ? start : 0,
                item: start && start.nodeType === 1 ? start : undefined,
                skipIntro: true,
                pinItem: !!(start && start.nodeType === 1),
              },
        })
      );
      return true;
    } catch (e2) {}
    return false;
  }

  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      /* Painting / wall card → walkthrough on THAT work */
      var card = t.closest('[data-arc-item], [data-gx-card]');
      if (card && card.closest('[data-archive]')) {
        /* Don’t steal clicks from real links inside a card chrome */
        if (t.closest('a[href]') && !t.closest('a[href]').hasAttribute('data-arc-item')) {
          var linkEl = t.closest('a[href]');
          if (linkEl && !linkEl.hasAttribute('data-gx-card') && linkEl !== card) return;
        }
        var rootC = card.closest('[data-archive]') || document.querySelector('[data-archive]');
        if (!rootC) return;
        var vp = card.closest('[data-gx-wall-viewport]');
        if (vp) {
          /*
            Page-flow archive does not pan-drag. Any leftover is-dragging /
            is-panning from older builds would block opens forever — always
            clear, then open the painting.
          */
          var wasDragging =
            vp.classList.contains('is-dragging') || vp.classList.contains('is-panning');
          vp.classList.remove('is-dragging', 'is-panning', 'is-pan-lock', 'is-coasting');
          /* Only suppress when a real active drag just finished (not page-flow) */
          if (wasDragging && !vp.classList.contains('is-page-flow')) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
        e.preventDefault();
        e.stopPropagation();
        var detailC = {
          skipIntro: true,
          pinItem: true,
          item: card,
          id: card.getAttribute('data-id') || '',
          slug: card.getAttribute('data-slug') || '',
          src: (card.getAttribute('data-src') || '').trim(),
        };
        if (typeof rootC._sgOpenWalkForItem === 'function') {
          rootC._sgOpenWalkForItem(card, detailC);
        } else {
          sgForceOpenWalk(rootC, card, detailC);
        }
        return;
      }

      var walkBtn = t.closest('[data-gx-walkthrough], #gx-walkthrough-btn');
      if (walkBtn) {
        var rootW = walkBtn.closest('[data-archive]') || document.querySelector('[data-archive]');
        if (!rootW) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        if (typeof rootW._sgOpenWalkthrough === 'function') {
          rootW._sgOpenWalkthrough();
        } else if (sgWalkReady(rootW)) {
          try {
            rootW._sgWalk.open(0, { skipIntro: true });
          } catch (eW) {
            sgForceOpenWalk(rootW, 0, { skipIntro: true });
          }
        } else {
          sgForceOpenWalk(rootW, 0, { skipIntro: true });
          /* Retry briefly if walk module is still booting */
          var wTries = 0;
          var wTimer = setInterval(function () {
            wTries += 1;
            if (sgWalkReady(rootW)) {
              try {
                rootW._sgWalk.open(0, { skipIntro: true });
              } catch (eWr) {}
              clearInterval(wTimer);
            } else if (wTries > 40) clearInterval(wTimer);
          }, 40);
        }
        return;
      }

      var randomBtn = t.closest('[data-gx-random], #gx-random-btn');
      if (randomBtn) {
        var rootR =
          randomBtn.closest('[data-archive]') ||
          document.querySelector('[data-archive]') ||
          document.querySelector('[data-gx]');
        if (!rootR) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        function pickRandomCard() {
          var pool = Array.prototype.slice
            .call(rootR.querySelectorAll('[data-arc-item], [data-gx-card]'))
            .filter(function (it) {
              if (!it || it.classList.contains('is-hidden')) return false;
              var src = (it.getAttribute('data-src') || '').trim();
              if (src) return true;
              var img = it.querySelector('img');
              return !!(img && (img.getAttribute('src') || img.src));
            });
          if (!pool.length) return null;
          return pool[Math.floor(Math.random() * pool.length)];
        }

        function openRandomNow() {
          /* 1) Dedicated walk API */
          if (sgWalkReady(rootR) && typeof rootR._sgWalk.openRandom === 'function') {
            try {
              return rootR._sgWalk.openRandom() !== false;
            } catch (e1) {}
          }
          /* 2) Archive helper */
          if (typeof rootR._sgOpenRandom === 'function') {
            try {
              return rootR._sgOpenRandom() !== false;
            } catch (e2) {}
          }
          /* 3) Pick a card and open walkthrough on it */
          var pick = pickRandomCard();
          if (!pick) return false;
          if (sgWalkReady(rootR)) {
            try {
              var opened = rootR._sgWalk.open(pick, {
                skipIntro: true,
                pinItem: true,
                item: pick,
                id: pick.getAttribute('data-id') || '',
                slug: pick.getAttribute('data-slug') || '',
                src: (pick.getAttribute('data-src') || '').trim(),
              });
              if (opened !== false) return true;
            } catch (e3) {}
          }
          if (typeof rootR._sgOpenWalkForItem === 'function') {
            try {
              return !!rootR._sgOpenWalkForItem(pick, {
                skipIntro: true,
                pinItem: true,
              });
            } catch (e4) {}
          }
          return sgForceOpenWalk(rootR, pick, {
            skipIntro: true,
            pinItem: true,
            item: pick,
            id: pick.getAttribute('data-id') || '',
            slug: pick.getAttribute('data-slug') || '',
            src: (pick.getAttribute('data-src') || '').trim(),
            random: true,
          });
        }

        rootR.classList.add('is-live');
        if (!openRandomNow()) {
          var tries = 0;
          var timer = setInterval(function () {
            tries += 1;
            if (openRandomNow() || tries > 50) clearInterval(timer);
          }, 40);
        }
        return;
      }

      var browseBtn = t.closest('[data-gx-browse], [data-gx-wall-fs]');
      if (browseBtn) {
        var rootB = browseBtn.closest('[data-archive]');
        if (!rootB) return;
        /* Handled by the archive wall-fs toggle — do not double-scroll */
        if (typeof rootB._sgToggleWallFullscreen === 'function') return;
        e.preventDefault();
        rootB.classList.add('is-live');
        rootB.classList.remove('is-ui-hidden');
        rootB.querySelectorAll('[data-arc-item]').forEach(function (it) {
          it.classList.remove('is-fogged');
        });
      }
    },
    true
  );

  /* ── Walkthrough page · auto-open on a random piece ──────
     Once per page load. Reuses root._sgOpenRandom (retries until the walk API
     is ready) and skips the intro. Closing uses the normal walk close and
     never re-opens. Skipped when the URL targets a specific work (?work=,
     #work-…, #work=…, #walkthrough — handled by the deep-link code above),
     when "wall" is a query param or hash, in the theme editor, or when the
     section setting "Open walkthrough on page load" is off. */
  (function initWalkAutoOpen() {
    var html = document.documentElement;
    function reveal() {
      html.classList.remove('sg-walk-autoopen');
    }
    var root = document.querySelector('[data-archive][data-walk-autoopen]');
    if (!root || root.getAttribute('data-walk-autoopen') !== 'true') {
      reveal();
      return;
    }
    function urlSkips() {
      try {
        var q = new URLSearchParams(location.search || '');
        var h = (location.hash || '').replace(/^#/, '').toLowerCase();
        if ((q.get('work') || '').trim()) return true;
        if (q.has('wall')) return true;
        if (h.indexOf('work-') === 0 || h.indexOf('work=') === 0) return true;
        if (h === 'walkthrough' || h === 'gallery-wall') return true;
        if (h && new URLSearchParams(h).has('wall')) return true;
      } catch (eU) {}
      return false;
    }
    var inEditor = false;
    try {
      inEditor = !!(window.Shopify && window.Shopify.designMode);
    } catch (eD) {}
    if (inEditor || urlSkips() || window.__sgWalkAutoOpened) {
      reveal();
      return;
    }
    window.__sgWalkAutoOpened = true;

    /* Show the wall again as soon as the walk is up (or after 2.5s regardless) */
    var watch = null;
    function onWalkOpen() {
      if (!document.body.classList.contains('walk-open')) return false;
      reveal();
      if (watch) watch.disconnect();
      return true;
    }
    try {
      watch = new MutationObserver(onWalkOpen);
      watch.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    } catch (eM) {}
    setTimeout(function () {
      reveal();
      if (watch) watch.disconnect();
    }, 2500);

    var fired = false;
    function tryOpen(attempt) {
      if (fired) return;
      /* Something else (deep link, visitor click) already opened it */
      if (document.body.classList.contains('walk-open')) {
        fired = true;
        onWalkOpen();
        return;
      }
      var fn =
        typeof root._sgOpenRandom === 'function'
          ? root._sgOpenRandom
          : typeof window.sgOpenRandomWalk === 'function'
          ? window.sgOpenRandomWalk
          : null;
      if (fn) {
        fired = true;
        try {
          fn();
        } catch (eO) {
          console.warn('[gallery] Walkthrough auto-open failed', eO);
          reveal();
        }
        return;
      }
      if (attempt < 40) {
        setTimeout(function () {
          tryOpen(attempt + 1);
        }, 50);
      } else {
        reveal();
      }
    }
    tryOpen(0);
  })();

  /* ── Doubtful Sound polaroids · click to expand ───────── */
  (function initAdventurePolaroids() {
    var roots = document.querySelectorAll('[data-sg-adventure-polaroids], [data-sg-adventure]');
    if (!roots.length) return;

    roots.forEach(function (root) {
      var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-sg-adv-open]'));
      var lb = root.querySelector('[data-sg-adv-lb]');
      if (!buttons.length || !lb) return;

      var img = lb.querySelector('[data-sg-adv-lb-img]');
      var countEl = lb.querySelector('[data-sg-adv-lb-count]');
      var closeBtn = lb.querySelector('[data-sg-adv-close]');
      var prevBtn = lb.querySelector('[data-sg-adv-prev]');
      var nextBtn = lb.querySelector('[data-sg-adv-next]');
      var idx = 0;
      var lastFocus = null;

      function openAt(n) {
        if (!buttons.length) return;
        idx = ((n % buttons.length) + buttons.length) % buttons.length;
        var btn = buttons[idx];
        var src = btn.getAttribute('data-sg-adv-src') || '';
        var alt = btn.getAttribute('data-sg-adv-alt') || 'Doubtful Sound';
        if (!src || !img) return;
        lastFocus = document.activeElement;
        img.src = src;
        img.alt = alt;
        if (countEl) countEl.textContent = idx + 1 + ' / ' + buttons.length;
        lb.hidden = false;
        document.body.classList.add('sg-adv-lb-open');
        if (closeBtn) closeBtn.focus();
      }

      function close() {
        lb.hidden = true;
        document.body.classList.remove('sg-adv-lb-open');
        if (img) {
          img.removeAttribute('src');
          img.alt = '';
        }
        if (lastFocus && lastFocus.focus) {
          try {
            lastFocus.focus();
          } catch (err) {}
        }
      }

      function step(d) {
        openAt(idx + d);
      }

      buttons.forEach(function (btn, n) {
        on(btn, 'click', function (e) {
          e.preventDefault();
          openAt(n);
        });
      });
      if (closeBtn) {
        on(closeBtn, 'click', function (e) {
          e.preventDefault();
          close();
        });
      }
      if (prevBtn) {
        on(prevBtn, 'click', function (e) {
          e.preventDefault();
          step(-1);
        });
      }
      if (nextBtn) {
        on(nextBtn, 'click', function (e) {
          e.preventDefault();
          step(1);
        });
      }
      on(lb, 'click', function (e) {
        /* Backdrop click closes */
        if (e.target === lb) close();
      });
      /* Second click on the enlarged image (or its frame) exits enlarge mode */
      var figure = lb.querySelector('.sg-adventure__lb-figure');
      if (img) {
        img.style.cursor = 'zoom-out';
        img.setAttribute('title', 'Click to close');
        on(img, 'click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          close();
        });
      }
      if (figure) {
        figure.style.cursor = 'zoom-out';
        on(figure, 'click', function (e) {
          /* Ignore if a control was the real target */
          if (e.target && e.target.closest && e.target.closest('[data-sg-adv-close], [data-sg-adv-prev], [data-sg-adv-next]')) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          close();
        });
      }
      on(document, 'keydown', function (e) {
        if (lb.hidden) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          step(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          step(1);
        }
      });
    });
  })();


  /* ── Creatives letter · collapsible bio ───────────────── */
  (function initCreativesLetter() {
    var roots = document.querySelectorAll('[data-sg-creatives]');
    if (!roots.length) return;

    roots.forEach(function (root) {
      var toggle = root.querySelector('[data-sg-creatives-toggle]');
      var panel = root.querySelector('[data-sg-creatives-panel]');
      var label = root.querySelector('[data-sg-creatives-label]');
      var closeBtn = root.querySelector('[data-sg-creatives-close]');
      if (!toggle || !panel) return;

      var open = false;

      function setOpen(next) {
        open = !!next;
        root.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) label.textContent = open ? 'Close' : label.getAttribute('data-label-closed') || 'Read';
        if (open) {
          panel.removeAttribute('hidden');
          /* next frame so grid-rows animate from 0fr */
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              if (open) panel.classList.add('is-open');
            });
          });
        } else {
          panel.classList.remove('is-open');
          window.setTimeout(function () {
            if (!open) panel.setAttribute('hidden', '');
          }, 520);
        }
      }

      on(toggle, 'click', function (e) {
        e.preventDefault();
        setOpen(!open);
      });
      if (closeBtn) {
        on(closeBtn, 'click', function (e) {
          e.preventDefault();
          setOpen(false);
          try {
            toggle.focus();
          } catch (err) {}
        });
      }
      on(toggle, 'keydown', function (e) {
        if (e.key === 'Escape' && open) {
          e.preventDefault();
          setOpen(false);
        }
      });
    });
  })();

  /* ── Artist voice quote carousel (one line at a time) ─── */
  (function initArtistVoiceCarousel() {
    var roots = document.querySelectorAll('[data-sg-artist-voice]');
    if (!roots.length) return;
    var reduce = false;
    try {
      reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (err) {}

    roots.forEach(function (root) {
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-sg-voice-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-sg-voice-dot]'));
      var counter = root.querySelector('[data-sg-voice-counter]');
      var prev = root.querySelector('[data-sg-voice-prev]');
      var next = root.querySelector('[data-sg-voice-next]');
      var stage = root.querySelector('[data-sg-voice-stage]') || root;
      if (slides.length < 2) return;

      var i = 0;
      var timer = null;
      var AUTO_MS = 6500;
      var paused = false;

      function paint(n) {
        i = ((n % slides.length) + slides.length) % slides.length;
        slides.forEach(function (slide, idx) {
          var on = idx === i;
          slide.classList.toggle('is-active', on);
          if (on) slide.removeAttribute('hidden');
          else slide.setAttribute('hidden', '');
        });
        dots.forEach(function (dot, idx) {
          var on = idx === i;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        if (counter) counter.textContent = i + 1 + ' / ' + slides.length;
      }

      function go(n) {
        paint(n);
        arm();
      }

      function arm() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
        if (reduce || paused) return;
        timer = setInterval(function () {
          paint(i + 1);
        }, AUTO_MS);
      }

      if (prev) {
        on(prev, 'click', function (e) {
          e.preventDefault();
          go(i - 1);
        });
      }
      if (next) {
        on(next, 'click', function (e) {
          e.preventDefault();
          go(i + 1);
        });
      }
      dots.forEach(function (dot) {
        on(dot, 'click', function (e) {
          e.preventDefault();
          var idx = parseInt(dot.getAttribute('data-index'), 10);
          if (isNaN(idx)) return;
          go(idx);
        });
      });

      on(stage, 'pointerenter', function () {
        paused = true;
        arm();
      });
      on(stage, 'pointerleave', function () {
        paused = false;
        arm();
      });
      on(stage, 'focusin', function () {
        paused = true;
        arm();
      });
      on(stage, 'focusout', function () {
        paused = false;
        arm();
      });

      var sx = 0;
      var dx = 0;
      var dragging = false;
      on(stage, 'pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('button, a')) return;
        dragging = true;
        sx = e.clientX;
        dx = 0;
        try {
          stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
        } catch (err2) {}
      });
      on(stage, 'pointermove', function (e) {
        if (!dragging) return;
        dx = e.clientX - sx;
      });
      function endSwipe() {
        if (!dragging) return;
        dragging = false;
        if (Math.abs(dx) > 48) go(dx < 0 ? i + 1 : i - 1);
      }
      on(stage, 'pointerup', endSwipe);
      on(stage, 'pointercancel', endSwipe);

      paint(0);
      arm();
    });
  })();

  /* ── About polaroid lightbox ───────────────────────────── */
  (function initPolaroidLightbox() {
    var wall = document.querySelector('[data-about-polaroids]');
    var lb = document.querySelector('[data-polaroid-lb]');
    if (!wall || !lb) return;

    var buttons = Array.prototype.slice.call(wall.querySelectorAll('[data-polaroid-open]'));
    var img = lb.querySelector('[data-polaroid-lb-img]');
    var countEl = lb.querySelector('[data-polaroid-lb-count]');
    var closeBtn = lb.querySelector('[data-polaroid-close]');
    var prevBtn = lb.querySelector('[data-polaroid-prev]');
    var nextBtn = lb.querySelector('[data-polaroid-next]');
    var idx = 0;
    var lastFocus = null;

    function srcAt(n) {
      var b = buttons[n];
      return b ? b.getAttribute('data-polaroid-src') : '';
    }

    function openAt(n) {
      if (!buttons.length) return;
      idx = ((n % buttons.length) + buttons.length) % buttons.length;
      var src = srcAt(idx);
      if (!src || !img) return;
      lastFocus = document.activeElement;
      img.src = src;
      if (countEl) countEl.textContent = idx + 1 + ' / ' + buttons.length;
      lb.hidden = false;
      document.body.classList.add('polaroid-lb-open');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      lb.hidden = true;
      document.body.classList.remove('polaroid-lb-open');
      if (img) img.removeAttribute('src');
      if (lastFocus && lastFocus.focus) {
        try {
          lastFocus.focus();
        } catch (err) {}
      }
    }

    function step(d) {
      openAt(idx + d);
    }

    buttons.forEach(function (btn, n) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        openAt(n);
      });
    });
    if (closeBtn) on(closeBtn, 'click', function (e) {
      e.preventDefault();
      close();
    });
    if (prevBtn) on(prevBtn, 'click', function (e) {
      e.preventDefault();
      step(-1);
    });
    if (nextBtn) on(nextBtn, 'click', function (e) {
      e.preventDefault();
      step(1);
    });
    on(lb, 'click', function (e) {
      if (e.target === lb) close();
    });
    /* Second click on enlarged polaroid exits */
    var figure = lb.querySelector('.about-polaroids__lb-figure');
    if (img) {
      img.style.cursor = 'zoom-out';
      img.setAttribute('title', 'Click to close');
      on(img, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        close();
      });
    }
    if (figure) {
      figure.style.cursor = 'zoom-out';
      on(figure, 'click', function (e) {
        if (e.target && e.target.closest && e.target.closest('[data-polaroid-close], [data-polaroid-prev], [data-polaroid-next]')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        close();
      });
    }
    on(document, 'keydown', function (e) {
      if (lb.hidden) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        step(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        step(1);
      }
    });
  })();
})();

